import {
  assessBlockStyleSeparation,
  STYLE_SEPARATION_GENERATOR_CONTRACT,
  type StyleSeparationGeneratorContract,
  type StyleSeparationReport,
} from '@cogo/headless-core/lint-block-style-separation'
import {
  assessReportApplicationShell,
  REPORT_APPLICATION_SHELL_CONTRACT,
  type ReportApplicationShellContract,
  type ReportApplicationShellReport,
} from '@cogo/headless-core/lint-report-application-shell'
import {
  CANONICAL_GENERATED_UI_PROVENANCE_TIERS,
  IMAGE_EVIDENCE_CANDIDATE_ADMISSION_SCHEMA_VERSION,
  GENERATED_UI_VERIFICATION_SUMMARY_SCHEMA_VERSION,
  buildGeneratedUiVerificationSummary,
  validateGeneratedUiVerificationSurfaceContract,
  validateImageSourceEvidenceContract,
  type ImageEvidenceCandidateAdmissionResult,
  type GeneratedUiVerificationSurface,
  type GeneratedUiVerificationSummary,
  type ImageSourceEvidence,
} from '@cogo/headless-core/image-evidence-verification'
import {
  ACTION_CAPABILITY_LIST,
  ACTION_CAPABILITY_REGISTRY_SCHEMA_VERSION,
} from '@cogo/action-registry'

import type {
  UiConformancePanelItem,
  UiConformancePanelModel,
  UiConformancePanelSection,
} from './ui-ontology-panel-model'
import {
  readDesignQualityEvidenceModel,
  type DesignQualityEvidenceSourceKind,
} from './design-quality-panel-model'
import {
  VISIBLE_FROM_JSON_ORACLE_SCHEMA_VERSION,
} from './visible-from-json-oracle'
import {
  createMeasuredUiBlockConsumerReceiptV1,
  isPassingUiBlockActionFlowTraceV1,
} from '@cogo/headless-core/ui-block-consumer-receipts'
import { hashCanonicalContent } from '@cogo/schema/canonical-content-hash'
import {
  deriveSingleBlockUiBlockClosureIdentityV1,
  type UiBlockClosureIdentityV1,
  type UiBlockConsumerReceiptV1,
} from '@cogo/schema/ui-block-e2e-closure'

export type StudioGenerationTargetPlatform = 'react' | 'flutter'
export type StudioRuntimeTargetEvidence = 'web' | 'rn' | 'flutter' | 'mobile' | 'desktop'
export type StudioGeneratedAppReadinessStatus = 'pending' | 'pass' | 'partial' | 'not-required'

export interface StudioGenerationEvidenceSource {
  appId?: string
  name?: string
  targetPlatform?: string
  generationTargetPlatform?: StudioGenerationTargetPlatform
  runtimeTargets?: readonly string[]
  nativeTargetProof?: unknown
  appClass?: string
  blocks?: unknown
  datasources?: unknown
  globalState?: unknown
  interactionContract?: unknown
  projectPlan?: StudioProjectPlanEvidenceSource | null
  adaptiveRuntime?: unknown
  goldenRecipePack?: unknown
  fullCoverageAudit?: unknown
  exhaustiveProofAudit?: unknown
  applicationConformanceOracle?: unknown
  visibleFromJsonOracle?: unknown
  imageEvidenceVerificationEnabled?: boolean
  imageSourceEvidence?: unknown
  generatedUiVerificationSurface?: unknown
  generatedUiVerificationSurfaces?: unknown
  imageEvidenceVerification?: unknown
  imageEvidenceCandidateAdmission?: unknown
}

export interface StudioUiOntologyGenerationConstraintsPayload {
  mode: StudioUiOntologyPreflightSummary['mode']
  canGenerate: boolean
  ragCandidates: StudioUiOntologyRagCandidate[]
  bindPlan: StudioUiOntologyBindPlanItem[]
  negativeConstraints: string[]
  generationRetrievalRouting: StudioGenerationRetrievalRoutingSummary
  fallbackCandidateFacts: StudioUiOntologyFallbackCandidateFact[]
  safeDesignFacts: StudioUiOntologySafeDesignFact[]
  promptGuardrails: string[]
  blockedReasons: string[]
  rendererBlankTypes: number
  targetPlatform: StudioGenerationTargetPlatform
  adaptiveRuntime: StudioAdaptiveRuntimeEvidenceSummary
  goldenRecipes: StudioGoldenRecipeEvidenceSummary
  styleSeparationContract: StyleSeparationGeneratorContract
  reportApplicationShellContract: ReportApplicationShellContract
  generatedUiVerificationPolicy: StudioGeneratedUiVerificationPolicyConstraints
}

export interface StudioGeneratedUiVerificationPolicyConstraints {
  schemaVersion: 'studio-generated-ui-verification-policy-constraints.v1'
  featureFlag: 'COGO_IMAGE_EVIDENCE_VERIFICATION'
  enabled: boolean
  canonicalProvenanceTiers: string[]
  requiredWhen: string[]
  publishRule: 'block-when-summary-blocked-or-promotion-disallowed'
}

export interface StudioProjectPlanEvidenceSource {
  intent?: string
  appClass?: string
  runtimeTargets?: readonly string[]
  nativeTargetProof?: unknown
  adaptiveRuntime?: unknown
}

export interface RenderEngineRegistrySummary {
  status: 'pass'
  denominator: number
  fullyCovered: number
  drift: number
  engines: {
    web: { bound: number; missing: number }
    rn: { bound: number; missing: number }
    flutter: { bound: number; missing: number; direct: number; subpart: number }
  }
  sources: string[]
}

export type StudioRendererTargetReadiness = 'ready' | 'partial' | 'blocked' | 'not-requested'
export type StudioRendererAxisStatus = 'pass' | 'partial' | 'pending' | 'not-applicable'
export type StudioGeneratedAppArtifactParityStatus = 'pending' | 'pass' | 'partial' | 'not-applicable'
export type StudioEndUserAppProofStatus = 'pass' | 'fail'
export type StudioReferoBatchAdmissionStatus = 'open' | 'blocked'
export type StudioEndUserScenarioId = 'shopping' | 'complex-mobile' | 'backoffice-data-table'
export type StudioEndUserScenarioStatus = 'pass' | 'partial' | 'fail' | 'not-requested'
export type StudioEndUserScenarioMatrixStatus = 'pass' | 'partial' | 'fail'
export type StudioProductionGenerationEvidenceStatus = 'pass' | 'partial' | 'blocked'

export interface StudioProductionGenerationEvidenceChain {
  schemaVersion: 'cogo.production-generation-evidence-chain.v1'
  status: StudioProductionGenerationEvidenceStatus
  sourceEvidenceRefs: string[]
  validationEvidenceRefs: string[]
  selectedEvidenceCount: number
  evidenceBackedBlockCount: number
  totalBlockCount: number
  llmOnly: boolean
  fallbackHidden: boolean
  deterministic?: boolean
  generatedAt?: string
  provenance?: Record<string, unknown>
}

export type StudioApplicationConformanceOracleStatus = 'pass' | 'blocked' | 'missing'
export type StudioVisibleFromJsonOracleStatus = 'pass' | 'blocked' | 'missing'

export interface StudioApplicationConformanceDimensionSummary {
  dimension: string
  status: 'pass' | 'blocked' | 'missing'
  checkedCount: number
  divergenceCount: number
  firstDivergenceReason?: string
}

export interface StudioApplicationConformanceOracleSummary {
  schemaVersion: 'studio-application-conformance-oracle.v1'
  sourceSchemaVersion?: 'cogo.application-conformance-oracle.v1'
  status: StudioApplicationConformanceOracleStatus
  required: boolean
  blockCount: number
  checkedBlockNames: string[]
  dimensions: StudioApplicationConformanceDimensionSummary[]
  blockedReasons: string[]
  notes: string[]
}

export interface StudioVisibleFromJsonCheckedSlotSummary {
  slotId?: string
  nodeId?: string
  label?: string
  value: string
  required: boolean
  foundTextNodeCount: number
  visibleTextNodeCount: number
  status: 'pass' | 'blocked'
  firstReason?: string
}

export interface StudioVisibleFromJsonOracleSummary {
  schemaVersion: 'studio-visible-from-json-oracle.v1'
  sourceSchemaVersion?: typeof VISIBLE_FROM_JSON_ORACLE_SCHEMA_VERSION
  status: StudioVisibleFromJsonOracleStatus
  required: boolean
  checkedSurface: 'cogo-json-render-tree' | 'missing'
  declaredSlotCount: number
  visibleSlotCount: number
  hiddenSlotCount: number
  missingSlotCount: number
  rootRasterOnly: boolean
  rasterAuthorityDetected: boolean
  blockedReasons: string[]
  checkedSlots: StudioVisibleFromJsonCheckedSlotSummary[]
  notes: string[]
}

export interface StudioRendererTargetMatrix {
  schemaVersion: 'studio-renderer-target-matrix.v1'
  selectedTargetPlatform: StudioGenerationTargetPlatform
  requestedRuntimeTargets: StudioRuntimeTargetEvidence[]
  targets: {
    web: StudioRendererTargetMatrixEntry
    rn: StudioRendererTargetMatrixEntry
    flutter: StudioRendererTargetMatrixEntry
  }
}

export interface StudioRendererTargetMatrixEntry {
  readiness: StudioRendererTargetReadiness
  structuralCoverage: StudioRendererAxisStatus
  r2VisualFaithfulness: StudioRendererAxisStatus
  generatedArtifactParity: StudioRendererAxisStatus
  nativeRuntimeParity: StudioRendererAxisStatus
  nativeLivePackaging: StudioRendererAxisStatus
  evidence: string[]
  partialReasons: string[]
}

export type StudioNativeTargetId = 'rn' | 'flutter'
export type StudioNativeTargetProofStatus = 'pass' | 'partial' | 'blocked' | 'missing'

export interface StudioNativeTargetProofEntry {
  status: StudioNativeTargetProofStatus
  nativeRuntimeParity: StudioRendererAxisStatus
  nativeLivePackaging: StudioRendererAxisStatus
  r2VisualFaithfulness: StudioRendererAxisStatus
  evidence: string[]
  reasons: string[]
}

export interface StudioNativeTargetProofSummary {
  schemaVersion: 'studio-native-target-proof.v1'
  status: StudioNativeTargetProofStatus
  /** Same trust contract as the coverage/proof audits: app-document
   * provenance (dug from a generated document) can never mint a pass. */
  provenance?: StudioEvidenceAuditProvenance
  targets: Record<StudioNativeTargetId, StudioNativeTargetProofEntry>
  sources: string[]
}

export interface ActionFlowContractSummary {
  status: 'pass'
  executorSurface: 'runtime-honored'
  tboxParamDrift: number
  longTailParamCapabilities: 'drained'
  registrySchemaVersion: string
  capabilityCount: number
  generationAllowedCount: number
  directAtomicActionCount: number
  sources: string[]
}

export type StudioFullCoverageAuditStatus = 'pass' | 'partial' | 'blocked' | 'missing'

/**
 * Where an audit entered the evidence pipeline. `studio-input` = the
 * explicit top-level fields of the evidence source object Studio code
 * constructs (tests, CI/local audit attach). `app-document` = dug out of a
 * generated app/block document (metadata/meta/manifest) — a channel the
 * generator (LLM) can write. App-document audits may inform panels but can
 * never mint a `pass` (D-406: a pass must come from a channel the
 * generator cannot write).
 */
export type StudioEvidenceAuditProvenance = 'studio-input' | 'app-document'

export interface StudioFullCoverageEvidenceSummary {
  schemaVersion: 'studio-full-coverage-evidence.v1'
  status: StudioFullCoverageAuditStatus
  provenance?: StudioEvidenceAuditProvenance
  componentOntologyCount: number
  actionOntologyCount: number
  rendererFullyCoveredCount: number
  componentCapabilityCatalogCount: number
  componentCapabilityDenominator: number
  componentCapabilityPlatformCoverage: {
    webSupported: number
    rnSupported: number
    flutterSupported: number
    renderPackComponents: number
  }
  componentCapabilityHighRiskComponents: string[]
  bundledStyleCount: number
  flatbufferMappedComponentCount: number
  blockingFindingCount: number
  sectionStatuses: {
    uiElements: StudioFullCoverageAuditStatus
    renderers: StudioFullCoverageAuditStatus
    componentCapabilities: StudioFullCoverageAuditStatus
    styleSeparation: StudioFullCoverageAuditStatus
    actions: StudioFullCoverageAuditStatus
    flatbuffer: StudioFullCoverageAuditStatus
  }
  findings: Array<{
    code: string
    severity: Exclude<StudioFullCoverageAuditStatus, 'missing'>
    message: string
    sample: string[]
  }>
  sources: string[]
}

export interface StudioExhaustiveProofEvidenceSummary {
  schemaVersion: 'studio-exhaustive-proof-evidence.v1'
  status: StudioFullCoverageAuditStatus
  provenance?: StudioEvidenceAuditProvenance
  ontologyComponentCount: number
  styleCount: number
  contractProbeCount: number
  renderPackProbeCount: number
  ssrRenderedComponentCount: number
  soloSsrRenderedComponentCount: number
  compoundSsrRenderedComponentCount: number
  ssrMissingComponentCount: number
  providerSlotHydrationProofStatus: StudioFullCoverageAuditStatus
  providerSlotHydratedComponentCount: number
  providerSlotHydrationRequiredComponentCount: number
  runtimeHonoredActionCount: number
  runtimeExecutedActionCount: number
  runtimeAdapterProvenActionCount: number
  runtimeProvenActionCount: number
  runtimeAdapterRequiredActionCount: number
  runtimeNotExecutedActionCount: number
  collapsedStyleSelectors: string[]
  blockingFindingCount: number
  partialFindingCount: number
  findings: Array<{
    code: string
    severity: Exclude<StudioFullCoverageAuditStatus, 'missing'>
    message: string
    sample: string[]
  }>
  sources: string[]
}

export interface UiOntologyGenerationSummary {
  status: 'pass' | 'blocked' | 'fallback'
  mode: 'ontology-driven' | 'llm-fallback' | 'blocked'
  targetPlatform: StudioGenerationTargetPlatform
  runtimeTargets: StudioRuntimeTargetEvidence[]
  adaptiveRuntime: StudioAdaptiveRuntimeEvidenceSummary
  goldenRecipes: StudioGoldenRecipeEvidenceSummary
  rendererBlankTypes: number
  preflight: StudioUiOntologyPreflightSummary
  sources: string[]
}

export interface StudioAdaptiveRuntimeEvidenceSummary {
  schemaVersion: 'studio-adaptive-runtime-evidence.v1'
  status: 'planned' | 'missing'
  enabled: boolean
  situationSignals: string[]
  adaptationAxes: string[]
  localStatePolicy: string
  globalStatePolicy: string
  evidencePolicy: {
    requireMeasuredDesignEvidence: boolean
    quarantineUnmeasuredOntologyFacts: boolean
    requireActionFlowTraceForDynamicApps: boolean
  }
  notes: string[]
}

export interface StudioGoldenRecipeEvidenceSummary {
  schemaVersion: 'studio-golden-recipe-evidence.v1'
  status: 'available' | 'missing'
  recipeIds: string[]
  scenarioIds: StudioEndUserScenarioId[]
  designSystemGates: string[]
  actionFlowContracts: string[]
  browserProof: string[]
}

export interface StudioUiOntologyRagCandidate {
  blockName: string
  blockType: string
  candidateId: string
  patternId: string
  score: number
  reason: string
}

export interface StudioUiOntologyBindPlanItem {
  blockName: string
  blockType: string
  bindingCount: number
  eventTypes: string[]
  stateScopes: string[]
  notes: string[]
}

export interface StudioUiOntologyFallbackCandidateFact {
  code: string
  blockName?: string
  blockType?: string
  reviewStatus: 'pending-review' | 'quarantine'
  message: string
}

export interface StudioUiOntologySafeDesignFact {
  kind: string
  normalizedTarget: string
  confidence: number
}

export interface StudioGenerationRetrievalRoutingSummary {
  schemaVersion: 'agentic-generation-retrieval-routing.v1'
  policy: {
    admittedGenerationUse: 'admitted-only'
    blockedGenerationUse: 'mining-only'
    failClosed: boolean
  }
  admittedRetrieval: Array<{
    retrievalId: string
    inventoryId: string
    itemName: string
    itemKind: string
    admissionTier: 'T4' | 'T5'
    lanes: string[]
    generationUse: 'admitted-only'
    staticGenerationAllowed: boolean
    interactiveGenerationAllowed: boolean
    promptHint: string
  }>
  referoStyles: Array<{
    styleId: string
    title: string
    theme: 'light' | 'dark'
    roles: Record<string, string>
    verifiedSampleBlocks: string[]
    promptHint: string
  }>
  gapGuidance: Array<{
    guidanceId: string
    inventoryId: string
    itemName: string
    gapStage: 'render-admission' | 'actionflow-admission'
    gapCategory: string
    toolQueue: string
    generationUse: 'mining-only'
    generationAllowed: false
    negativeConstraints: string[]
    nextGate: string
  }>
}

export interface StudioUiOntologyPreflightSummary {
  mode: 'ontology-driven' | 'llm-fallback' | 'blocked'
  canGenerate: boolean
  ragCandidates: StudioUiOntologyRagCandidate[]
  bindPlan: StudioUiOntologyBindPlanItem[]
  fallbackCandidateFacts: StudioUiOntologyFallbackCandidateFact[]
  safeDesignFacts: StudioUiOntologySafeDesignFact[]
  negativeConstraints: string[]
  generationRetrievalRouting: StudioGenerationRetrievalRoutingSummary
  promptGuardrails: string[]
  blockedReasons: string[]
  deterministicCandidateCount: number
}

export interface StudioGeneratedAppReadinessSummary {
  status: StudioGeneratedAppReadinessStatus
  appClass: 'static' | 'dynamic' | 'hybrid' | 'unknown'
  flowCount: number
  eventBindingCount: number
  datasourceCount: number
  stateVariableCount: number
  localStateVariableCount: number
  globalStateVariableCount: number
  stateIsolation: 'pass' | 'partial' | 'not-required'
  actionFlowRequired: boolean
  artifactParity: StudioGeneratedAppArtifactParitySummary
  styleSeparation: StudioGeneratedAppStyleSeparationSummary
  reportApplicationShell: StudioGeneratedAppReportApplicationShellSummary
  applicationConformance: StudioApplicationConformanceOracleSummary
  visibleFromJson: StudioVisibleFromJsonOracleSummary
  imageEvidenceVerification: GeneratedUiVerificationSummary
  imageEvidenceCandidateAdmission?: ImageEvidenceCandidateAdmissionResult
  notes: string[]
}

export interface StudioGeneratedAppReportApplicationShellSummary {
  schemaVersion: 'studio-generated-app-report-application-shell.v1'
  status: 'not-applicable' | 'pending' | 'pass' | 'partial'
  applicable: boolean
  findingCount: number
  summary: ReportApplicationShellReport['summary']
  firstFinding?: {
    rule: string
    severity: string
    path: string
  }
  notes: string[]
}

export interface StudioGeneratedAppStyleSeparationSummary {
  schemaVersion: 'studio-generated-app-style-separation.v1'
  status: 'pending' | 'pass' | 'partial'
  checkedBlockCount: number
  blockedBlockCount: number
  findingCount: number
  visualClassNameTokenCount: number
  rawStyleValueCount: number
  inlineStyleObjectCount: number
  inlineVisualStyleCount: number
  unresolvedDesignAuthorityCount: number
  reports: Array<{
    blockId: string
    status: StyleSeparationReport['status']
    findingCount: number
    summary: StyleSeparationReport['summary']
    firstFinding?: {
      rule: string
      severity: string
      path: string
      token?: string
      key?: string
    }
  }>
  notes: string[]
}

export interface StudioGeneratedAppArtifactParitySummary {
  status: StudioGeneratedAppArtifactParityStatus
  manifestPresent: boolean
  blockCount: number
  pageCount: number
  missingPageBlockRefs: string[]
  targetPlatforms: StudioRuntimeTargetEvidence[]
  notes: string[]
}

export interface StudioGeneratedAppReadinessGate {
  scope: 'generated-app'
  status: StudioGeneratedAppReadinessStatus | 'blocked'
  separatesGlobalOntologyPass: true
  canPreview: boolean
  canPublish: boolean
  requiredEvidence: string[]
  blockedReasons: string[]
  counts: {
    flows: number
    eventBindings: number
    datasources: number
    stateVariables: number
  }
  targetSupport: {
    web: 'pass'
    rn: 'pass' | 'partial'
    flutter: 'pass' | 'partial'
  }
}

export type StudioAgenticRagValidationAssetKind =
  | 'reference-render'
  | 'component-oracle'
  | 'app-oracle'
  | 'contract-scenario'
  | 'forbidden-pattern'

export type StudioAgenticRagValidationAssetStatus =
  | 'admitted'
  | 'candidate'
  | 'quarantined'
  | 'rejected'
  | 'deprecated'

export type StudioAgenticRagValidationAssetAuthority =
  | 'canonical'
  | 'promoted'
  | 'candidate'
  | 'quarantined'

export interface StudioAgenticRagValidationAssetEvidence {
  kind: StudioAgenticRagValidationAssetKind
  assetId: string
  sourceRef: string
  status: StudioAgenticRagValidationAssetStatus
  authority: StudioAgenticRagValidationAssetAuthority
  score?: number
  evidenceRefs: string[]
}

export interface StudioAgenticRagValidationProof {
  schemaVersion: 'studio-agentic-rag-validation-proof.v1'
  status: 'pass' | 'blocked'
  requiredKinds: StudioAgenticRagValidationAssetKind[]
  coveredKinds: StudioAgenticRagValidationAssetKind[]
  missingKinds: StudioAgenticRagValidationAssetKind[]
  minimumEvidenceCount: number
  acceptedEvidenceIds: string[]
  rejectedEvidenceIds: string[]
  policy: {
    admittedValidationUse: 'admitted-only'
    candidateValidationUse: 'blocked'
    failClosed: true
  }
  reasons: string[]
}

export interface StudioEndUserAppGenerationProof {
  schemaVersion: 'studio-end-user-app-generation-proof.v1'
  speed: StudioEndUserAppSpeedEvidence
  quality: StudioEndUserAppQualityEvidence
  validationRag: StudioAgenticRagValidationProof
  designSandbox: StudioEndUserDesignSandboxIsolationProof
  adaptiveRuntime: StudioEndUserAdaptiveRuntimeProof
  scenarioMatrix: StudioEndUserScenarioMatrix
  referoBatch: StudioReferoOntologyBatchAdmission
  generatedAt: string
}

export interface StudioEndUserDesignSandboxIsolationProof {
  schemaVersion: 'studio-end-user-design-sandbox-isolation-proof.v1'
  status: 'pass' | 'fail' | 'missing'
  appRootSandboxed: boolean | null
  portalInsideAppHost: boolean | null
  portalInsideAppSurface: boolean | null
  htmlHasAppStyleClass: boolean | null
  bodyHasAppStyleClass: boolean | null
  htmlHasCompleteTokenAttr: boolean | null
  bodyHasCompleteTokenAttr: boolean | null
  portalCount: number
  portalId: string | null
  portalAppId: string | null
  portalParentTag: string | null
  appHostHasCompleteTokenAttr: boolean | null
  appRenderRootHasCompleteTokenAttr: boolean | null
  appHostDesignScope: string | null
  outsideAppCompleteTokenScopeCount: number
  outsideAppInlinePrimaryTokenCount: number
  appStyleElementCount: number
  appStyleElementsInAppHostCount: number
  appStyleElementsInHeadCount: number
  outsideAppStyleElementCount: number
  portalHasStyleClass: boolean | null
  portalHasCompleteTokenAttr: boolean | null
  appHostBackgroundToken?: string
  appHostForegroundToken?: string
  portalBackgroundToken?: string
  portalForegroundToken?: string
  reasons: string[]
}

export interface StudioEndUserAdaptiveRuntimeProof {
  schemaVersion: 'studio-end-user-adaptive-runtime-proof.v1'
  status: 'pass' | 'partial' | 'missing'
  enabled: boolean
  situationSignals: string[]
  adaptationAxes: string[]
  localStatePolicy: string
  globalStatePolicy: string
  transitionCount: number
  passedTransitionCount: number
  transitions: StudioEndUserAdaptiveTransitionEvidence[]
  axisCoverage: StudioEndUserAdaptiveAxisCoverageProof
  stateBoundary: StudioEndUserStateBoundaryProof
  evidence: string[]
  reasons: string[]
}

export interface StudioEndUserAdaptiveAxisCoverageProof {
  schemaVersion: 'studio-end-user-adaptive-axis-coverage-proof.v1'
  status: 'pass' | 'fail' | 'missing'
  requiredAxes: string[]
  passedAxes: string[]
  missingAxes: string[]
  reasons: string[]
}

export interface StudioEndUserStateBoundaryProof {
  schemaVersion: 'studio-end-user-state-boundary-proof.v1'
  status: 'pass' | 'fail' | 'missing'
  localStateEvidencePaths: string[]
  globalStateEvidencePaths: string[]
  resultStateEvidencePaths: string[]
  computedStateEvidencePaths: string[]
  ambiguousStateEvidencePaths: string[]
  legacyStateEvidencePaths: string[]
  transitionScopes: string[]
  localOnlyTransitionCount: number
  globalOnlyTransitionCount: number
  mixedLocalGlobalTransitionCount: number
  resultTransitionCount: number
  computedTransitionCount: number
  designTokenTransitionCount: number
  reasons: string[]
}

export interface StudioEndUserScenarioMatrix {
  schemaVersion: 'studio-end-user-scenario-matrix.v1'
  status: StudioEndUserScenarioMatrixStatus
  requiredScenarios: StudioEndUserScenarioId[]
  passedScenarios: StudioEndUserScenarioId[]
  scenarios: StudioEndUserScenarioEvidence[]
  reasons: string[]
}

export interface StudioEndUserScenarioEvidence {
  id: StudioEndUserScenarioId
  label: string
  status: StudioEndUserScenarioStatus
  required: boolean
  signals: string[]
  interactions: string[]
  stateEvidence: string[]
  dataEvidence: string[]
  viewportEvidence: string[]
  artifactEvidence: string[]
  actionFlowTraceEvidence: string[]
  actionFlowProof: StudioEndUserActionFlowRuntimeProof | null
  adaptiveTransitions: StudioEndUserAdaptiveTransitionEvidence[]
  reasons: string[]
}

export interface StudioEndUserActionFlowRuntimeProof {
  schemaVersion: 'studio-end-user-actionflow-runtime-proof.v1'
  status: 'pass' | 'fail'
  traceCount: number
  requiredFlowCount: number
  passedFlowCount: number
  flowNames: string[]
  stateDiffPaths: string[]
  actionNames: string[]
  resultKeys: string[]
  computedKeys: string[]
  rendererProjectionTexts: string[]
  requirements: Array<{
    flowName: string
    status: 'pass' | 'fail'
    reasons: string[]
  }>
  reasons: string[]
}

export interface StudioEndUserAdaptiveTransitionEvidence {
  signal: string
  axis: string
  before: string
  after: string
  uiProbe: string
  stateScope: string
  actionFlow: string
  status: 'pass' | 'fail'
  reasons: string[]
}

export interface StudioEndUserDesignStyleSwitchProof {
  schemaVersion: 'studio-end-user-design-style-switch-proof.v1'
  status: StudioEndUserAppProofStatus | 'missing'
  mutationApi: 'app-design-sandbox' | 'legacy-theme-slice' | 'missing'
  structuralDomIdentical: boolean | null
  basePrimaryBackground: string | null
  targetPrimaryBackground: string | null
  primaryBackgroundChanged: boolean
  appHostHasCompleteTokenAttr: boolean | null
  targetPrimaryToken: string | null
  targetColorPrimaryToken: string | null
  studioHtmlPrimaryChanged: boolean | null
  studioBodyPrimaryChanged: boolean | null
  studioHtmlHasCompleteTokenAttr: boolean | null
  studioBodyHasCompleteTokenAttr: boolean | null
  reasons: string[]
}

export interface StudioEndUserScenarioBrowserEvidence {
  id: StudioEndUserScenarioId
  status?: Exclude<StudioEndUserScenarioStatus, 'not-requested'>
  signals?: readonly string[]
  interactions?: readonly string[]
  stateEvidence?: readonly string[]
  dataEvidence?: readonly string[]
  viewportEvidence?: readonly string[]
  artifactEvidence?: readonly string[]
  actionFlowTraceEvidence?: readonly string[]
  actionFlowProof?: Partial<StudioEndUserActionFlowRuntimeProof> | null
  adaptiveTransitions?: readonly Partial<StudioEndUserAdaptiveTransitionEvidence>[]
  reasons?: readonly string[]
}

export interface StudioEndUserAppSpeedEvidence {
  status: StudioEndUserAppProofStatus
  totalMs: number
  generationMs: number
  validationMs: number
  firstBlockMs: number | null
  budgets: {
    totalMs: number
    validationMs: number
    firstBlockMs: number
  }
  reasons: string[]
}

export interface StudioEndUserAppQualityEvidence {
  status: StudioEndUserAppProofStatus
  averageScore: number
  scoreFloor: number
  designQualityStatus: 'pass' | 'warn' | 'fail' | 'metric-only' | 'missing'
  designQualityScore: number
  designQualityScoreFloor: number
  designQualityHardeningPassed: boolean
  designQualityArtifactCount: number
  designQualitySourceKind: DesignQualityEvidenceSourceKind | 'missing'
  designQualityMeasuredBy?: string
  designQualityCapturedAt?: string
  designQualityInteractionTraceArtifactCount: number
  designQualityAuthorStyleObjectCount: number
  designQualityUnconvertedStyleObjectCount: number
  visualRichness: StudioEndUserVisualRichnessProof
  overallGrade: string
  blockCount: number
  minBlockScore: number | null
  crossBlockIssueCount: number
  generatedAppStatus: StudioGeneratedAppReadinessStatus | 'blocked'
  artifactParityStatus: StudioGeneratedAppArtifactParityStatus
  renderedBlockCount: number | null
  screenshotArtifactCount: number
  brokenImageCount: number | null
  nonFixturePrompt: boolean | null
  designStyleSwitch: StudioEndUserDesignStyleSwitchProof
  reasons: string[]
}

export interface StudioEndUserVisualRichnessProof {
  schemaVersion: 'studio-end-user-visual-richness-proof.v1'
  status: 'pass' | 'fail' | 'missing'
  scenarioCount: number
  requiredScenarioCount: number
  minDistinctSurfaceColorCount: number
  shoppingProminentMediaCount: number
  minSemanticVisualTypeCount: number
  reasons: string[]
}

export interface StudioReferoOntologyBatchAdmission {
  schemaVersion: 'studio-refero-ontology-batch-admission.v1'
  status: StudioReferoBatchAdmissionStatus
  canRun: boolean
  requiredEvidence: string[]
  blockedReasons: string[]
  nextAction: string
}

export interface StudioEndUserAppGenerationProofInput {
  evidence: StudioGenerationEvidence
  app?: unknown
  startedAtMs: number
  completedAtMs: number
  validationDurationMs: number
  firstBlockAtMs?: number | null
  validationResult?: StudioAppValidationResultSource | null
  blockQualityScores?: Record<string, number> | number[]
  browserEvidence?: StudioEndUserAppBrowserEvidence | null
  validationRagEvidence?: unknown
  thresholds?: Partial<StudioEndUserAppProofThresholds>
}

export interface StudioEndUserBrowserEvidenceUpgradeInput {
  evidence: StudioGenerationEvidence
  app?: unknown
  proof: StudioEndUserAppGenerationProof
  browserEvidence: StudioEndUserAppBrowserEvidence
}

export interface StudioAppValidationResultSource {
  scores?: Record<string, {
    score?: number
    grade?: string
    errors?: unknown[]
    warnings?: unknown[]
  }>
  crossBlock?: {
    issueCount?: number
  }
  averageScore?: number
  overallGrade?: string
  blockCount?: number
}

export interface StudioEndUserAppBrowserEvidence {
  renderedBlockCount?: number
  screenshotArtifacts?: readonly string[]
  brokenImageCount?: number
  nonFixturePrompt?: boolean
  requiredScenarioIds?: readonly StudioEndUserScenarioId[]
  validationResult?: StudioAppValidationResultSource | null
  blockQualityScores?: Record<string, number> | number[]
  validationRagEvidence?: unknown
  designQuality?: StudioEndUserDesignQualityBrowserEvidence
  designSandboxIsolation?: StudioEndUserDesignSandboxIsolationBrowserEvidence
  designStyleSwitch?: StudioEndUserDesignStyleSwitchBrowserEvidence
  scenarioEvidence?: readonly StudioEndUserScenarioBrowserEvidence[]
}

export interface StudioEndUserDesignQualityBrowserEvidence {
  status?: StudioEndUserAppQualityEvidence['designQualityStatus']
  score?: number
  sourceKind?: DesignQualityEvidenceSourceKind | 'missing'
  measuredBy?: string
  capturedAt?: string
  hardeningPassed?: boolean
  artifactCount?: number
  screenshotArtifactCount?: number
  interactionTraceArtifactCount?: number
  authorStyleObjectCount?: number
  unconvertedStyleObjectCount?: number
  rawStyleLeakCount?: number
  prominentMediaCount?: number
  distinctSurfaceColorCount?: number
  semanticVisualTypeCount?: number
  reasons?: readonly string[]
}

export interface StudioEndUserDesignStyleSwitchBrowserEvidence {
  status?: 'pass' | 'fail'
  mutationApi?: string | null
  structuralDomIdentical?: boolean | null
  basePrimaryBackground?: string | null
  targetPrimaryBackground?: string | null
  appHostHasCompleteTokenAttr?: boolean | null
  targetPrimaryToken?: string | null
  targetColorPrimaryToken?: string | null
  studioHtmlPrimaryChanged?: boolean | null
  studioBodyPrimaryChanged?: boolean | null
  studioHtmlHasCompleteTokenAttr?: boolean | null
  studioBodyHasCompleteTokenAttr?: boolean | null
  reasons?: readonly string[]
}

export interface StudioEndUserDesignSandboxIsolationBrowserEvidence {
  status?: 'pass' | 'fail'
  appRootSandboxed?: boolean | string | null
  portalInsideAppHost?: boolean | string | null
  portalInsideAppSurface?: boolean | string | null
  htmlHasAppStyleClass?: boolean | null
  bodyHasAppStyleClass?: boolean | null
  htmlHasCompleteTokenAttr?: boolean | null
  bodyHasCompleteTokenAttr?: boolean | null
  portalCount?: number
  portalId?: string | null
  portalAppId?: string | null
  portalParentTag?: string | null
  appHostHasCompleteTokenAttr?: boolean | null
  appRenderRootHasCompleteTokenAttr?: boolean | null
  appHostDesignScope?: string | null
  appHostBackgroundToken?: string
  appHostForegroundToken?: string
  outsideAppCompleteTokenScopeCount?: number
  outsideAppInlinePrimaryTokenCount?: number
  appStyleElementCount?: number
  appStyleElementsInAppHostCount?: number
  appStyleElementsInHeadCount?: number
  outsideAppStyleElementCount?: number
  portalHasStyleClass?: boolean | null
  portalHasCompleteTokenAttr?: boolean | null
  portalBackgroundToken?: string
  portalForegroundToken?: string
  reasons?: readonly string[]
}

export interface StudioEndUserAppProofThresholds {
  totalMs: number
  validationMs: number
  firstBlockMs: number
  scoreFloor: number
  designQualityScoreFloor: number
  requireDesignQualityArtifacts: boolean
}

export interface StudioGenerationEvidence {
  schemaVersion: 'studio-generation-evidence.v1'
  uiOntology: UiOntologyGenerationSummary
  renderEngine: RenderEngineRegistrySummary
  actionflow: ActionFlowContractSummary
  fullCoverage: StudioFullCoverageEvidenceSummary
  exhaustiveProof: StudioExhaustiveProofEvidenceSummary
  nativeTargetProof: StudioNativeTargetProofSummary
  applicationConformanceOracle: StudioApplicationConformanceOracleSummary
  generatedApp: StudioGeneratedAppReadinessSummary
  productionEvidenceChain?: StudioProductionGenerationEvidenceChain
  endUserAppProof?: StudioEndUserAppGenerationProof
  generatedAt: string
}

const VALID_RUNTIME_TARGETS = new Set<StudioRuntimeTargetEvidence>([
  'web',
  'rn',
  'flutter',
  'mobile',
  'desktop',
])

const GENERIC_BLOCK_TYPES = new Set(['section', 'container', 'block', 'custom', 'panel'])
const BLOCKED_BLOCK_TYPES = new Set(['script', 'style', 'raw-html', 'html', 'head', 'body'])
const END_USER_SCENARIO_IDS: StudioEndUserScenarioId[] = [
  'shopping',
  'complex-mobile',
  'backoffice-data-table',
]

const END_USER_SCENARIO_LABELS: Record<StudioEndUserScenarioId, string> = {
  shopping: 'Shopping / Commerce',
  'complex-mobile': 'Complex Mobile App',
  'backoffice-data-table': 'Backoffice Data Table',
}

const REQUIRED_AGENTIC_VALIDATION_RAG_KINDS: StudioAgenticRagValidationAssetKind[] = [
  'reference-render',
  'component-oracle',
  'app-oracle',
  'contract-scenario',
  'forbidden-pattern',
]

const REQUIRED_ADAPTIVE_AXES_BY_SCENARIO: Record<StudioEndUserScenarioId, string[]> = {
  shopping: ['workflow', 'action-availability', 'design-style'],
  'complex-mobile': ['layout-density', 'action-availability', 'design-style'],
  'backoffice-data-table': ['data-scope', 'action-availability', 'design-style'],
}

export const STUDIO_RENDER_ENGINE_REGISTRY_SUMMARY: RenderEngineRegistrySummary = {
  status: 'pass',
  denominator: 190,
  fullyCovered: 190,
  drift: 0,
  engines: {
    web: { bound: 190, missing: 0 },
    rn: { bound: 190, missing: 0 },
    flutter: { bound: 190, missing: 0, direct: 141, subpart: 49 },
  },
  sources: [
    'packages/cogo-headless/src/render-engine-registry.ts',
    'scripts/render-engine-registry.md',
    'scripts/flutter-renderable-types.json',
    'cogo-web PRs #920/#922/#923/#924/#925',
  ],
}

export const STUDIO_ACTIONFLOW_CONTRACT_SUMMARY: ActionFlowContractSummary = {
  status: 'pass',
  executorSurface: 'runtime-honored',
  tboxParamDrift: 0,
  longTailParamCapabilities: 'drained',
  registrySchemaVersion: ACTION_CAPABILITY_REGISTRY_SCHEMA_VERSION,
  capabilityCount: ACTION_CAPABILITY_LIST.length,
  generationAllowedCount: ACTION_CAPABILITY_LIST.filter(
    (capability) => capability.generationPolicy === 'allow',
  ).length,
  directAtomicActionCount: ACTION_CAPABILITY_LIST.filter(
    (capability) =>
      capability.lifecycle === 'active' &&
      capability.registryExposure === 'public' &&
      capability.runtimeImplemented &&
      capability.generationPolicy === 'allow' &&
      capability.semantics.atomic,
  ).length,
  sources: [
    'packages/cogo-action-registry/src/generated/action-capability-registry.v1.json',
    'packages/cogo-kg-browser/fixtures/derived/action-capabilities-v1.ttl',
    'packages/cogo-kg-browser/fixtures/actions-v1.ttl',
    'packages/cogo-kg-browser/fixtures/actionflow-executors-v1.ttl',
    'packages/cogo-kg-browser/fixtures/action-params-shapes-v1.ttl',
    'cogo-web PRs #913/#914/#915/#916/#917/#918',
  ],
}

export const STUDIO_END_USER_APP_PROOF_THRESHOLDS: StudioEndUserAppProofThresholds = {
  totalMs: 240_000,
  validationMs: 30_000,
  firstBlockMs: 60_000,
  scoreFloor: 85,
  designQualityScoreFloor: 90,
  requireDesignQualityArtifacts: true,
}

export function normalizeStudioRuntimeTargets(
  source?: StudioGenerationEvidenceSource | null,
  projectPlan?: StudioProjectPlanEvidenceSource | null,
): StudioRuntimeTargetEvidence[] {
  const explicitTargets = [
    ...(source?.runtimeTargets ?? []),
    ...(projectPlan?.runtimeTargets ?? []),
  ].filter((target): target is StudioRuntimeTargetEvidence =>
    typeof target === 'string' && VALID_RUNTIME_TARGETS.has(target as StudioRuntimeTargetEvidence),
  )

  if (explicitTargets.length > 0) {
    return [...new Set(explicitTargets)]
  }

  if (source?.targetPlatform === 'mobile') return ['mobile']
  if (source?.targetPlatform === 'both') return ['web', 'mobile']
  return ['web']
}

export function generationTargetPlatformForEvidence(
  source?: StudioGenerationEvidenceSource | null,
  projectPlan?: StudioProjectPlanEvidenceSource | null,
): StudioGenerationTargetPlatform {
  if (source?.generationTargetPlatform === 'flutter') return 'flutter'
  if (source?.generationTargetPlatform === 'react') return 'react'

  const runtimeTargets = normalizeStudioRuntimeTargets(source, projectPlan)
  if (runtimeTargets.includes('flutter') || runtimeTargets.includes('mobile')) {
    return 'flutter'
  }
  return 'react'
}

function buildStudioAdaptiveRuntimeEvidence(
  source?: StudioGenerationEvidenceSource | null,
  projectPlan?: StudioProjectPlanEvidenceSource | null,
): StudioAdaptiveRuntimeEvidenceSummary {
  const sourcePlan = readRecord(source?.projectPlan)
  const adaptive =
    readRecord(source?.adaptiveRuntime) ??
    readRecord(sourcePlan?.adaptiveRuntime) ??
    readRecord(projectPlan?.adaptiveRuntime)

  if (!adaptive) {
    return {
      schemaVersion: 'studio-adaptive-runtime-evidence.v1',
      status: 'missing',
      enabled: false,
      situationSignals: [],
      adaptationAxes: [],
      localStatePolicy: 'missing',
      globalStatePolicy: 'missing',
      evidencePolicy: {
        requireMeasuredDesignEvidence: false,
        quarantineUnmeasuredOntologyFacts: false,
        requireActionFlowTraceForDynamicApps: false,
      },
      notes: ['Adaptive runtime planning metadata is missing from the generation request.'],
    }
  }

  const evidencePolicy = readRecord(adaptive.evidencePolicy)
  return {
    schemaVersion: 'studio-adaptive-runtime-evidence.v1',
    status: 'planned',
    enabled: adaptive.enabled !== false,
    situationSignals: normalizeStringArray(readArray(adaptive.situationSignals) ?? undefined),
    adaptationAxes: normalizeStringArray(readArray(adaptive.adaptationAxes) ?? undefined),
    localStatePolicy: typeof adaptive.localStatePolicy === 'string' ? adaptive.localStatePolicy : 'block-local-first',
    globalStatePolicy: typeof adaptive.globalStatePolicy === 'string' ? adaptive.globalStatePolicy : 'appData-shared',
    evidencePolicy: {
      requireMeasuredDesignEvidence: evidencePolicy?.requireMeasuredDesignEvidence !== false,
      quarantineUnmeasuredOntologyFacts: evidencePolicy?.quarantineUnmeasuredOntologyFacts !== false,
      requireActionFlowTraceForDynamicApps: evidencePolicy?.requireActionFlowTraceForDynamicApps === true,
    },
    notes: normalizeStringArray(readArray(adaptive.notes) ?? undefined),
  }
}

function buildStudioGoldenRecipeEvidence(
  source?: StudioGenerationEvidenceSource | null,
): StudioGoldenRecipeEvidenceSummary {
  const sourceRecord = readRecord(source)
  const manifest = readRecord(sourceRecord?.manifest)
  const pack = readRecord(source?.goldenRecipePack) ?? readRecord(manifest?.goldenRecipePack)
  const recipes = Array.isArray(pack?.recipes)
    ? pack.recipes.flatMap((item) => readRecord(item) ? [readRecord(item)!] : [])
    : []
  if (!pack || recipes.length === 0) {
    return {
      schemaVersion: 'studio-golden-recipe-evidence.v1',
      status: 'missing',
      recipeIds: [],
      scenarioIds: [],
      designSystemGates: [],
      actionFlowContracts: [],
      browserProof: [],
    }
  }

  const scenarioIds: StudioEndUserScenarioId[] = recipes.flatMap((recipe): StudioEndUserScenarioId[] => {
    const scenarioId = recipe.scenarioId
    return scenarioId === 'shopping' ||
      scenarioId === 'complex-mobile' ||
      scenarioId === 'backoffice-data-table'
      ? [scenarioId]
      : []
  })

  return {
    schemaVersion: 'studio-golden-recipe-evidence.v1',
    status: 'available',
    recipeIds: normalizeStringArray(readArray(pack.recipeIds) ?? undefined),
    scenarioIds: [...new Set(scenarioIds)],
    designSystemGates: recipes.flatMap((recipe) => normalizeStringArray(readArray(recipe.designQualityGates) ?? undefined)),
    actionFlowContracts: recipes.flatMap((recipe) => normalizeStringArray(readArray(recipe.actionFlowContracts) ?? undefined)),
    browserProof: recipes.flatMap((recipe) => normalizeStringArray(readArray(recipe.browserProof) ?? undefined)),
  }
}

interface AuditProvenanceOptions {
  /** Treat even the top-level source fields as app-document (used when the
   * evidence source IS a generated app/block document, e.g. the
   * attach-to-app path). */
  documentOnly?: boolean
}

const AUDIT_PROVENANCE_FINDING = {
  code: 'audit-provenance-unverified',
  severity: 'partial' as const,
  message:
    'Audit was carried by the generated app document (a model-writable channel); ' +
    'a studio-attached audit is required before it can count as pass.',
  sample: [],
}

/**
 * Stamp the resolved provenance onto an audit summary — overriding any
 * provenance the document itself claimed — and cap app-document audits at
 * `partial`: a shape-valid self-claimed `pass` riding the generated
 * document must never satisfy the gates.
 */
function applyAuditProvenance<
  T extends {
    status: StudioFullCoverageAuditStatus
    findings: Array<{ code: string; severity: Exclude<StudioFullCoverageAuditStatus, 'missing'>; message: string; sample: string[] }>
    partialFindingCount?: number
  },
>(summary: T, provenance: StudioEvidenceAuditProvenance): T {
  if (provenance === 'studio-input') {
    return { ...summary, provenance }
  }
  const capped = summary.status === 'pass'
  return {
    ...summary,
    provenance,
    status: capped ? ('partial' as StudioFullCoverageAuditStatus) : summary.status,
    findings: capped ? [...summary.findings, AUDIT_PROVENANCE_FINDING] : summary.findings,
    ...(capped && typeof summary.partialFindingCount === 'number'
      ? { partialFindingCount: summary.partialFindingCount + 1 }
      : {}),
  }
}

function buildStudioFullCoverageEvidence(
  source?: StudioGenerationEvidenceSource | null,
  options?: AuditProvenanceOptions,
): StudioFullCoverageEvidenceSummary {
  const sourceRecord = readRecord(source)
  const manifest = readRecord(sourceRecord?.manifest)
  const metadata = readRecord(sourceRecord?.metadata) ?? readRecord(sourceRecord?.meta)
  const topLevelAudit =
    readRecord(source?.fullCoverageAudit) ??
    readRecord(sourceRecord?.studioFullCoverageAudit) ??
    readRecord(sourceRecord?.fullCoverageAudit)
  const documentAudit =
    readRecord(metadata?.studioFullCoverageAudit) ??
    readRecord(metadata?.fullCoverageAudit) ??
    readRecord(manifest?.studioFullCoverageAudit) ??
    readRecord(manifest?.fullCoverageAudit)
  const audit = topLevelAudit ?? documentAudit
  const provenance: StudioEvidenceAuditProvenance =
    topLevelAudit && !options?.documentOnly ? 'studio-input' : 'app-document'

  if (!audit) {
    return {
      schemaVersion: 'studio-full-coverage-evidence.v1',
      status: 'missing',
      componentOntologyCount: 0,
      actionOntologyCount: 0,
      rendererFullyCoveredCount: 0,
      componentCapabilityCatalogCount: 0,
      componentCapabilityDenominator: 0,
      componentCapabilityPlatformCoverage: {
        webSupported: 0,
        rnSupported: 0,
        flutterSupported: 0,
        renderPackComponents: 0,
      },
      componentCapabilityHighRiskComponents: [],
      bundledStyleCount: 0,
      flatbufferMappedComponentCount: 0,
      blockingFindingCount: 1,
      sectionStatuses: {
        uiElements: 'missing',
        renderers: 'missing',
        componentCapabilities: 'missing',
        styleSeparation: 'missing',
        actions: 'missing',
        flatbuffer: 'missing',
      },
      findings: [{
        code: 'full-coverage-audit-missing',
        severity: 'blocked',
        message: 'Full component/action/style/FlatBuffer coverage audit is missing.',
        sample: [],
      }],
      sources: ['packages/cogo-headless/src/studio-full-coverage-audit.ts'],
    }
  }
  if (isStudioFullCoverageEvidenceSummary(audit)) return applyAuditProvenance(audit, provenance)

  const summary = readRecord(audit.summary)
  const sections = readRecord(audit.sections)
  const componentCapabilitiesSection = readRecord(sections?.componentCapabilities)
  const componentCapabilitiesEvidence = readRecord(componentCapabilitiesSection?.evidence)
  const componentCapabilityDenominator = readNumber(
    componentCapabilitiesEvidence?.denominator,
    readNumber(componentCapabilitiesSection?.total, readNumber(summary?.componentOntologyCount)),
  )
  const componentCapabilityCatalogCount = readNumber(
    summary?.componentCapabilityCatalogCount,
    readNumber(componentCapabilitiesSection?.covered, readNumber(componentCapabilitiesEvidence?.componentCount)),
  )
  const componentCapabilityHighRiskComponents = Object.keys(
    readRecord(componentCapabilitiesEvidence?.highRiskComponents) ?? {},
  ).sort()
  const componentCapabilityFindings: StudioFullCoverageEvidenceSummary['findings'] = []
  if (!componentCapabilitiesSection) {
    componentCapabilityFindings.push({
      code: 'component-capability-catalog-missing',
      severity: 'blocked',
      message: 'Component capability catalog section is missing from the full coverage audit.',
      sample: ['sections.componentCapabilities'],
    })
  }
  if (
    componentCapabilityDenominator > 0 &&
    componentCapabilityCatalogCount !== componentCapabilityDenominator
  ) {
    componentCapabilityFindings.push({
      code: 'component-capability-catalog-denominator-drift',
      severity: 'blocked',
      message:
        `Component capability catalog covers ${componentCapabilityCatalogCount}/${componentCapabilityDenominator}; ` +
        'generation evidence requires a capability contract for every ontology component.',
      sample: ['packages/cogo-validator/ontology/component-capabilities.json'],
    })
  }
  const auditFindings = Object.values(sections ?? {})
    .flatMap((section) => readArray(readRecord(section)?.findings) ?? [])
    .flatMap((finding) => {
      const record = readRecord(finding)
      const severity = normalizeFullCoverageStatus(record?.severity)
      if (!record || severity === 'missing') return []
      return [{
        code: readString(record.code, 'full-coverage-finding'),
        severity,
        message: readString(record.message, 'Full coverage audit finding.'),
        sample: normalizeStringArray(readArray(record.sample) ?? undefined).slice(0, 12),
      }]
    })
  const findings = [...auditFindings, ...componentCapabilityFindings]
  const auditStatus = normalizeFullCoverageStatus(audit.status)
  // Fail-closed: the audit's own blocked findings (harvested from every
  // section) must escalate the summary status — a top-level `status: 'pass'`
  // self-claim may never override them. Previously only the locally computed
  // componentCapabilityFindings escalated.
  const computedBlockingFindingCount = findings.filter((finding) => finding.severity === 'blocked').length
  const status = computedBlockingFindingCount > 0 ? 'blocked' : auditStatus

  return applyAuditProvenance({
    schemaVersion: 'studio-full-coverage-evidence.v1',
    status,
    componentOntologyCount: readNumber(summary?.componentOntologyCount),
    actionOntologyCount: readNumber(summary?.actionOntologyCount),
    rendererFullyCoveredCount: readNumber(summary?.rendererFullyCoveredCount),
    componentCapabilityCatalogCount,
    componentCapabilityDenominator,
    componentCapabilityPlatformCoverage: {
      webSupported: readNumber(componentCapabilitiesEvidence?.webSupported),
      rnSupported: readNumber(componentCapabilitiesEvidence?.rnSupported),
      flutterSupported: readNumber(componentCapabilitiesEvidence?.flutterSupported),
      renderPackComponents: readNumber(componentCapabilitiesEvidence?.renderPackComponents),
    },
    componentCapabilityHighRiskComponents,
    bundledStyleCount: readNumber(summary?.bundledStyleCount),
    flatbufferMappedComponentCount: readNumber(summary?.flatbufferMappedComponentCount),
    // worst-of(self-reported, computed) — an audit reporting 0 may not hide
    // the blocked findings it ships alongside.
    blockingFindingCount: Math.max(readNumber(summary?.blockingFindingCount), computedBlockingFindingCount),
    sectionStatuses: {
      uiElements: normalizeFullCoverageStatus(readRecord(sections?.uiElements)?.status),
      renderers: normalizeFullCoverageStatus(readRecord(sections?.renderers)?.status),
      componentCapabilities: normalizeFullCoverageStatus(componentCapabilitiesSection?.status),
      styleSeparation: normalizeFullCoverageStatus(readRecord(sections?.styleSeparation)?.status),
      actions: normalizeFullCoverageStatus(readRecord(sections?.actions)?.status),
      flatbuffer: normalizeFullCoverageStatus(readRecord(sections?.flatbuffer)?.status),
    },
    findings,
    sources: [
      'packages/cogo-headless/src/studio-full-coverage-audit.ts',
      'packages/cogo-validator/ontology/element-types.json',
      'packages/cogo-validator/ontology/action-types.json',
      'packages/cogo-validator/ontology/component-capabilities.json',
      'scripts/render-engine-registry.json',
      'packages/cogo-runtime-v2/src/render-pack.ts',
    ],
  }, provenance)
}

function buildStudioExhaustiveProofEvidence(
  source?: StudioGenerationEvidenceSource | null,
  options?: AuditProvenanceOptions,
): StudioExhaustiveProofEvidenceSummary {
  const sourceRecord = readRecord(source)
  const manifest = readRecord(sourceRecord?.manifest)
  const metadata = readRecord(sourceRecord?.metadata) ?? readRecord(sourceRecord?.meta)
  const topLevelAudit =
    readRecord(source?.exhaustiveProofAudit) ??
    readRecord(sourceRecord?.studioExhaustiveProofAudit) ??
    readRecord(sourceRecord?.exhaustiveProofAudit)
  const documentAudit =
    readRecord(metadata?.studioExhaustiveProofAudit) ??
    readRecord(metadata?.exhaustiveProofAudit) ??
    readRecord(manifest?.studioExhaustiveProofAudit) ??
    readRecord(manifest?.exhaustiveProofAudit)
  const audit = topLevelAudit ?? documentAudit
  const provenance: StudioEvidenceAuditProvenance =
    topLevelAudit && !options?.documentOnly ? 'studio-input' : 'app-document'

  if (!audit) {
    return {
      schemaVersion: 'studio-exhaustive-proof-evidence.v1',
      status: 'missing',
      ontologyComponentCount: 0,
      styleCount: 0,
      contractProbeCount: 0,
      renderPackProbeCount: 0,
      ssrRenderedComponentCount: 0,
      soloSsrRenderedComponentCount: 0,
      compoundSsrRenderedComponentCount: 0,
      ssrMissingComponentCount: 0,
      providerSlotHydrationProofStatus: 'missing',
      providerSlotHydratedComponentCount: 0,
      providerSlotHydrationRequiredComponentCount: 0,
      runtimeHonoredActionCount: 0,
      runtimeExecutedActionCount: 0,
      runtimeAdapterProvenActionCount: 0,
      runtimeProvenActionCount: 0,
      runtimeAdapterRequiredActionCount: 0,
      runtimeNotExecutedActionCount: 0,
      collapsedStyleSelectors: [],
      blockingFindingCount: 1,
      partialFindingCount: 0,
      findings: [{
        code: 'exhaustive-proof-audit-missing',
        severity: 'blocked',
        message: 'Exhaustive component SSR/style/runtime action execution proof audit is missing.',
        sample: [],
      }],
      sources: ['packages/cogo-headless/src/studio-exhaustive-proof-audit.ts'],
    }
  }
  if (isStudioExhaustiveProofEvidenceSummary(audit)) return applyAuditProvenance(audit, provenance)

  const components = readRecord(audit.components)
  const styles = readRecord(audit.styles)
  const actions = readRecord(audit.actions)
  const summary = readRecord(audit.summary)
  const providerSlotHydrationProof = readRecord(components?.providerSlotHydrationProof)
  const adapterActionProof = readRecord(actions?.adapterActionProof)
  const findings = [
    ...normalizeProofFindings(readArray(components?.findings)),
    ...normalizeProofFindings(readArray(styles?.findings)),
    ...normalizeProofFindings(readArray(actions?.findings)),
  ]
  // Fail-closed: same contract as the full-coverage builder — blocked
  // findings escalate the status regardless of the audit's self-claim.
  const computedBlockingFindingCount = findings.filter((finding) => finding.severity === 'blocked').length

  return applyAuditProvenance({
    schemaVersion: 'studio-exhaustive-proof-evidence.v1',
    status: computedBlockingFindingCount > 0 ? 'blocked' : normalizeFullCoverageStatus(audit.status),
    ontologyComponentCount: readNumber(summary?.ontologyComponentCount, readNumber(components?.ontologyComponentCount)),
    styleCount: readNumber(summary?.styleCount, readNumber(components?.styleCount)),
    contractProbeCount: readNumber(summary?.contractProbeCount, readNumber(components?.contractProbeCount)),
    renderPackProbeCount: readNumber(components?.renderPackProbeCount),
    ssrRenderedComponentCount: readNumber(summary?.ssrRenderedComponentCount, readNumber(components?.ssrRenderedCount)),
    soloSsrRenderedComponentCount: readNumber(components?.soloSsrRenderedCount),
    compoundSsrRenderedComponentCount: readNumber(components?.compoundSsrRenderedCount),
    ssrMissingComponentCount: readNumber(components?.ssrMissingCount),
    providerSlotHydrationProofStatus: normalizeFullCoverageStatus(providerSlotHydrationProof?.status),
    providerSlotHydratedComponentCount: normalizeStringArray(readArray(components?.providerSlotHydratedComponents) ?? undefined).length,
    providerSlotHydrationRequiredComponentCount: normalizeStringArray(readArray(components?.providerSlotHydrationRequiredComponents) ?? undefined).length,
    runtimeHonoredActionCount: readNumber(summary?.runtimeHonoredActionCount, readNumber(actions?.runtimeHonoredActionCount)),
    runtimeExecutedActionCount: readNumber(summary?.runtimeExecutedActionCount, readNumber(actions?.executedCount)),
    runtimeAdapterProvenActionCount: readNumber(summary?.runtimeAdapterProvenActionCount, readNumber(actions?.adapterProvenCount)),
    runtimeProvenActionCount: readNumber(
      summary?.runtimeProvenActionCount,
      readNumber(actions?.executedCount) + readNumber(actions?.adapterProvenCount),
    ),
    runtimeAdapterRequiredActionCount: readNumber(actions?.adapterRequiredCount),
    runtimeNotExecutedActionCount: readNumber(actions?.notExecutedCount),
    collapsedStyleSelectors: normalizeStringArray(readArray(styles?.collapsedSelectors) ?? undefined),
    blockingFindingCount: Math.max(readNumber(summary?.blockingFindingCount), computedBlockingFindingCount),
    partialFindingCount: Math.max(
      readNumber(summary?.partialFindingCount),
      findings.filter((finding) => finding.severity === 'partial').length,
    ),
    findings,
    sources: [
      'packages/cogo-headless/src/studio-exhaustive-proof-audit.ts',
      'packages/cogo-headless/src/verify/component-render-fidelity.ts',
      'packages/cogo-react-renderer/src/scripts/run-provider-slot-hydration-proof.tsx',
      'packages/cogo-headless/src/scripts/run-studio-adapter-action-proof.ts',
      'packages/cogo-runtime-v2/src/action-flow-engine.ts',
      ...normalizeStringArray(readArray(adapterActionProof?.sources) ?? undefined),
    ],
  }, provenance)
}

function normalizeProofFindings(findings?: readonly unknown[] | null): StudioExhaustiveProofEvidenceSummary['findings'] {
  return (findings ?? []).flatMap((finding) => {
    const record = readRecord(finding)
    const severity = normalizeFullCoverageStatus(record?.severity)
    if (!record || severity === 'missing') return []
    return [{
      code: readString(record.code, 'exhaustive-proof-finding'),
      severity,
      message: readString(record.message, 'Exhaustive proof audit finding.'),
      sample: normalizeStringArray(readArray(record.sample) ?? undefined).slice(0, 12),
    }]
  })
}

function buildStudioNativeTargetProofEvidence(
  source?: StudioGenerationEvidenceSource | null,
  projectPlan?: StudioProjectPlanEvidenceSource | null,
  options?: AuditProvenanceOptions,
): StudioNativeTargetProofSummary {
  const sourceRecord = readRecord(source)
  const manifest = readRecord(sourceRecord?.manifest)
  const metadata = readRecord(sourceRecord?.metadata) ?? readRecord(sourceRecord?.meta)
  const topLevelProof =
    readRecord(source?.nativeTargetProof) ??
    readRecord(sourceRecord?.studioNativeTargetProof) ??
    readRecord(sourceRecord?.nativeTargetProof) ??
    readRecord(sourceRecord?.nativeRendererProof)
  const documentProof =
    readRecord(metadata?.studioNativeTargetProof) ??
    readRecord(metadata?.nativeTargetProof) ??
    readRecord(metadata?.nativeRendererProof) ??
    readRecord(manifest?.studioNativeTargetProof) ??
    readRecord(manifest?.nativeTargetProof) ??
    readRecord(manifest?.nativeRendererProof)
  const planProof = readRecord(projectPlan?.nativeTargetProof)
  const proof = topLevelProof ?? documentProof ?? planProof
  // Trust contract (#942): top-level evidence-source fields are the
  // studio-input channel UNLESS the whole source is a generated document
  // (documentOnly). A summary passed through the project plan keeps the
  // provenance it already carries (it is prior studio evidence).
  const provenance: StudioEvidenceAuditProvenance =
    proof && proof === planProof
      ? (proof.provenance === 'app-document' ? 'app-document' : 'studio-input')
      : proof && proof === topLevelProof && !options?.documentOnly
        ? 'studio-input'
        : 'app-document'

  if (!proof) {
    return {
      schemaVersion: 'studio-native-target-proof.v1',
      status: 'missing',
      targets: {
        rn: missingNativeTargetProofEntry(),
        flutter: missingNativeTargetProofEntry(),
      },
      sources: ['apps/cogo-studio/src/features/validation/studio-generation-evidence.ts'],
    }
  }
  const targets = readRecord(proof.targets) ?? proof
  const rn = normalizeNativeTargetProofEntry(
    readRecord(targets.rn) ??
      readRecord(targets.reactNative) ??
      readRecord(targets.mobile),
  )
  const flutter = normalizeNativeTargetProofEntry(readRecord(targets.flutter))
  const targetStatuses = [rn.status, flutter.status]
  const derivedStatus: StudioNativeTargetProofStatus =
    targetStatuses.every((status) => status === 'missing')
      ? 'missing'
      : targetStatuses.some((status) => status === 'blocked')
        ? 'blocked'
        : targetStatuses.some((status) => status === 'partial' || status === 'missing')
          ? 'partial'
          : 'pass'
  const explicitStatus = normalizeNativeTargetProofStatus(proof.status)
  const combinedStatus = combineNativeTargetProofStatus(explicitStatus, derivedStatus)
  // App-document proofs may inform the panels but never mint a pass.
  const capped = provenance === 'app-document' && combinedStatus === 'pass'
  const provenanceReason =
    'Native target proof was carried by the generated app document (a model-writable channel); ' +
    'a studio-attached proof is required before it can count as pass.'
  const withProvenanceReason = (entry: StudioNativeTargetProofEntry): StudioNativeTargetProofEntry =>
    entry.status === 'missing' ? entry : { ...entry, reasons: [...entry.reasons, provenanceReason] }

  return {
    schemaVersion: 'studio-native-target-proof.v1',
    status: capped ? 'partial' : combinedStatus,
    provenance,
    targets: capped
      ? { rn: withProvenanceReason(rn), flutter: withProvenanceReason(flutter) }
      : { rn, flutter },
    sources: [
      'apps/cogo-studio/src/features/validation/studio-generation-evidence.ts',
      ...normalizeStringArray(readArray(proof.sources) ?? undefined),
    ],
  }
}

function missingNativeTargetProofEntry(): StudioNativeTargetProofEntry {
  return {
    status: 'missing',
    nativeRuntimeParity: 'pending',
    nativeLivePackaging: 'pending',
    r2VisualFaithfulness: 'pending',
    evidence: [],
    reasons: ['Native target proof is missing.'],
  }
}

function normalizeNativeTargetProofEntry(
  record: Record<string, unknown> | null,
): StudioNativeTargetProofEntry {
  if (!record) return missingNativeTargetProofEntry()

  const nativeRuntimeParity = normalizeRendererAxisStatus(
    record.nativeRuntimeParity ?? record.runtimeParity ?? record.runtime,
    'pending',
  )
  const nativeLivePackaging = normalizeRendererAxisStatus(
    record.nativeLivePackaging ?? record.livePackaging ?? record.appLoaderPackaging,
    'pending',
  )
  const r2VisualFaithfulness = normalizeRendererAxisStatus(
    record.r2VisualFaithfulness ?? record.visualFaithfulness ?? record.r2,
    'pending',
  )
  const explicitStatus = normalizeNativeTargetProofStatus(record.status)
  const derivedStatus: StudioNativeTargetProofStatus =
    explicitStatus === 'blocked'
      ? 'blocked'
      : nativeRuntimeParity === 'pass' && nativeLivePackaging === 'pass' && r2VisualFaithfulness === 'pass'
        ? 'pass'
        : 'partial'
  const reasons = normalizeStringArray(readArray(record.reasons) ?? undefined)

  return {
    status: combineNativeTargetProofStatus(explicitStatus, derivedStatus),
    nativeRuntimeParity,
    nativeLivePackaging,
    r2VisualFaithfulness,
    evidence: normalizeStringArray(readArray(record.evidence) ?? undefined),
    reasons: reasons.length > 0
      ? reasons
      : explicitStatus === 'blocked'
        ? ['Native target proof was blocked.']
        : [],
  }
}

function combineNativeTargetProofStatus(
  explicitStatus: StudioNativeTargetProofStatus,
  derivedStatus: StudioNativeTargetProofStatus,
): StudioNativeTargetProofStatus {
  if (explicitStatus === 'blocked' || derivedStatus === 'blocked') return 'blocked'
  if (derivedStatus === 'missing') return explicitStatus === 'missing' ? 'missing' : 'partial'
  if (derivedStatus === 'partial') return 'partial'
  return explicitStatus === 'partial' ? 'partial' : 'pass'
}

function normalizeRendererAxisStatus(
  value: unknown,
  fallback: StudioRendererAxisStatus,
): StudioRendererAxisStatus {
  return value === 'pass' || value === 'partial' || value === 'pending' || value === 'not-applicable'
    ? value
    : fallback
}

function normalizeNativeTargetProofStatus(value: unknown): StudioNativeTargetProofStatus {
  if (value === 'pass' || value === 'partial' || value === 'blocked') return value
  return 'missing'
}

export function buildStudioGenerationEvidence(
  source?: StudioGenerationEvidenceSource | null,
  projectPlan?: StudioProjectPlanEvidenceSource | null,
): StudioGenerationEvidence {
  const runtimeTargets = normalizeStudioRuntimeTargets(source, projectPlan)
  const targetPlatform = generationTargetPlatformForEvidence(source, projectPlan)
  const preflight = buildStudioUiOntologyPreflight(source, projectPlan, targetPlatform)
  const adaptiveRuntime = buildStudioAdaptiveRuntimeEvidence(source, projectPlan)
  const goldenRecipes = buildStudioGoldenRecipeEvidence(source)
  const fullCoverage = buildStudioFullCoverageEvidence(source)
  const exhaustiveProof = buildStudioExhaustiveProofEvidence(source)
  const nativeTargetProof = buildStudioNativeTargetProofEvidence(source, projectPlan)
  const generatedApp = buildGeneratedAppReadiness(source, projectPlan, 'pending')

  return {
    schemaVersion: 'studio-generation-evidence.v1',
    uiOntology: {
      status: !preflight.canGenerate
        ? 'blocked'
        : preflight.mode === 'llm-fallback'
          ? 'fallback'
          : 'pass',
      mode: preflight.mode,
      targetPlatform,
      runtimeTargets,
      adaptiveRuntime,
      goldenRecipes,
      rendererBlankTypes: 0,
      preflight,
      sources: [
        'packages/cogo-kg-browser/fixtures/ui-renderability-v1.ttl',
        'packages/cogo-kg-browser/fixtures/schema-v4-shapes-v1.ttl',
      ],
    },
    renderEngine: { ...STUDIO_RENDER_ENGINE_REGISTRY_SUMMARY },
    actionflow: { ...STUDIO_ACTIONFLOW_CONTRACT_SUMMARY },
    fullCoverage,
    exhaustiveProof,
    nativeTargetProof,
    applicationConformanceOracle: generatedApp.applicationConformance,
    generatedApp,
    generatedAt: new Date().toISOString(),
  }
}

export function attachStudioGenerationEvidenceToAppSpec<T extends StudioGenerationEvidenceSource>(
  appSpec: T,
  evidence = buildStudioGenerationEvidence(appSpec),
): T & {
  generationTargetPlatform: StudioGenerationTargetPlatform
  runtimeTargets: StudioRuntimeTargetEvidence[]
  generationEvidence: StudioGenerationEvidence
  uiOntologyGenerationConstraints: StudioUiOntologyGenerationConstraintsPayload
} {
  return {
    ...appSpec,
    generationTargetPlatform: evidence.uiOntology.targetPlatform,
    runtimeTargets: evidence.uiOntology.runtimeTargets,
    generationEvidence: evidence,
    uiOntologyGenerationConstraints: buildStudioUiOntologyGenerationConstraints(evidence),
  }
}

export function buildStudioUiOntologyGenerationConstraints(
  evidence: StudioGenerationEvidence,
): StudioUiOntologyGenerationConstraintsPayload {
  return {
    mode: evidence.uiOntology.preflight.mode,
    canGenerate: evidence.uiOntology.preflight.canGenerate,
    ragCandidates: evidence.uiOntology.preflight.ragCandidates,
    bindPlan: evidence.uiOntology.preflight.bindPlan,
    negativeConstraints: evidence.uiOntology.preflight.negativeConstraints,
    generationRetrievalRouting: evidence.uiOntology.preflight.generationRetrievalRouting,
    fallbackCandidateFacts: evidence.uiOntology.preflight.fallbackCandidateFacts,
    safeDesignFacts: evidence.uiOntology.preflight.safeDesignFacts,
    promptGuardrails: evidence.uiOntology.preflight.promptGuardrails,
    blockedReasons: evidence.uiOntology.preflight.blockedReasons,
    rendererBlankTypes: evidence.uiOntology.rendererBlankTypes,
    targetPlatform: evidence.uiOntology.targetPlatform,
    adaptiveRuntime: evidence.uiOntology.adaptiveRuntime,
    goldenRecipes: evidence.uiOntology.goldenRecipes,
    styleSeparationContract: STYLE_SEPARATION_GENERATOR_CONTRACT,
    reportApplicationShellContract: REPORT_APPLICATION_SHELL_CONTRACT,
    generatedUiVerificationPolicy: buildStudioGeneratedUiVerificationPolicyConstraints(evidence),
  }
}

function buildStudioGeneratedUiVerificationPolicyConstraints(
  evidence: StudioGenerationEvidence,
): StudioGeneratedUiVerificationPolicyConstraints {
  return {
    schemaVersion: 'studio-generated-ui-verification-policy-constraints.v1',
    featureFlag: 'COGO_IMAGE_EVIDENCE_VERIFICATION',
    enabled: evidence.generatedApp.imageEvidenceVerification.enabled,
    canonicalProvenanceTiers: [...CANONICAL_GENERATED_UI_PROVENANCE_TIERS],
    requiredWhen: [
      'imageSourceEvidence is present',
      'generatedUiVerificationSurface is present',
      'imageEvidenceVerificationEnabled is true',
    ],
    publishRule: 'block-when-summary-blocked-or-promotion-disallowed',
  }
}

export function shouldUseOntologyDrivenDeterministicGeneration(
  evidence: StudioGenerationEvidence,
): boolean {
  const preflight = evidence.uiOntology.preflight
  return Boolean(
    preflight.canGenerate &&
      preflight.mode === 'ontology-driven' &&
      preflight.deterministicCandidateCount > 0,
  )
}

export function mergeStudioGenerationEvidenceMeta(
  meta: unknown,
  evidence: StudioGenerationEvidence,
): Record<string, unknown> {
  const record = readRecord(meta)
  const panel = buildUiConformancePanelFromStudioGenerationEvidence(evidence)
  const existingPanel = firstPanel(record?.uiOntologyConformance, record?.uiConformancePanel)
  const mergedPanel = existingPanel ? mergeEvidencePanel(existingPanel, panel) : panel

  return {
    ...(record ?? {}),
    generationEvidence: evidence,
    renderEngineRegistrySummary: evidence.renderEngine,
    actionflowContractSummary: evidence.actionflow,
    fullCoverageAudit: evidence.fullCoverage,
    exhaustiveProofAudit: evidence.exhaustiveProof,
    nativeTargetProof: evidence.nativeTargetProof,
    applicationConformanceOracle: evidence.applicationConformanceOracle,
    visibleFromJsonOracle: evidence.generatedApp.visibleFromJson,
    imageEvidenceVerification: evidence.generatedApp.imageEvidenceVerification,
    ...(evidence.generatedApp.imageEvidenceCandidateAdmission
      ? { imageEvidenceCandidateAdmission: evidence.generatedApp.imageEvidenceCandidateAdmission }
      : {}),
    ...(evidence.productionEvidenceChain
      ? { productionEvidenceChain: evidence.productionEvidenceChain }
      : {}),
    generatedAppReadinessGate: buildStudioGeneratedAppReadinessGate(evidence),
    rendererTargetMatrix: buildStudioRendererTargetMatrix(evidence),
    adaptiveRuntimePlan: evidence.uiOntology.adaptiveRuntime,
    goldenRecipeEvidence: evidence.uiOntology.goldenRecipes,
    uiOntologyGenerationConstraints: buildStudioUiOntologyGenerationConstraints(evidence),
    uiOntologyConformance: mergedPanel,
  }
}

export function withStudioEndUserAppGenerationProof(
  evidence: StudioGenerationEvidence,
  proof: StudioEndUserAppGenerationProof,
): StudioGenerationEvidence {
  return {
    ...evidence,
    endUserAppProof: proof,
  }
}

export function readStudioProductionGenerationEvidenceChain(
  value: unknown,
): StudioProductionGenerationEvidenceChain | null {
  const record = readRecord(value)
  if (!record || record.schemaVersion !== 'cogo.production-generation-evidence-chain.v1') return null
  const status = record.status
  if (status !== 'pass' && status !== 'partial' && status !== 'blocked') return null

  const sourceEvidenceRefs = readArray(record.sourceEvidenceRefs)
    ?.filter((item): item is string => typeof item === 'string' && item.length > 0) ?? []
  const validationEvidenceRefs = readArray(record.validationEvidenceRefs)
    ?.filter((item): item is string => typeof item === 'string' && item.length > 0) ?? []
  const provenance = readRecord(record.provenance)

  return {
    schemaVersion: 'cogo.production-generation-evidence-chain.v1',
    status,
    sourceEvidenceRefs,
    validationEvidenceRefs,
    selectedEvidenceCount: readNumber(record.selectedEvidenceCount, sourceEvidenceRefs.length),
    evidenceBackedBlockCount: readNumber(record.evidenceBackedBlockCount),
    totalBlockCount: readNumber(record.totalBlockCount),
    llmOnly: record.llmOnly === true,
    fallbackHidden: record.fallbackHidden === true,
    ...(typeof record.deterministic === 'boolean' ? { deterministic: record.deterministic } : {}),
    ...(typeof record.generatedAt === 'string' ? { generatedAt: record.generatedAt } : {}),
    ...(provenance ? { provenance } : {}),
  }
}

export function withStudioProductionGenerationEvidenceChain(
  evidence: StudioGenerationEvidence,
  productionEvidenceChain: StudioProductionGenerationEvidenceChain | null | undefined,
): StudioGenerationEvidence {
  if (!productionEvidenceChain) return evidence
  return {
    ...evidence,
    productionEvidenceChain,
  }
}

export function readStudioApplicationConformanceOracle(
  value: unknown,
): StudioApplicationConformanceOracleSummary | null {
  const record = readRecord(value)
  if (!record) return null
  const schemaVersion = record.schemaVersion
  if (
    schemaVersion !== 'cogo.application-conformance-oracle.v1' &&
    schemaVersion !== 'studio-application-conformance-oracle.v1'
  ) {
    return null
  }

  const status = normalizeStudioApplicationConformanceStatus(record.status)
  if (status === 'missing') return null
  const dimensions = normalizeStudioApplicationConformanceDimensions(readArray(record.dimensions))
  const blockedReasons = normalizeStringArray(readArray(record.blockedReasons) ?? undefined)
  const checkedBlockNames = normalizeStringArray(readArray(record.checkedBlockNames) ?? undefined)
  const derivedBlockedReasons = status === 'blocked'
    ? dimensions.flatMap((dimension) =>
        dimension.firstDivergenceReason ? [dimension.firstDivergenceReason] : []
      )
    : []

  return {
    schemaVersion: 'studio-application-conformance-oracle.v1',
    ...(schemaVersion === 'cogo.application-conformance-oracle.v1'
      ? { sourceSchemaVersion: schemaVersion }
      : {}),
    status,
    required: record.required === true || status === 'blocked',
    blockCount: readNumber(record.blockCount, checkedBlockNames.length),
    checkedBlockNames,
    dimensions,
    blockedReasons: blockedReasons.length > 0 ? blockedReasons : derivedBlockedReasons,
    notes: status === 'pass'
      ? ['ApplicationCompositionPlan conformance oracle passed.']
      : (blockedReasons.length > 0 ? blockedReasons : derivedBlockedReasons).map((reason) =>
          `ApplicationCompositionPlan conformance oracle blocked: ${reason}`
        ),
  }
}

export function readStudioVisibleFromJsonOracle(
  value: unknown,
): StudioVisibleFromJsonOracleSummary | null {
  const record = readRecord(value)
  if (!record) return null
  const schemaVersion = record.schemaVersion
  if (
    schemaVersion !== VISIBLE_FROM_JSON_ORACLE_SCHEMA_VERSION &&
    schemaVersion !== 'studio-visible-from-json-oracle.v1'
  ) {
    return null
  }
  const status = normalizeStudioVisibleFromJsonStatus(record.status)
  if (status === 'missing') return null
  const blockedReasons = normalizeStringArray(readArray(record.blockedReasons) ?? undefined)
  const checkedSlots = normalizeVisibleFromJsonCheckedSlots(readArray(record.checkedSlots))
  const derivedReasons = status === 'blocked'
    ? checkedSlots.flatMap((slot) => slot.firstReason ? [slot.firstReason] : [])
    : []
  const reasons = blockedReasons.length > 0 ? blockedReasons : derivedReasons

  return {
    schemaVersion: 'studio-visible-from-json-oracle.v1',
    ...(schemaVersion === VISIBLE_FROM_JSON_ORACLE_SCHEMA_VERSION
      ? { sourceSchemaVersion: schemaVersion }
      : {}),
    status,
    required: record.required === true || status === 'blocked',
    checkedSurface: record.checkedSurface === 'cogo-json-render-tree'
      ? 'cogo-json-render-tree'
      : 'missing',
    declaredSlotCount: readNumber(record.declaredSlotCount),
    visibleSlotCount: readNumber(record.visibleSlotCount),
    hiddenSlotCount: readNumber(record.hiddenSlotCount),
    missingSlotCount: readNumber(record.missingSlotCount),
    rootRasterOnly: record.rootRasterOnly === true,
    rasterAuthorityDetected: record.rasterAuthorityDetected === true,
    blockedReasons: reasons,
    checkedSlots,
    notes: status === 'pass'
      ? ['Visible-from-JSON oracle passed.']
      : reasons.map((reason) => `Visible-from-JSON oracle blocked: ${reason}`),
  }
}

export function withStudioApplicationConformanceOracle(
  evidence: StudioGenerationEvidence,
  applicationConformanceOracle: StudioApplicationConformanceOracleSummary | null | undefined,
): StudioGenerationEvidence {
  if (!applicationConformanceOracle) return evidence
  return {
    ...evidence,
    applicationConformanceOracle,
    generatedApp: {
      ...evidence.generatedApp,
      applicationConformance: applicationConformanceOracle,
    },
  }
}

export function withStudioVisibleFromJsonOracle(
  evidence: StudioGenerationEvidence,
  visibleFromJsonOracle: StudioVisibleFromJsonOracleSummary | null | undefined,
): StudioGenerationEvidence {
  if (!visibleFromJsonOracle) return evidence
  return {
    ...evidence,
    generatedApp: {
      ...evidence.generatedApp,
      visibleFromJson: visibleFromJsonOracle,
    },
  }
}

function isBrowserDependentQualityReason(reason: string): boolean {
  return (
    /^Browser proof did not render any generated blocks\./.test(reason) ||
    /^Browser proof found \d+ broken image\(s\)\./.test(reason) ||
    /^Browser proof indicates the generated app came from a fixture prompt\./.test(reason) ||
    /^Generated-app design sandbox isolation/.test(reason) ||
    /^Generated-app design style switch/.test(reason) ||
    /^Generated-app visual-richness/.test(reason)
  )
}

function isBrowserUpgradeableDesignQualityReason(reason: string): boolean {
  return (
    /^Rendered design quality/.test(reason) ||
    /^design-quality/.test(reason)
  )
}

function normalizeBrowserDesignQualityEvidence(
  evidence: StudioEndUserDesignQualityBrowserEvidence | undefined,
): Required<Pick<
  StudioEndUserDesignQualityBrowserEvidence,
  | 'status'
  | 'score'
  | 'sourceKind'
  | 'hardeningPassed'
  | 'artifactCount'
  | 'screenshotArtifactCount'
  | 'interactionTraceArtifactCount'
  | 'authorStyleObjectCount'
  | 'unconvertedStyleObjectCount'
  | 'prominentMediaCount'
  | 'distinctSurfaceColorCount'
  | 'semanticVisualTypeCount'
  | 'reasons'
>> & {
  measuredBy?: string
  capturedAt?: string
} | null {
  if (!evidence) return null
  const screenshotArtifactCount = readNumber(evidence.screenshotArtifactCount, readNumber(evidence.artifactCount))
  const artifactCount = readNumber(evidence.artifactCount, screenshotArtifactCount)
  return {
    status: evidence.status ?? 'missing',
    score: readNumber(evidence.score),
    sourceKind: evidence.sourceKind ?? 'missing',
    hardeningPassed: evidence.hardeningPassed === true,
    artifactCount,
    screenshotArtifactCount,
    interactionTraceArtifactCount: readNumber(evidence.interactionTraceArtifactCount),
    authorStyleObjectCount: readNumber(evidence.authorStyleObjectCount),
    unconvertedStyleObjectCount: readNumber(evidence.unconvertedStyleObjectCount),
    prominentMediaCount: readNumber(evidence.prominentMediaCount),
    distinctSurfaceColorCount: readNumber(evidence.distinctSurfaceColorCount),
    semanticVisualTypeCount: readNumber(evidence.semanticVisualTypeCount),
    reasons: normalizeStringArray(evidence.reasons),
    ...(evidence.measuredBy ? { measuredBy: evidence.measuredBy } : {}),
    ...(evidence.capturedAt ? { capturedAt: evidence.capturedAt } : {}),
  }
}

function browserDesignQualityReasons(
  designQuality: NonNullable<ReturnType<typeof normalizeBrowserDesignQualityEvidence>>,
  scoreFloor: number,
  requireArtifacts: boolean,
): string[] {
  return [
    ...(designQuality.status !== 'pass'
      ? [`Rendered browser design quality status is ${designQuality.status}; pass is required.`]
      : []),
    ...(designQuality.sourceKind !== 'measured'
      ? [`Rendered browser design quality evidence is ${designQuality.sourceKind}; measured evidence is required.`]
      : []),
    ...(designQuality.score < scoreFloor
      ? [`Rendered browser design quality score is below floor (${designQuality.score} < ${scoreFloor}).`]
      : []),
    ...(!designQuality.hardeningPassed
      ? ['Rendered browser design quality hardening did not pass.']
      : []),
    ...(designQuality.authorStyleObjectCount > 0
      ? [`Rendered browser design quality contains author style objects (${designQuality.authorStyleObjectCount}); design compiler normalization is required.`]
      : []),
    ...(designQuality.unconvertedStyleObjectCount > 0
      ? [`Rendered browser design quality contains unconverted style objects (${designQuality.unconvertedStyleObjectCount}).`]
      : []),
    ...(requireArtifacts && designQuality.artifactCount <= 0
      ? ['Rendered browser design quality screenshot artifacts are missing; metric-only quality is not sufficient.']
      : []),
    ...designQuality.reasons,
  ]
}

function buildStudioEndUserVisualRichnessProof(
  browserEvidence: StudioEndUserAppBrowserEvidence | null | undefined,
  designQuality: NonNullable<ReturnType<typeof normalizeBrowserDesignQualityEvidence>> | null,
): StudioEndUserVisualRichnessProof {
  const scenarioIds = Array.from(new Set(
    (browserEvidence?.scenarioEvidence ?? [])
      .filter((scenario) => scenario.status === 'pass')
      .map((scenario) => scenario.id),
  ))
  const requiredScenarioCount = scenarioIds.length > 0 ? scenarioIds.length : designQuality ? 1 : 0
  const scenarioCount = designQuality ? requiredScenarioCount : 0
  const minDistinctSurfaceColorCount = designQuality?.distinctSurfaceColorCount ?? 0
  const minSemanticVisualTypeCount = designQuality?.semanticVisualTypeCount ?? 0
  const shoppingRequired = scenarioIds.includes('shopping')
  const shoppingProminentMediaCount = shoppingRequired ? designQuality?.prominentMediaCount ?? 0 : 3
  const reasons = [
    ...(!designQuality ? ['Generated-app visual-richness browser measurements are missing.'] : []),
    ...(requiredScenarioCount <= 0 ? ['Generated-app visual-richness proof has no required scenario denominator.'] : []),
    ...(scenarioCount < requiredScenarioCount
      ? [`Generated-app visual-richness proof covers ${scenarioCount}/${requiredScenarioCount} scenarios.`]
      : []),
    ...(designQuality && minDistinctSurfaceColorCount < 3
      ? [`Generated-app visual-richness has only ${minDistinctSurfaceColorCount} distinct surface colors; at least 3 are required.`]
      : []),
    ...(shoppingRequired && shoppingProminentMediaCount < 3
      ? [`Generated-app shopping visual-richness has only ${shoppingProminentMediaCount} prominent product media items; at least 3 are required.`]
      : []),
    ...(designQuality && minSemanticVisualTypeCount < 8
      ? [`Generated-app visual-richness has only ${minSemanticVisualTypeCount} semantic visual types; at least 8 are required.`]
      : []),
  ]

  return {
    schemaVersion: 'studio-end-user-visual-richness-proof.v1',
    status: !designQuality ? 'missing' : reasons.length === 0 ? 'pass' : 'fail',
    scenarioCount,
    requiredScenarioCount,
    minDistinctSurfaceColorCount,
    shoppingProminentMediaCount,
    minSemanticVisualTypeCount,
    reasons,
  }
}

export function applyStudioEndUserBrowserEvidenceToProof(
  input: StudioEndUserBrowserEvidenceUpgradeInput,
): StudioEndUserAppGenerationProof {
  const designSandbox = buildStudioEndUserDesignSandboxIsolationProof(input.browserEvidence)
  const designStyleSwitch = buildStudioEndUserDesignStyleSwitchProof(input.browserEvidence)
  const browserDesignQuality = normalizeBrowserDesignQualityEvidence(input.browserEvidence.designQuality)
  const visualRichness = buildStudioEndUserVisualRichnessProof(input.browserEvidence, browserDesignQuality)
  const renderedBlockCount = typeof input.browserEvidence.renderedBlockCount === 'number'
    ? input.browserEvidence.renderedBlockCount
    : input.proof.quality.renderedBlockCount
  const screenshotArtifactCount = input.browserEvidence.screenshotArtifacts?.length ?? input.proof.quality.screenshotArtifactCount
  const brokenImageCount = typeof input.browserEvidence.brokenImageCount === 'number'
    ? input.browserEvidence.brokenImageCount
    : input.proof.quality.brokenImageCount
  const nonFixturePrompt = typeof input.browserEvidence.nonFixturePrompt === 'boolean'
    ? input.browserEvidence.nonFixturePrompt
    : input.proof.quality.nonFixturePrompt
  const qualityReasons = [
    ...input.proof.quality.reasons.filter((reason) => (
      !isBrowserDependentQualityReason(reason) &&
      !(browserDesignQuality && isBrowserUpgradeableDesignQualityReason(reason))
    )),
    ...(browserDesignQuality
      ? browserDesignQualityReasons(
          browserDesignQuality,
          input.proof.quality.designQualityScoreFloor,
          true,
        )
      : []),
    ...(renderedBlockCount !== null && renderedBlockCount <= 0
      ? ['Browser proof did not render any generated blocks.']
      : []),
    ...(brokenImageCount !== null && brokenImageCount > 0
      ? [`Browser proof found ${brokenImageCount} broken image(s).`]
      : []),
    ...(nonFixturePrompt === false
      ? ['Browser proof indicates the generated app came from a fixture prompt.']
      : []),
    ...(designSandbox.status !== 'pass'
      ? [
          designSandbox.status === 'missing'
            ? 'Generated-app design sandbox isolation browser proof is missing.'
            : `Generated-app design sandbox isolation failed: ${designSandbox.reasons.join(' ')}`,
        ]
      : []),
    ...(designStyleSwitch.status !== 'pass'
      ? [
          designStyleSwitch.status === 'missing'
            ? 'Generated-app design style switch browser proof is missing.'
            : `Generated-app design style switch failed: ${designStyleSwitch.reasons.join(' ')}`,
        ]
      : []),
    ...(visualRichness.status !== 'pass'
      ? [
          visualRichness.status === 'missing'
            ? 'Generated-app visual-richness browser proof is missing.'
            : `Generated-app visual-richness failed: ${visualRichness.reasons.join(' ')}`,
        ]
      : []),
  ]
  const quality: StudioEndUserAppQualityEvidence = {
    ...input.proof.quality,
    status: qualityReasons.length === 0 ? 'pass' : 'fail',
    ...(browserDesignQuality
      ? {
          designQualityStatus: browserDesignQuality.status,
          designQualityScore: browserDesignQuality.score,
          designQualityHardeningPassed: browserDesignQuality.hardeningPassed,
          designQualityArtifactCount: browserDesignQuality.artifactCount,
          designQualitySourceKind: browserDesignQuality.sourceKind,
          ...(browserDesignQuality.measuredBy ? { designQualityMeasuredBy: browserDesignQuality.measuredBy } : {}),
          ...(browserDesignQuality.capturedAt ? { designQualityCapturedAt: browserDesignQuality.capturedAt } : {}),
          designQualityInteractionTraceArtifactCount: browserDesignQuality.interactionTraceArtifactCount,
          designQualityAuthorStyleObjectCount: browserDesignQuality.authorStyleObjectCount,
          designQualityUnconvertedStyleObjectCount: browserDesignQuality.unconvertedStyleObjectCount,
        }
      : {}),
    visualRichness,
    renderedBlockCount,
    screenshotArtifactCount,
    brokenImageCount,
    nonFixturePrompt,
    designStyleSwitch,
    reasons: qualityReasons,
  }
  const scenarioMatrix = buildStudioEndUserScenarioMatrix(
    { app: input.app, evidence: input.evidence },
    input.browserEvidence,
  )
  const adaptiveRuntime = buildStudioEndUserAdaptiveRuntimeProof(input.evidence, quality, scenarioMatrix)
  const referoBatch = buildStudioReferoOntologyBatchAdmission(input.proof.speed, quality, scenarioMatrix, adaptiveRuntime)

  return {
    ...input.proof,
    quality,
    designSandbox,
    adaptiveRuntime,
    scenarioMatrix,
    referoBatch,
    generatedAt: new Date().toISOString(),
  }
}

export function buildStudioAgenticRagValidationProof(
  source?: unknown,
): StudioAgenticRagValidationProof {
  const sourceRecord = readRecord(source)
  if (sourceRecord?.schemaVersion === 'studio-agentic-rag-validation-proof.v1') {
    const declaredRequiredKinds = normalizeAgenticValidationKinds(readArray(sourceRecord.requiredKinds))
    const requiredKinds = declaredRequiredKinds.length > 0
      ? declaredRequiredKinds
      : REQUIRED_AGENTIC_VALIDATION_RAG_KINDS
    const coveredKinds = normalizeAgenticValidationKinds(readArray(sourceRecord.coveredKinds))
    const declaredMissingKinds = normalizeAgenticValidationKinds(readArray(sourceRecord.missingKinds))
    const missingKinds = Array.from(new Set([
      ...declaredMissingKinds,
      ...requiredKinds.filter((kind) => !coveredKinds.includes(kind)),
    ]))
    const acceptedEvidenceIds = normalizeStringArray(readArray(sourceRecord.acceptedEvidenceIds) ?? undefined)
    const rejectedEvidenceIds = normalizeStringArray(readArray(sourceRecord.rejectedEvidenceIds) ?? undefined)
    const explicitReasons = normalizeStringArray(readArray(sourceRecord.reasons) ?? undefined)
    const minimumEvidenceCount = Math.max(
      1,
      readNumber(sourceRecord.minimumEvidenceCount, requiredKinds.length),
    )
    const status = sourceRecord.status === 'pass' &&
      missingKinds.length === 0 &&
      acceptedEvidenceIds.length >= minimumEvidenceCount
      ? 'pass'
      : 'blocked'

    return {
      schemaVersion: 'studio-agentic-rag-validation-proof.v1',
      status,
      requiredKinds,
      coveredKinds,
      missingKinds,
      minimumEvidenceCount,
      acceptedEvidenceIds,
      rejectedEvidenceIds,
      policy: {
        admittedValidationUse: 'admitted-only',
        candidateValidationUse: 'blocked',
        failClosed: true,
      },
      reasons: status === 'pass'
        ? []
        : explicitReasons.length > 0
          ? explicitReasons
          : ['Agentic RAG validation proof is not passing.'],
    }
  }

  const assets = readAgenticValidationAssets(source)
  const explicitRequiredKinds = normalizeAgenticValidationKinds(readArray(sourceRecord?.requiredKinds))
  const requiredKinds = explicitRequiredKinds.length > 0
    ? explicitRequiredKinds
    : REQUIRED_AGENTIC_VALIDATION_RAG_KINDS
  const minimumEvidenceCount = Math.max(
    1,
    readNumber(sourceRecord?.minimumEvidenceCount, requiredKinds.length),
  )
  const accepted = assets.filter((asset) => (
    requiredKinds.includes(asset.kind) &&
    asset.status === 'admitted' &&
    (asset.authority === 'canonical' || asset.authority === 'promoted')
  ))
  const coveredKinds = requiredKinds.filter((kind) => accepted.some((asset) => asset.kind === kind))
  const missingKinds = requiredKinds.filter((kind) => !coveredKinds.includes(kind))
  const rejected = assets.filter((asset) => !accepted.includes(asset))
  const reasons = [
    ...(assets.length === 0
      ? ['No validation RAG assets were supplied for accepted app generation.']
      : []),
    ...missingKinds.map((kind) => `Missing admitted validation RAG asset kind: ${kind}.`),
    ...(accepted.length < minimumEvidenceCount
      ? [`Validation RAG accepted evidence count ${accepted.length} is below minimum ${minimumEvidenceCount}.`]
      : []),
    ...rejected
      .filter((asset) => requiredKinds.includes(asset.kind))
      .map((asset) =>
        `Validation RAG asset ${asset.assetId} is ${asset.status}/${asset.authority}; accepted generation requires admitted canonical or promoted evidence.`,
      ),
  ]

  return {
    schemaVersion: 'studio-agentic-rag-validation-proof.v1',
    status: reasons.length === 0 ? 'pass' : 'blocked',
    requiredKinds,
    coveredKinds,
    missingKinds,
    minimumEvidenceCount,
    acceptedEvidenceIds: accepted.map((asset) => asset.assetId),
    rejectedEvidenceIds: rejected.map((asset) => asset.assetId),
    policy: {
      admittedValidationUse: 'admitted-only',
      candidateValidationUse: 'blocked',
      failClosed: true,
    },
    reasons,
  }
}

export function buildStudioEndUserAppGenerationProof(
  input: StudioEndUserAppGenerationProofInput,
): StudioEndUserAppGenerationProof {
  const thresholds = {
    ...STUDIO_END_USER_APP_PROOF_THRESHOLDS,
    ...(input.thresholds ?? {}),
  }
  const generationMs = Math.max(0, Math.round(input.completedAtMs - input.startedAtMs))
  const validationMs = Math.max(0, Math.round(input.validationDurationMs))
  const totalMs = generationMs + validationMs
  const firstBlockMs = typeof input.firstBlockAtMs === 'number'
    ? Math.max(0, Math.round(input.firstBlockAtMs - input.startedAtMs))
    : null
  const speedReasons: string[] = []

  if (totalMs > thresholds.totalMs) {
    speedReasons.push(`Total generation proof exceeded budget (${totalMs}ms > ${thresholds.totalMs}ms).`)
  }
  if (validationMs > thresholds.validationMs) {
    speedReasons.push(`Post-generation validation exceeded budget (${validationMs}ms > ${thresholds.validationMs}ms).`)
  }
  if (firstBlockMs !== null && firstBlockMs > thresholds.firstBlockMs) {
    speedReasons.push(`First generated block exceeded budget (${firstBlockMs}ms > ${thresholds.firstBlockMs}ms).`)
  }

  const speed: StudioEndUserAppSpeedEvidence = {
    status: speedReasons.length === 0 ? 'pass' : 'fail',
    totalMs,
    generationMs,
    validationMs,
    firstBlockMs,
    budgets: {
      totalMs: thresholds.totalMs,
      validationMs: thresholds.validationMs,
      firstBlockMs: thresholds.firstBlockMs,
    },
    reasons: speedReasons,
  }

  const gate = buildStudioGeneratedAppReadinessGate(input.evidence)
  const validation = input.validationResult
  const validationScores = readValidationScores(validation, input.blockQualityScores)
  const blockCount = readNumber(validation?.blockCount, validationScores.length)
  const averageScore = readNumber(
    validation?.averageScore,
    validationScores.length > 0
      ? validationScores.reduce((sum, score) => sum + score, 0) / validationScores.length
      : 0,
  )
  const crossBlockIssueCount = readNumber(validation?.crossBlock?.issueCount)
  const minBlockScore = validationScores.length > 0 ? Math.min(...validationScores) : null
  const browserEvidence = input.browserEvidence ?? null
  const renderedBlockCount = typeof browserEvidence?.renderedBlockCount === 'number'
    ? browserEvidence.renderedBlockCount
    : countGeneratedBlocks(input.app)
  const screenshotArtifactCount = browserEvidence?.screenshotArtifacts?.length ?? 0
  const brokenImageCount = typeof browserEvidence?.brokenImageCount === 'number'
    ? browserEvidence.brokenImageCount
    : null
  const nonFixturePrompt = typeof browserEvidence?.nonFixturePrompt === 'boolean'
    ? browserEvidence.nonFixturePrompt
    : null
  const designSandbox = buildStudioEndUserDesignSandboxIsolationProof(browserEvidence)
  const designStyleSwitch = buildStudioEndUserDesignStyleSwitchProof(browserEvidence)
  const browserDesignQuality = normalizeBrowserDesignQualityEvidence(browserEvidence?.designQuality)
  const visualRichness = buildStudioEndUserVisualRichnessProof(browserEvidence, browserDesignQuality)
  const validationRag = buildStudioAgenticRagValidationProof(input.validationRagEvidence)
  const qualityReasons: string[] = []
  const designQuality = readDesignQualityEvidenceModel(input.app)
  const designQualityStatus = designQuality?.status ?? 'missing'
  const designQualityScore = designQuality?.summary.score ?? 0
  const designQualityArtifactCount = designQuality?.summary.artifactCount ?? 0
  const designQualityHardeningPassed = designQuality?.summary.hardeningPassed ?? false
  const designQualitySourceKind = designQuality?.evidence.sourceKind ?? 'missing'
  const designQualityInteractionTraceArtifactCount = designQuality?.evidence.interactionTraceArtifactCount ?? 0
  const designQualityAuthorStyleObjectCount = designQuality?.summary.authorStyleObjectCount ?? 0
  const designQualityUnconvertedStyleObjectCount = designQuality?.summary.unconvertedStyleObjectCount ?? 0

  if (!validation) qualityReasons.push('Post-generation app validation result is missing.')
  if (averageScore < thresholds.scoreFloor) {
    qualityReasons.push(`Average validation score is below floor (${averageScore} < ${thresholds.scoreFloor}).`)
  }
  if (!designQuality) {
    qualityReasons.push('Rendered design quality evidence is missing; ontology generation cannot pass on structural validation alone.')
  } else {
    if (designQuality.status !== 'pass') {
      qualityReasons.push(`Rendered design quality status is ${designQuality.status}; pass is required.`)
    }
    if (designQuality.evidence.sourceKind !== 'measured') {
      qualityReasons.push(`Rendered design quality evidence is ${designQuality.evidence.sourceKind}; measured evidence is required.`)
    }
    if (designQualityScore < thresholds.designQualityScoreFloor) {
      qualityReasons.push(`Rendered design quality score is below floor (${designQualityScore} < ${thresholds.designQualityScoreFloor}).`)
    }
    if (!designQualityHardeningPassed) {
      qualityReasons.push('Rendered design quality hardening did not pass.')
    }
    if (designQualityAuthorStyleObjectCount > 0) {
      qualityReasons.push(`Rendered design quality contains author style objects (${designQualityAuthorStyleObjectCount}); design compiler normalization is required.`)
    }
    if (designQualityUnconvertedStyleObjectCount > 0) {
      qualityReasons.push(`Rendered design quality contains unconverted style objects (${designQualityUnconvertedStyleObjectCount}).`)
    }
    if (thresholds.requireDesignQualityArtifacts && designQualityArtifactCount <= 0) {
      qualityReasons.push('Rendered design quality screenshot artifacts are missing; metric-only quality is not sufficient.')
    }
    qualityReasons.push(...designQuality.generatorFeedback.blockedReasons)
  }
  if (validation?.overallGrade && validation.overallGrade !== 'pass') {
    qualityReasons.push(`Overall validation grade is ${validation.overallGrade}; pass is required.`)
  }
  if (blockCount <= 0) qualityReasons.push('Generated app has no validated blocks.')
  if (crossBlockIssueCount > 0) {
    qualityReasons.push(`Cross-block validation reported ${crossBlockIssueCount} issue(s).`)
  }
  if (gate.status !== 'pass' && gate.status !== 'not-required') {
    qualityReasons.push(`Generated-app readiness gate is ${gate.status}.`)
  }
  if (
    input.evidence.generatedApp.artifactParity.status !== 'pass' &&
    input.evidence.generatedApp.artifactParity.status !== 'not-applicable'
  ) {
    qualityReasons.push(`Generated app artifact parity is ${input.evidence.generatedApp.artifactParity.status}.`)
  }
  if (input.evidence.fullCoverage.status !== 'pass') {
    qualityReasons.push(
      `Full component/action/style/FlatBuffer coverage audit is ${input.evidence.fullCoverage.status}; pass is required before ontology batch promotion.`,
    )
  }
  if (input.evidence.exhaustiveProof.status !== 'pass') {
    qualityReasons.push(
      `Exhaustive component SSR/style/runtime action proof audit is ${input.evidence.exhaustiveProof.status}; pass is required before ontology batch promotion.`,
    )
  }
  if (renderedBlockCount !== null && renderedBlockCount <= 0) {
    qualityReasons.push('Browser proof did not render any generated blocks.')
  }
  if (brokenImageCount !== null && brokenImageCount > 0) {
    qualityReasons.push(`Browser proof found ${brokenImageCount} broken image(s).`)
  }
  if (nonFixturePrompt === false) {
    qualityReasons.push('Browser proof indicates the generated app came from a fixture prompt.')
  }
  if (designSandbox.status !== 'pass') {
    qualityReasons.push(
      designSandbox.status === 'missing'
        ? 'Generated-app design sandbox isolation browser proof is missing.'
        : `Generated-app design sandbox isolation failed: ${designSandbox.reasons.join(' ')}`,
    )
  }
  if (designStyleSwitch.status !== 'pass') {
    qualityReasons.push(
      designStyleSwitch.status === 'missing'
        ? 'Generated-app design style switch browser proof is missing.'
        : `Generated-app design style switch failed: ${designStyleSwitch.reasons.join(' ')}`,
    )
  }
  if (visualRichness.status !== 'pass') {
    qualityReasons.push(
      visualRichness.status === 'missing'
        ? 'Generated-app visual-richness browser proof is missing.'
        : `Generated-app visual-richness failed: ${visualRichness.reasons.join(' ')}`,
    )
  }
  if (validationRag.status !== 'pass') {
    qualityReasons.push(...validationRag.reasons.map((reason) => `Agentic RAG validation: ${reason}`))
  }

  const quality: StudioEndUserAppQualityEvidence = {
    status: qualityReasons.length === 0 ? 'pass' : 'fail',
    averageScore: Math.round(averageScore * 100) / 100,
    scoreFloor: thresholds.scoreFloor,
    designQualityStatus,
    designQualityScore,
    designQualityScoreFloor: thresholds.designQualityScoreFloor,
    designQualityHardeningPassed,
    designQualityArtifactCount,
    designQualitySourceKind,
    ...(designQuality?.evidence.measuredBy ? { designQualityMeasuredBy: designQuality.evidence.measuredBy } : {}),
    ...(designQuality?.evidence.capturedAt ? { designQualityCapturedAt: designQuality.evidence.capturedAt } : {}),
    designQualityInteractionTraceArtifactCount,
    designQualityAuthorStyleObjectCount,
    designQualityUnconvertedStyleObjectCount,
    visualRichness,
    overallGrade: validation?.overallGrade ?? 'missing',
    blockCount,
    minBlockScore,
    crossBlockIssueCount,
    generatedAppStatus: gate.status,
    artifactParityStatus: input.evidence.generatedApp.artifactParity.status,
    renderedBlockCount,
    screenshotArtifactCount,
    brokenImageCount,
    nonFixturePrompt,
    designStyleSwitch,
    reasons: qualityReasons,
  }

  const scenarioMatrix = buildStudioEndUserScenarioMatrix(input, browserEvidence)
  const adaptiveRuntime = buildStudioEndUserAdaptiveRuntimeProof(input.evidence, quality, scenarioMatrix)
  const referoBatch = buildStudioReferoOntologyBatchAdmission(speed, quality, scenarioMatrix, adaptiveRuntime)
  return {
    schemaVersion: 'studio-end-user-app-generation-proof.v1',
    speed,
    quality,
    validationRag,
    designSandbox,
    adaptiveRuntime,
    scenarioMatrix,
    referoBatch,
    generatedAt: new Date().toISOString(),
  }
}

function buildStudioEndUserDesignSandboxIsolationProof(
  browserEvidence?: StudioEndUserAppBrowserEvidence | null,
): StudioEndUserDesignSandboxIsolationProof {
  const evidence = browserEvidence?.designSandboxIsolation
  if (!evidence) {
    return {
      schemaVersion: 'studio-end-user-design-sandbox-isolation-proof.v1',
      status: 'missing',
      appRootSandboxed: null,
      portalInsideAppHost: null,
      portalInsideAppSurface: null,
      htmlHasAppStyleClass: null,
      bodyHasAppStyleClass: null,
      htmlHasCompleteTokenAttr: null,
      bodyHasCompleteTokenAttr: null,
      portalCount: 0,
      portalId: null,
      portalAppId: null,
      portalParentTag: null,
      appHostHasCompleteTokenAttr: null,
      appRenderRootHasCompleteTokenAttr: null,
      appHostDesignScope: null,
      outsideAppCompleteTokenScopeCount: 0,
      outsideAppInlinePrimaryTokenCount: 0,
      appStyleElementCount: 0,
      appStyleElementsInAppHostCount: 0,
      appStyleElementsInHeadCount: 0,
      outsideAppStyleElementCount: 0,
      portalHasStyleClass: null,
      portalHasCompleteTokenAttr: null,
      reasons: ['Generated-app design sandbox isolation evidence is missing.'],
    }
  }

  const appRootSandboxed = normalizeBooleanish(evidence.appRootSandboxed)
  const portalInsideAppSurface = normalizeBooleanish(
    evidence.portalInsideAppSurface ?? evidence.portalInsideAppHost,
  )
  const portalInsideAppHost = portalInsideAppSurface
  const htmlHasAppStyleClass = normalizeNullableBoolean(evidence.htmlHasAppStyleClass)
  const bodyHasAppStyleClass = normalizeNullableBoolean(evidence.bodyHasAppStyleClass)
  const htmlHasCompleteTokenAttr = normalizeNullableBoolean(evidence.htmlHasCompleteTokenAttr)
  const bodyHasCompleteTokenAttr = normalizeNullableBoolean(evidence.bodyHasCompleteTokenAttr)
  const portalCount = readNumber(evidence.portalCount)
  const portalHasStyleClass = normalizeNullableBoolean(evidence.portalHasStyleClass)
  const portalHasCompleteTokenAttr = normalizeNullableBoolean(evidence.portalHasCompleteTokenAttr)
  const appRenderRootHasCompleteTokenAttr = normalizeNullableBoolean(
    evidence.appRenderRootHasCompleteTokenAttr ?? evidence.appHostHasCompleteTokenAttr,
  )
  const appHostHasCompleteTokenAttr = appRenderRootHasCompleteTokenAttr
  const portalId = typeof evidence.portalId === 'string' ? evidence.portalId : null
  const portalAppId = typeof evidence.portalAppId === 'string' ? evidence.portalAppId : null
  const portalParentTag = typeof evidence.portalParentTag === 'string' ? evidence.portalParentTag : null
  const appHostDesignScope = typeof evidence.appHostDesignScope === 'string' ? evidence.appHostDesignScope : null
  const outsideAppCompleteTokenScopeCount = readNumber(evidence.outsideAppCompleteTokenScopeCount)
  const outsideAppInlinePrimaryTokenCount = readNumber(evidence.outsideAppInlinePrimaryTokenCount)
  const appStyleElementCount = readNumber(evidence.appStyleElementCount)
  const appStyleElementsInAppHostCount = readNumber(evidence.appStyleElementsInAppHostCount)
  const appStyleElementsInHeadCount = readNumber(evidence.appStyleElementsInHeadCount)
  const outsideAppStyleElementCount = readNumber(evidence.outsideAppStyleElementCount)
  const appHostBackgroundToken = typeof evidence.appHostBackgroundToken === 'string'
    ? evidence.appHostBackgroundToken
    : undefined
  const appHostForegroundToken = typeof evidence.appHostForegroundToken === 'string'
    ? evidence.appHostForegroundToken
    : undefined
  const portalBackgroundToken = typeof evidence.portalBackgroundToken === 'string'
    ? evidence.portalBackgroundToken
    : undefined
  const portalForegroundToken = typeof evidence.portalForegroundToken === 'string'
    ? evidence.portalForegroundToken
    : undefined
  const reasons = [
    ...normalizeStringArray(evidence.reasons),
    ...(evidence.status === 'fail' ? ['Browser evidence reported design sandbox isolation failure.'] : []),
    ...(appRootSandboxed !== true ? ['App root is not marked as design-sandboxed.'] : []),
    ...(portalInsideAppSurface !== true ? ['Generated-app portal is not mounted inside the generated-app design surface.'] : []),
    ...(portalParentTag === 'BODY' ? ['Generated-app portal is mounted under document.body.'] : []),
    ...(htmlHasAppStyleClass === true ? ['Studio document html has generated-app style class.'] : []),
    ...(bodyHasAppStyleClass === true ? ['Studio document body has generated-app style class.'] : []),
    ...(htmlHasCompleteTokenAttr === true ? ['Studio document html has generated-app complete-token attr.'] : []),
    ...(bodyHasCompleteTokenAttr === true ? ['Studio document body has generated-app complete-token attr.'] : []),
    ...(appHostDesignScope !== 'application' ? ['Generated-app render root is not marked as application design scope.'] : []),
    ...(appRenderRootHasCompleteTokenAttr !== true ? ['Generated-app render root is missing complete-token attr.'] : []),
    ...(outsideAppCompleteTokenScopeCount > 0 ? [`Generated-app complete-token scope escaped outside app surface (${outsideAppCompleteTokenScopeCount}).`] : []),
    ...(outsideAppInlinePrimaryTokenCount > 0 ? [`Generated-app inline primary token escaped outside app surface (${outsideAppInlinePrimaryTokenCount}).`] : []),
    ...(appStyleElementsInHeadCount > 0 ? [`Generated-app style element escaped into Studio document head (${appStyleElementsInHeadCount}).`] : []),
    ...(outsideAppStyleElementCount > 0 ? [`Generated-app style element escaped outside app surface (${outsideAppStyleElementCount}).`] : []),
    ...(appStyleElementCount > 0 && appStyleElementsInAppHostCount !== appStyleElementCount
      ? [`Generated-app style element ownership mismatch (${appStyleElementsInAppHostCount}/${appStyleElementCount} in app surface).`]
      : []),
    ...(!appHostBackgroundToken ? ['Generated-app render root is missing --background token evidence.'] : []),
    ...(!appHostForegroundToken ? ['Generated-app render root is missing --foreground token evidence.'] : []),
    ...(portalCount <= 0 ? ['Generated-app portal root is missing.'] : []),
    ...(portalId === 'cogo-app-portal-root' ? ['Generated-app portal uses the old shared global ID.'] : []),
    ...(portalId && !portalId.startsWith('cogo-app-portal-root-') ? [`Generated-app portal has unexpected id ${portalId}.`] : []),
    ...(portalHasStyleClass !== true ? ['Generated-app portal is missing the app style class.'] : []),
    ...(portalHasCompleteTokenAttr !== true ? ['Generated-app portal is missing complete-token attr.'] : []),
    ...(!portalBackgroundToken ? ['Generated-app portal is missing --background token evidence.'] : []),
    ...(!portalForegroundToken ? ['Generated-app portal is missing --foreground token evidence.'] : []),
  ]

  return {
    schemaVersion: 'studio-end-user-design-sandbox-isolation-proof.v1',
    status: reasons.length === 0 ? 'pass' : 'fail',
    appRootSandboxed,
    portalInsideAppHost,
    portalInsideAppSurface,
    htmlHasAppStyleClass,
    bodyHasAppStyleClass,
    htmlHasCompleteTokenAttr,
    bodyHasCompleteTokenAttr,
    portalCount,
    portalId,
    portalAppId,
    portalParentTag,
    appHostHasCompleteTokenAttr,
    appRenderRootHasCompleteTokenAttr,
    appHostDesignScope,
    outsideAppCompleteTokenScopeCount,
    outsideAppInlinePrimaryTokenCount,
    appStyleElementCount,
    appStyleElementsInAppHostCount,
    appStyleElementsInHeadCount,
    outsideAppStyleElementCount,
    portalHasStyleClass,
    portalHasCompleteTokenAttr,
    ...(appHostBackgroundToken ? { appHostBackgroundToken } : {}),
    ...(appHostForegroundToken ? { appHostForegroundToken } : {}),
    ...(portalBackgroundToken ? { portalBackgroundToken } : {}),
    ...(portalForegroundToken ? { portalForegroundToken } : {}),
    reasons,
  }
}

function isTransparentCssColor(value: string | null): boolean {
  const normalized = (value ?? '').trim().toLowerCase()
  return (
    normalized.length === 0 ||
    normalized === 'transparent' ||
    normalized === 'rgba(0, 0, 0, 0)' ||
    normalized === 'rgb(0 0 0 / 0)' ||
    normalized === 'rgba(0 0 0 / 0)'
  )
}

function buildStudioEndUserDesignStyleSwitchProof(
  browserEvidence?: StudioEndUserAppBrowserEvidence | null,
): StudioEndUserDesignStyleSwitchProof {
  const evidence = browserEvidence?.designStyleSwitch
  if (!evidence) {
    return {
      schemaVersion: 'studio-end-user-design-style-switch-proof.v1',
      status: 'missing',
      mutationApi: 'missing',
      structuralDomIdentical: null,
      basePrimaryBackground: null,
      targetPrimaryBackground: null,
      primaryBackgroundChanged: false,
      appHostHasCompleteTokenAttr: null,
      targetPrimaryToken: null,
      targetColorPrimaryToken: null,
      studioHtmlPrimaryChanged: null,
      studioBodyPrimaryChanged: null,
      studioHtmlHasCompleteTokenAttr: null,
      studioBodyHasCompleteTokenAttr: null,
      reasons: ['Generated-app design style switch evidence is missing.'],
    }
  }

  const structuralDomIdentical = normalizeNullableBoolean(evidence.structuralDomIdentical)
  const appHostHasCompleteTokenAttr = normalizeNullableBoolean(evidence.appHostHasCompleteTokenAttr)
  const mutationApi = evidence.mutationApi === 'app-design-sandbox'
    ? 'app-design-sandbox'
    : evidence.mutationApi === 'legacy-theme-slice'
      ? 'legacy-theme-slice'
      : 'missing'
  const studioHtmlPrimaryChanged = normalizeNullableBoolean(evidence.studioHtmlPrimaryChanged)
  const studioBodyPrimaryChanged = normalizeNullableBoolean(evidence.studioBodyPrimaryChanged)
  const studioHtmlHasCompleteTokenAttr = normalizeNullableBoolean(evidence.studioHtmlHasCompleteTokenAttr)
  const studioBodyHasCompleteTokenAttr = normalizeNullableBoolean(evidence.studioBodyHasCompleteTokenAttr)
  const basePrimaryBackground = typeof evidence.basePrimaryBackground === 'string'
    ? evidence.basePrimaryBackground
    : null
  const targetPrimaryBackground = typeof evidence.targetPrimaryBackground === 'string'
    ? evidence.targetPrimaryBackground
    : null
  const targetPrimaryToken = typeof evidence.targetPrimaryToken === 'string'
    ? evidence.targetPrimaryToken
    : null
  const targetColorPrimaryToken = typeof evidence.targetColorPrimaryToken === 'string'
    ? evidence.targetColorPrimaryToken
    : null
  const primaryBackgroundChanged =
    !isTransparentCssColor(basePrimaryBackground) &&
    !isTransparentCssColor(targetPrimaryBackground) &&
    basePrimaryBackground !== targetPrimaryBackground
  const reasons = [
    ...normalizeStringArray(evidence.reasons),
    ...(evidence.status === 'fail' ? ['Browser evidence reported design style switch failure.'] : []),
    ...(structuralDomIdentical !== true ? ['Generated-app DOM structure changed during design style switch.'] : []),
    ...(appHostHasCompleteTokenAttr !== true ? ['Generated-app render root is missing complete-token attr during style switch.'] : []),
    ...(!targetPrimaryToken ? ['Target --primary token evidence is missing.'] : []),
    ...(!targetColorPrimaryToken ? ['Target --color-primary token evidence is missing.'] : []),
    ...(mutationApi !== 'app-design-sandbox' ? ['Design style switch did not use the app-design sandbox API.'] : []),
    ...(studioHtmlPrimaryChanged === true ? ['Studio document html primary token changed during app-design switch.'] : []),
    ...(studioBodyPrimaryChanged === true ? ['Studio document body primary token changed during app-design switch.'] : []),
    ...(studioHtmlHasCompleteTokenAttr === true ? ['Studio document html has generated-app complete-token attr during style switch.'] : []),
    ...(studioBodyHasCompleteTokenAttr === true ? ['Studio document body has generated-app complete-token attr during style switch.'] : []),
  ]

  return {
    schemaVersion: 'studio-end-user-design-style-switch-proof.v1',
    status: reasons.length === 0 ? 'pass' : 'fail',
    mutationApi,
    structuralDomIdentical,
    basePrimaryBackground,
    targetPrimaryBackground,
    primaryBackgroundChanged,
    appHostHasCompleteTokenAttr,
    targetPrimaryToken,
    targetColorPrimaryToken,
    studioHtmlPrimaryChanged,
    studioBodyPrimaryChanged,
    studioHtmlHasCompleteTokenAttr,
    studioBodyHasCompleteTokenAttr,
    reasons,
  }
}

const STATE_BOUNDARY_PATH_RE = /\b(?:local|appData|_results|computed|tokens|state|localState|response|ui|params|page)\.[A-Za-z0-9_$.-]+/g
const LEGACY_STATE_PREFIX_RE = /^(?:state|localState|response|ui|params|page)\./i

function sortedUnique(values: readonly string[]): string[] {
  return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean))).sort()
}

function extractStateBoundaryPaths(values: readonly string[]): string[] {
  const paths: string[] = []
  for (const value of values) {
    for (const match of value.matchAll(STATE_BOUNDARY_PATH_RE)) {
      paths.push(match[0])
    }
  }
  return sortedUnique(paths)
}

function isPathInScope(paths: readonly string[], prefix: string): boolean {
  return paths.some((path) => path.startsWith(prefix))
}

function toResultPaths(keys: readonly string[]): string[] {
  return keys.map((key) => key.startsWith('_results.') ? key : `_results.${key}`)
}

function toComputedPaths(keys: readonly string[]): string[] {
  return keys.map((key) => key.startsWith('computed.') ? key : `computed.${key}`)
}

function buildMissingStateBoundaryProof(reason: string): StudioEndUserStateBoundaryProof {
  return {
    schemaVersion: 'studio-end-user-state-boundary-proof.v1',
    status: 'missing',
    localStateEvidencePaths: [],
    globalStateEvidencePaths: [],
    resultStateEvidencePaths: [],
    computedStateEvidencePaths: [],
    ambiguousStateEvidencePaths: [],
    legacyStateEvidencePaths: [],
    transitionScopes: [],
    localOnlyTransitionCount: 0,
    globalOnlyTransitionCount: 0,
    mixedLocalGlobalTransitionCount: 0,
    resultTransitionCount: 0,
    computedTransitionCount: 0,
    designTokenTransitionCount: 0,
    reasons: [reason],
  }
}

function buildStudioEndUserStateBoundaryProof(
  plan: StudioAdaptiveRuntimeEvidenceSummary,
  scenarioMatrix: StudioEndUserScenarioMatrix,
  adaptiveTransitions: readonly StudioEndUserAdaptiveTransitionEvidence[],
): StudioEndUserStateBoundaryProof {
  const requiredScenarios = scenarioMatrix.scenarios.filter((scenario) => scenario.required)
  if (requiredScenarios.length === 0) {
    return buildMissingStateBoundaryProof('State boundary proof requires at least one required end-user scenario.')
  }

  const stateEvidenceText = requiredScenarios.flatMap((scenario) => [
    ...scenario.stateEvidence,
    ...scenario.dataEvidence,
    ...scenario.actionFlowTraceEvidence,
    ...(scenario.actionFlowProof?.stateDiffPaths ?? []),
    ...toResultPaths(scenario.actionFlowProof?.resultKeys ?? []),
    ...toComputedPaths(scenario.actionFlowProof?.computedKeys ?? []),
  ])
  const transitionScopes = sortedUnique(adaptiveTransitions.map((transition) => transition.stateScope))
  const transitionPathGroups = adaptiveTransitions.map((transition) => extractStateBoundaryPaths([transition.stateScope]))
  const statePaths = extractStateBoundaryPaths([
    ...stateEvidenceText,
    ...transitionScopes,
  ])

  const localStateEvidencePaths = statePaths.filter((path) => path.startsWith('local.'))
  const globalStateEvidencePaths = statePaths.filter((path) => path.startsWith('appData.'))
  const resultStateEvidencePaths = statePaths.filter((path) => path.startsWith('_results.'))
  const computedStateEvidencePaths = statePaths.filter((path) => path.startsWith('computed.'))
  const legacyStateEvidencePaths = statePaths.filter((path) => LEGACY_STATE_PREFIX_RE.test(path))
  const ambiguousStateEvidencePaths = legacyStateEvidencePaths
  const localOnlyTransitionCount = transitionPathGroups.filter((paths) => (
    isPathInScope(paths, 'local.') && !isPathInScope(paths, 'appData.')
  )).length
  const globalOnlyTransitionCount = transitionPathGroups.filter((paths) => (
    isPathInScope(paths, 'appData.') && !isPathInScope(paths, 'local.')
  )).length
  const mixedLocalGlobalTransitionCount = transitionPathGroups.filter((paths) => (
    isPathInScope(paths, 'local.') && isPathInScope(paths, 'appData.')
  )).length
  const resultTransitionCount = transitionPathGroups.filter((paths) => isPathInScope(paths, '_results.')).length
  const computedTransitionCount = transitionPathGroups.filter((paths) => isPathInScope(paths, 'computed.')).length
  const designTokenTransitionCount = transitionPathGroups.filter((paths) => isPathInScope(paths, 'tokens.')).length
  const needsGlobalEvidence =
    plan.globalStatePolicy === 'appData-shared' &&
    plan.adaptationAxes.some((axis) => axis === 'workflow' || axis === 'data-scope' || axis === 'action-availability')
  const needsResultEvidence = plan.evidencePolicy.requireActionFlowTraceForDynamicApps
  const needsComputedEvidence = plan.evidencePolicy.requireActionFlowTraceForDynamicApps

  const reasons = [
    ...(plan.localStatePolicy === 'block-local-first' && localStateEvidencePaths.length === 0
      ? ['State boundary proof requires local.* evidence for block-local state isolation.']
      : []),
    ...(needsGlobalEvidence && globalStateEvidencePaths.length === 0
      ? ['State boundary proof requires appData.* evidence for shared global state.']
      : []),
    ...(needsResultEvidence && resultStateEvidencePaths.length === 0
      ? ['State boundary proof requires _results.* evidence from ActionFlow execution.']
      : []),
    ...(needsComputedEvidence && computedStateEvidencePaths.length === 0
      ? ['State boundary proof requires computed.* evidence for UI projection.']
      : []),
    ...(needsGlobalEvidence && plan.localStatePolicy === 'block-local-first' && mixedLocalGlobalTransitionCount === 0
      ? ['State boundary proof requires at least one adaptive transition that combines local.* and appData.* scopes.']
      : []),
    ...(legacyStateEvidencePaths.length > 0
      ? [`State boundary proof rejects legacy state prefixes: ${legacyStateEvidencePaths.join(', ')}.`]
      : []),
  ]

  return {
    schemaVersion: 'studio-end-user-state-boundary-proof.v1',
    status: reasons.length === 0 ? 'pass' : 'fail',
    localStateEvidencePaths,
    globalStateEvidencePaths,
    resultStateEvidencePaths,
    computedStateEvidencePaths,
    ambiguousStateEvidencePaths,
    legacyStateEvidencePaths,
    transitionScopes,
    localOnlyTransitionCount,
    globalOnlyTransitionCount,
    mixedLocalGlobalTransitionCount,
    resultTransitionCount,
    computedTransitionCount,
    designTokenTransitionCount,
    reasons,
  }
}

function isPassingAdaptiveTransition(transition: StudioEndUserAdaptiveTransitionEvidence): boolean {
  return (
    transition.status === 'pass' &&
    transition.before.length > 0 &&
    transition.after.length > 0 &&
    transition.before !== transition.after
  )
}

function buildMissingAdaptiveAxisCoverageProof(reason: string): StudioEndUserAdaptiveAxisCoverageProof {
  return {
    schemaVersion: 'studio-end-user-adaptive-axis-coverage-proof.v1',
    status: 'missing',
    requiredAxes: [],
    passedAxes: [],
    missingAxes: [],
    reasons: [reason],
  }
}

function buildRequiredAdaptiveAxisCoverage(
  scenarioMatrix: StudioEndUserScenarioMatrix,
  adaptiveTransitions: readonly StudioEndUserAdaptiveTransitionEvidence[],
): StudioEndUserAdaptiveAxisCoverageProof {
  const requiredAxes: string[] = []
  const passedAxes: string[] = []
  const missingAxes: string[] = []
  const reasons: string[] = []

  for (const scenario of scenarioMatrix.scenarios.filter((item) => item.required)) {
    const scenarioRequiredAxes = REQUIRED_ADAPTIVE_AXES_BY_SCENARIO[scenario.id] ?? []
    const scenarioPassedAxes = new Set(
      scenario.adaptiveTransitions
        .filter(isPassingAdaptiveTransition)
        .map((transition) => transition.axis),
    )

    for (const axis of scenarioRequiredAxes) {
      const key = `${scenario.id}:${axis}`
      const axisPassed =
        scenarioPassedAxes.has(axis) ||
        (
          axis === 'design-style' &&
          adaptiveTransitions.some((transition) => (
            isPassingAdaptiveTransition(transition) &&
            transition.axis === 'design-style'
          ))
        )
      requiredAxes.push(key)
      if (axisPassed) {
        passedAxes.push(key)
      } else {
        missingAxes.push(key)
        reasons.push(`${END_USER_SCENARIO_LABELS[scenario.id]} requires a measured ${axis} adaptive transition.`)
      }
    }
  }

  const normalizedRequiredAxes = sortedUnique(requiredAxes)
  const normalizedPassedAxes = sortedUnique(passedAxes)
  const normalizedMissingAxes = sortedUnique(missingAxes)

  if (normalizedRequiredAxes.length === 0) {
    return buildMissingAdaptiveAxisCoverageProof(
      'Adaptive runtime axis coverage requires at least one required end-user scenario.',
    )
  }

  return {
    schemaVersion: 'studio-end-user-adaptive-axis-coverage-proof.v1',
    status: normalizedMissingAxes.length === 0 ? 'pass' : 'fail',
    requiredAxes: normalizedRequiredAxes,
    passedAxes: normalizedPassedAxes,
    missingAxes: normalizedMissingAxes,
    reasons,
  }
}

function buildStudioEndUserAdaptiveRuntimeProof(
  evidence: StudioGenerationEvidence,
  quality: StudioEndUserAppQualityEvidence,
  scenarioMatrix: StudioEndUserScenarioMatrix,
): StudioEndUserAdaptiveRuntimeProof {
  const plan = evidence.uiOntology.adaptiveRuntime
  if (plan.status !== 'planned') {
    return {
      schemaVersion: 'studio-end-user-adaptive-runtime-proof.v1',
      status: 'missing',
      enabled: false,
      situationSignals: [],
      adaptationAxes: [],
      localStatePolicy: 'missing',
      globalStatePolicy: 'missing',
      transitionCount: 0,
      passedTransitionCount: 0,
      transitions: [],
      axisCoverage: buildMissingAdaptiveAxisCoverageProof('Adaptive runtime planning metadata is missing from the end-user app proof.'),
      stateBoundary: buildMissingStateBoundaryProof('Adaptive runtime planning metadata is missing from the end-user app proof.'),
      evidence: [],
      reasons: ['Adaptive runtime planning metadata is missing from the end-user app proof.'],
    }
  }

  const scenarioText = scenarioMatrix.scenarios
    .flatMap((scenario) => [
      ...scenario.stateEvidence,
      ...scenario.dataEvidence,
      ...scenario.actionFlowTraceEvidence,
      ...scenario.artifactEvidence,
    ])
    .join(' ')
  const hasLocalStateEvidence = /\blocal\./i.test(scenarioText)
  const hasGlobalStateEvidence = /\bappData\./i.test(scenarioText)
  const hasActionFlowTraceEvidence = scenarioMatrix.scenarios.some((scenario) => (
    scenario.required &&
    scenario.status === 'pass' &&
    scenario.actionFlowTraceEvidence.length > 0
  ))
  const passedActionFlowRuntimeProofs = scenarioMatrix.scenarios.filter((scenario) => (
    scenario.required &&
    scenario.status === 'pass' &&
    scenario.actionFlowProof?.status === 'pass'
  ))
  const requiredScenarioCount = scenarioMatrix.scenarios.filter((scenario) => scenario.required).length
  const hasStructuredActionFlowRuntimeProof = requiredScenarioCount > 0 &&
    passedActionFlowRuntimeProofs.length === requiredScenarioCount
  const scenarioAdaptiveTransitions = scenarioMatrix.scenarios.flatMap((scenario) => scenario.adaptiveTransitions)
  const hasMeasuredDesignStyleTransition = scenarioAdaptiveTransitions.some((transition) => transition.axis === 'design-style')
  const designStyleTransition = plan.adaptationAxes.includes('design-style') && !hasMeasuredDesignStyleTransition
    ? [buildDesignStyleAdaptiveTransition(quality.designStyleSwitch)]
    : []
  const adaptiveTransitions = [...scenarioAdaptiveTransitions, ...designStyleTransition]
  const passedAdaptiveTransitions = adaptiveTransitions.filter(isPassingAdaptiveTransition)
  const adaptiveAxisCoverage = buildRequiredAdaptiveAxisCoverage(scenarioMatrix, adaptiveTransitions)
  const stateBoundary = buildStudioEndUserStateBoundaryProof(plan, scenarioMatrix, adaptiveTransitions)
  const reasons = [
    ...(!plan.enabled ? ['Adaptive runtime plan is disabled.'] : []),
    ...(plan.evidencePolicy.requireMeasuredDesignEvidence &&
      (
        quality.designQualitySourceKind !== 'measured' ||
        quality.designQualityStatus !== 'pass' ||
        quality.designQualityArtifactCount <= 0
      )
      ? ['Adaptive runtime requires measured design evidence with screenshot artifacts.']
      : []),
    ...(plan.evidencePolicy.requireActionFlowTraceForDynamicApps && !hasActionFlowTraceEvidence
      ? ['Adaptive runtime requires a passing required scenario with ActionFlow trace evidence.']
      : []),
    ...(plan.evidencePolicy.requireActionFlowTraceForDynamicApps && !hasStructuredActionFlowRuntimeProof
      ? ['Adaptive runtime requires structured ActionFlow runtime proof for every required scenario.']
      : []),
    ...(stateBoundary.status === 'pass' ? [] : stateBoundary.reasons),
    ...(passedAdaptiveTransitions.length === 0
      ? ['Adaptive runtime requires a measured situation transition that changes UI projection.']
      : []),
    ...adaptiveAxisCoverage.reasons,
    ...adaptiveTransitions.flatMap((transition) => (
      transition.status === 'fail'
        ? transition.reasons.map((reason) => `${transition.uiProbe}: ${reason}`)
        : []
    )),
  ]

  return {
    schemaVersion: 'studio-end-user-adaptive-runtime-proof.v1',
    status: reasons.length === 0 ? 'pass' : 'partial',
    enabled: plan.enabled,
    situationSignals: plan.situationSignals,
    adaptationAxes: plan.adaptationAxes,
    localStatePolicy: plan.localStatePolicy,
    globalStatePolicy: plan.globalStatePolicy,
    transitionCount: adaptiveTransitions.length,
    passedTransitionCount: passedAdaptiveTransitions.length,
    transitions: adaptiveTransitions,
    axisCoverage: adaptiveAxisCoverage,
    stateBoundary,
    evidence: [
      `signals:${plan.situationSignals.join('+') || 'none'}`,
      `axes:${plan.adaptationAxes.join('+') || 'none'}`,
      `localState:${hasLocalStateEvidence ? 'observed' : 'missing'}`,
      `globalState:${hasGlobalStateEvidence ? 'observed' : 'missing'}`,
      `stateBoundary:${stateBoundary.status}`,
      `stateBoundaryLocal:${stateBoundary.localStateEvidencePaths.length}`,
      `stateBoundaryGlobal:${stateBoundary.globalStateEvidencePaths.length}`,
      `stateBoundaryResults:${stateBoundary.resultStateEvidencePaths.length}`,
      `stateBoundaryComputed:${stateBoundary.computedStateEvidencePaths.length}`,
      `stateBoundaryMixedTransitions:${stateBoundary.mixedLocalGlobalTransitionCount}`,
      `actionFlowTrace:${hasActionFlowTraceEvidence ? 'observed' : 'missing'}`,
      `actionFlowRuntimeProof:${passedActionFlowRuntimeProofs.length}/${requiredScenarioCount}`,
      `adaptiveTransitions:${passedAdaptiveTransitions.length}/${adaptiveTransitions.length}`,
      `adaptiveAxisCoverage:${adaptiveAxisCoverage.passedAxes.length}/${adaptiveAxisCoverage.requiredAxes.length}`,
      `adaptiveAxisMissing:${adaptiveAxisCoverage.missingAxes.join('+') || 'none'}`,
      `designEvidence:${quality.designQualitySourceKind}:${quality.designQualityStatus}`,
      `designStyleSwitch:${quality.designStyleSwitch.status}`,
    ],
    reasons,
  }
}

function buildDesignStyleAdaptiveTransition(
  designStyleSwitch: StudioEndUserDesignStyleSwitchProof,
): StudioEndUserAdaptiveTransitionEvidence {
  const before = designStyleSwitch.basePrimaryBackground ?? ''
  const after = designStyleSwitch.targetPrimaryToken ?? designStyleSwitch.targetPrimaryBackground ?? ''
  const reasons = [
    ...designStyleSwitch.reasons,
    ...(designStyleSwitch.status !== 'pass' ? [`Design style switch proof is ${designStyleSwitch.status}.`] : []),
    ...(designStyleSwitch.structuralDomIdentical !== true ? ['Generated-app DOM structure changed during design style adaptation.'] : []),
    ...(!after ? ['Target primary token evidence is missing for design style adaptation.'] : []),
    ...(designStyleSwitch.appHostHasCompleteTokenAttr !== true ? ['Generated-app render root is missing complete-token attr during design style adaptation.'] : []),
    ...(!designStyleSwitch.targetPrimaryToken ? ['Target --primary token evidence is missing for design style adaptation.'] : []),
    ...(!designStyleSwitch.targetColorPrimaryToken ? ['Target --color-primary token evidence is missing for design style adaptation.'] : []),
    ...(designStyleSwitch.mutationApi !== 'app-design-sandbox' ? ['Design style adaptation did not use the app-design sandbox API.'] : []),
    ...(designStyleSwitch.studioHtmlPrimaryChanged === true ? ['Studio document html primary token changed during design style adaptation.'] : []),
    ...(designStyleSwitch.studioBodyPrimaryChanged === true ? ['Studio document body primary token changed during design style adaptation.'] : []),
    ...(designStyleSwitch.studioHtmlHasCompleteTokenAttr === true ? ['Studio document html has generated-app complete-token attr during design style adaptation.'] : []),
    ...(designStyleSwitch.studioBodyHasCompleteTokenAttr === true ? ['Studio document body has generated-app complete-token attr during design style adaptation.'] : []),
  ]
  return {
    signal: 'user-context',
    axis: 'design-style',
    before,
    after,
    uiProbe: 'default-primary-affordance',
    stateScope: 'tokens.colors.primary+tokens.colors.primaryForeground',
    actionFlow: 'designStyleSwitch',
    status: reasons.length === 0 ? 'pass' : 'fail',
    reasons,
  }
}

export function buildStudioEndUserScenarioMatrix(
  input: Pick<StudioEndUserAppGenerationProofInput, 'app' | 'evidence'>,
  browserEvidence?: StudioEndUserAppBrowserEvidence | null,
): StudioEndUserScenarioMatrix {
  const requiredScenarios = new Set([
    ...inferRequiredEndUserScenarios(input.app, input.evidence),
    ...normalizeEndUserScenarioIds(browserEvidence?.requiredScenarioIds),
  ])
  const explicitEvidence = new Map<StudioEndUserScenarioId, StudioEndUserScenarioBrowserEvidence>()
  for (const scenario of browserEvidence?.scenarioEvidence ?? []) {
    explicitEvidence.set(scenario.id, scenario)
  }

  const scenarios = END_USER_SCENARIO_IDS.map<StudioEndUserScenarioEvidence>((id) => {
    const explicit = explicitEvidence.get(id)
    const required = requiredScenarios.has(id)
    if (!required && !explicit) {
      return {
        id,
        label: END_USER_SCENARIO_LABELS[id],
        status: 'not-requested',
        required,
        signals: [],
        interactions: [],
        stateEvidence: [],
        dataEvidence: [],
        viewportEvidence: [],
        artifactEvidence: [],
        actionFlowTraceEvidence: [],
        actionFlowProof: null,
        adaptiveTransitions: [],
        reasons: ['This generated app did not request this end-user scenario.'],
      }
    }

    const signals = normalizeStringArray(explicit?.signals)
    const interactions = normalizeStringArray(explicit?.interactions)
    const stateEvidence = normalizeStringArray(explicit?.stateEvidence)
    const dataEvidence = normalizeStringArray(explicit?.dataEvidence)
    const viewportEvidence = normalizeStringArray(explicit?.viewportEvidence)
    const artifactEvidence = normalizeStringArray(explicit?.artifactEvidence)
    const actionFlowTraceEvidence = normalizeStringArray(explicit?.actionFlowTraceEvidence)
    const actionFlowProof = normalizeActionFlowRuntimeProof(explicit?.actionFlowProof)
    const adaptiveTransitions = normalizeAdaptiveTransitions(explicit?.adaptiveTransitions)
    const reasons = normalizeStringArray(explicit?.reasons)
    const hasEvidence =
      signals.length > 0 ||
      interactions.length > 0 ||
      stateEvidence.length > 0 ||
      dataEvidence.length > 0 ||
      viewportEvidence.length > 0 ||
      artifactEvidence.length > 0 ||
      actionFlowTraceEvidence.length > 0 ||
      actionFlowProof !== null ||
      adaptiveTransitions.length > 0
    // Fail-closed: a missing harness `status` is never a verdict. Evidence
    // strings alone do not prove a pass — a harness bug that drops the
    // status field must not flip a would-be fail into 'pass'.
    const explicitStatus: StudioEndUserScenarioStatus = explicit?.status ??
      (hasEvidence || required ? 'partial' : 'not-requested')
    if (!explicit?.status && hasEvidence) {
      reasons.push('Scenario evidence is attached but the browser harness did not assert a status.')
    }
    const missingRequiredEvidence = required && explicitStatus === 'pass'
      ? requiredScenarioEvidenceGaps(id, {
          interactions,
          stateEvidence,
          dataEvidence,
          artifactEvidence,
          actionFlowTraceEvidence,
          actionFlowProof,
        })
      : []
    const status: StudioEndUserScenarioStatus = missingRequiredEvidence.length > 0 ? 'fail' : explicitStatus

    return {
      id,
      label: END_USER_SCENARIO_LABELS[id],
      status,
      required,
      signals,
      interactions,
      stateEvidence,
      dataEvidence,
      viewportEvidence,
      artifactEvidence,
      actionFlowTraceEvidence,
      actionFlowProof,
      adaptiveTransitions,
      reasons: reasons.length > 0
        ? reasons
        : status === 'pass'
          ? ['Browser scenario evidence passed.']
          : missingRequiredEvidence.length > 0
            ? missingRequiredEvidence.map((gap) => `${END_USER_SCENARIO_LABELS[id]} requires ${gap} before release.`)
          : required
            ? [`${END_USER_SCENARIO_LABELS[id]} requires browser interaction proof before release.`]
            : ['This scenario has evidence but was not required by the app plan.'],
    }
  })

  const passedScenarios = scenarios
    .filter((scenario) => scenario.required && scenario.status === 'pass')
    .map((scenario) => scenario.id)
  const failingRequired = scenarios.filter((scenario) => (
    scenario.required && scenario.status === 'fail'
  ))
  const partialRequired = scenarios.filter((scenario) => (
    scenario.required && scenario.status !== 'pass' && scenario.status !== 'fail'
  ))
  const explicitFailures = scenarios.filter((scenario) => (
    !scenario.required && scenario.status === 'fail'
  ))
  const reasons = [
    ...failingRequired.flatMap((scenario) => scenario.reasons),
    ...partialRequired.flatMap((scenario) => scenario.reasons),
    ...explicitFailures.flatMap((scenario) => scenario.reasons),
  ]

  return {
    schemaVersion: 'studio-end-user-scenario-matrix.v1',
    status: failingRequired.length > 0 || explicitFailures.length > 0
      ? 'fail'
      : partialRequired.length > 0
        ? 'partial'
        : 'pass',
    requiredScenarios: END_USER_SCENARIO_IDS.filter((id) => requiredScenarios.has(id)),
    passedScenarios,
    scenarios,
    reasons,
  }
}

function normalizeEndUserScenarioIds(values: readonly unknown[] | undefined): StudioEndUserScenarioId[] {
  if (!values) return []
  return END_USER_SCENARIO_IDS.filter((id) => values.includes(id))
}

function requiredScenarioEvidenceGaps(
  id: StudioEndUserScenarioId,
  evidence: Pick<
    StudioEndUserScenarioEvidence,
    'interactions' | 'stateEvidence' | 'dataEvidence' | 'artifactEvidence' | 'actionFlowTraceEvidence' | 'actionFlowProof'
  >,
): string[] {
  const gaps = [
    ...(evidence.interactions.length === 0 ? ['interaction evidence'] : []),
    ...(evidence.stateEvidence.length === 0 ? ['local/global state evidence'] : []),
    ...(evidence.artifactEvidence.length === 0 ? ['rendered artifact evidence'] : []),
    ...(evidence.actionFlowTraceEvidence.length === 0 ? ['ActionFlow trace evidence'] : []),
    ...(evidence.actionFlowProof === null ? ['structured ActionFlow runtime proof'] : []),
    ...(evidence.actionFlowProof && evidence.actionFlowProof.status !== 'pass' ? ['passing ActionFlow runtime proof'] : []),
  ]
  if ((id === 'shopping' || id === 'backoffice-data-table') && evidence.dataEvidence.length === 0) {
    gaps.push('datasource or appData mutation evidence')
  }
  return gaps
}

export function buildStudioReferoOntologyBatchAdmission(
  speed: StudioEndUserAppSpeedEvidence,
  quality: StudioEndUserAppQualityEvidence,
  scenarioMatrix?: StudioEndUserScenarioMatrix | null,
  adaptiveRuntime?: StudioEndUserAdaptiveRuntimeProof | null,
): StudioReferoOntologyBatchAdmission {
  const blockedReasons = [
    ...(speed.status === 'pass' ? [] : speed.reasons),
    ...(quality.status === 'pass' ? [] : quality.reasons),
    ...(adaptiveRuntime && adaptiveRuntime.status === 'partial'
      ? adaptiveRuntime.reasons
      : []),
    ...(!scenarioMatrix
      ? ['End-user scenario matrix proof is missing.']
      : scenarioMatrix.status === 'pass'
        ? []
        : scenarioMatrix.reasons),
  ]
  const canRun = blockedReasons.length === 0

  return {
    schemaVersion: 'studio-refero-ontology-batch-admission.v1',
    status: canRun ? 'open' : 'blocked',
    canRun,
    requiredEvidence: [
      'end-user-generation-speed',
      'post-generation-validation-quality',
      'generated-app-readiness',
      'browser-render-proof',
      'visual-richness-proof',
      'design-sandbox-isolation',
      'design-style-switch-proof',
      'agentic-rag-validation',
      'end-user-scenario-matrix',
      'full-coverage-audit',
      'component-capability-catalog',
      'exhaustive-render-action-proof',
      ...(adaptiveRuntime && adaptiveRuntime.status !== 'missing' ? ['adaptive-runtime-proof'] : []),
      ...(adaptiveRuntime && adaptiveRuntime.status !== 'missing' ? ['state-boundary-proof'] : []),
      'refero-rights-gate',
    ],
    blockedReasons,
    nextAction: canRun
      ? 'Refero ontology batch may enqueue validated design-context candidates; promotion still requires offline/CI PR review.'
      : 'Do not run Refero ontology batch until speed, quality, and required end-user scenario proof all pass.',
  }
}

export function buildStudioGeneratedAppReadinessGate(
  evidence: StudioGenerationEvidence,
): StudioGeneratedAppReadinessGate {
  const imageVerification = evidence.generatedApp.imageEvidenceVerification
  const imageCandidateAdmission = evidence.generatedApp.imageEvidenceCandidateAdmission
  const imageVerificationPublishBlocked =
    imageVerification.enabled && !imageVerification.canPublish
  const blockedReasons = [...new Set([
    ...(!evidence.uiOntology.preflight.canGenerate
      ? evidence.uiOntology.preflight.blockedReasons
      : []),
    ...(evidence.generatedApp.status === 'partial'
      ? evidence.generatedApp.notes
      : []),
    ...(evidence.generatedApp.status === 'pending'
      ? ['Generated app readiness is pending until generated blocks are attached.']
      : []),
    ...(evidence.generatedApp.styleSeparation.status === 'partial'
      ? evidence.generatedApp.styleSeparation.notes
      : []),
    ...(evidence.generatedApp.reportApplicationShell.status === 'partial'
      ? evidence.generatedApp.reportApplicationShell.notes
      : []),
    ...(imageVerification.status === 'blocked'
      ? imageVerification.blockedReasons
      : []),
    ...(imageVerification.enabled && !imageVerification.promotionAllowed
      ? ['Generated UI verification policy requires candidate review before publish promotion.']
      : []),
    ...(imageCandidateAdmission?.status === 'blocked'
      ? imageCandidateAdmission.blockedReasons
      : []),
    ...(imageCandidateAdmission?.status === 'rejected' || imageCandidateAdmission?.status === 'quarantined'
      ? imageCandidateAdmission.blockedReasons
      : []),
  ].filter(Boolean))]
  const status: StudioGeneratedAppReadinessGate['status'] =
    !evidence.uiOntology.preflight.canGenerate || imageVerification.status === 'blocked'
      ? 'blocked'
      : evidence.generatedApp.status
  const renderSupport = evidence.renderEngine.engines

  return {
    scope: 'generated-app',
    status,
    separatesGlobalOntologyPass: true,
    canPreview: status !== 'blocked' && status !== 'pending',
    canPublish: (status === 'pass' || status === 'not-required') && !imageVerificationPublishBlocked,
    requiredEvidence: [...new Set([
      ...(evidence.generatedApp.actionFlowRequired
        ? ['flows', 'eventBindings', 'datasources-or-stateVariables']
        : []),
      'targetSupport',
      'renderEvidence',
      'styleSeparation',
      ...(evidence.generatedApp.reportApplicationShell.applicable ? ['reportApplicationShell'] : []),
    ...(evidence.generatedApp.applicationConformance.required ||
        evidence.generatedApp.applicationConformance.status !== 'missing'
        ? ['applicationConformanceOracle', 'visibleFromJsonOracle', 'measuredDesignQuality']
        : []),
      ...(evidence.generatedApp.visibleFromJson.required ||
        evidence.generatedApp.visibleFromJson.status !== 'missing'
        ? ['visibleFromJsonOracle']
        : []),
      ...(imageVerification.enabled
        ? ['generatedUiVerificationPolicy', ...imageVerification.requiredEvidence]
        : []),
      ...(imageCandidateAdmission
        ? ['imageEvidenceCandidateAdmission', ...imageCandidateAdmission.requiredEvidence]
        : []),
    ])],
    blockedReasons,
    counts: {
      flows: evidence.generatedApp.flowCount,
      eventBindings: evidence.generatedApp.eventBindingCount,
      datasources: evidence.generatedApp.datasourceCount,
      stateVariables: evidence.generatedApp.stateVariableCount,
    },
    targetSupport: {
      web: 'pass',
      rn: renderSupport.rn.missing === 0 ? 'pass' : 'partial',
      flutter: renderSupport.flutter.missing === 0 ? 'pass' : 'partial',
    },
  }
}

export function buildStudioRendererTargetMatrix(
  evidence: StudioGenerationEvidence,
): StudioRendererTargetMatrix {
  const requested = new Set(evidence.uiOntology.runtimeTargets)
  const gate = buildStudioGeneratedAppReadinessGate(evidence)
  const blocked = gate.status === 'blocked'
  const artifactParity = evidence.generatedApp.artifactParity.status

  return {
    schemaVersion: 'studio-renderer-target-matrix.v1',
    selectedTargetPlatform: evidence.uiOntology.targetPlatform,
    requestedRuntimeTargets: evidence.uiOntology.runtimeTargets,
    targets: {
      web: buildRendererTargetMatrixEntry({
        target: 'web',
        requested: requested.has('web') || requested.has('desktop'),
        blocked,
        structuralPass: evidence.renderEngine.engines.web.missing === 0,
        canPreview: gate.canPreview,
        artifactParity,
        native: false,
      }),
      rn: buildRendererTargetMatrixEntry({
        target: 'rn',
        requested: requested.has('rn') || requested.has('mobile'),
        blocked,
        structuralPass: evidence.renderEngine.engines.rn.missing === 0,
        canPreview: gate.canPreview,
        artifactParity,
        native: true,
        nativeProof: evidence.nativeTargetProof.targets.rn,
      }),
      flutter: buildRendererTargetMatrixEntry({
        target: 'flutter',
        requested: requested.has('flutter') || requested.has('mobile'),
        blocked,
        structuralPass: evidence.renderEngine.engines.flutter.missing === 0,
        canPreview: gate.canPreview,
        artifactParity,
        native: true,
        nativeProof: evidence.nativeTargetProof.targets.flutter,
      }),
    },
  }
}

function buildRendererTargetMatrixEntry({
  target,
  requested,
  blocked,
  structuralPass,
  canPreview,
  artifactParity,
  native,
  nativeProof,
}: {
  target: 'web' | 'rn' | 'flutter'
  requested: boolean
  blocked: boolean
  structuralPass: boolean
  canPreview: boolean
  artifactParity: StudioGeneratedAppArtifactParityStatus
  native: boolean
  nativeProof?: StudioNativeTargetProofEntry
}): StudioRendererTargetMatrixEntry {
  if (!requested) {
    return {
      readiness: 'not-requested',
      structuralCoverage: structuralPass ? 'pass' : 'partial',
      r2VisualFaithfulness: 'pending',
      generatedArtifactParity: 'not-applicable',
      nativeRuntimeParity: native ? 'not-applicable' : 'not-applicable',
      nativeLivePackaging: 'not-applicable',
      evidence: [`${target} was not requested in runtimeTargets.`],
      partialReasons: [],
    }
  }

  const nativeRuntimeParity = native
    ? nativeProof && nativeProof.status !== 'missing'
      ? nativeProof.nativeRuntimeParity
      : 'partial'
    : 'not-applicable'
  const nativeLivePackaging = native
    ? nativeProof && nativeProof.status !== 'missing'
      ? nativeProof.nativeLivePackaging
      : 'pending'
    : 'not-applicable'
  const r2VisualFaithfulness = native && nativeProof && nativeProof.status !== 'missing'
    ? nativeProof.r2VisualFaithfulness
    : 'pending'
  const nativeEvidence = native && nativeProof && nativeProof.status !== 'missing'
    ? nativeProof.evidence
    : []
  const nativeProofReasons = native && nativeProof && nativeProof.status !== 'missing'
    ? nativeProof.reasons
    : []
  const partialReasons = [
    ...(!structuralPass ? ['Renderer structural coverage is incomplete.'] : []),
    ...(r2VisualFaithfulness !== 'pass'
      ? nativeProofReasons.length > 0
        ? nativeProofReasons
        : ['R2 visual faithfulness evidence is pending.']
      : []),
    ...(artifactParity === 'pending' ? ['Generated app artifact parity is pending.'] : []),
    ...(artifactParity === 'partial' ? ['Generated app artifact parity is incomplete.'] : []),
    ...(native && nativeRuntimeParity !== 'pass'
      ? nativeProofReasons.length > 0
        ? nativeProofReasons
        : ['Native runtime parity proof is pending.']
      : []),
    ...(native && nativeLivePackaging !== 'pass'
      ? nativeProofReasons.length > 0
        ? nativeProofReasons
        : ['Native live app-loader packaging proof is pending.']
      : []),
    ...(!canPreview ? ['Generated app preview readiness is not yet available.'] : []),
  ]
  const artifactReady = artifactParity === 'pass' || artifactParity === 'not-applicable'
  const readiness: StudioRendererTargetReadiness = blocked
    ? 'blocked'
    : target === 'web' && structuralPass && canPreview && artifactReady
      ? 'ready'
      : native &&
          structuralPass &&
          canPreview &&
          artifactReady &&
          r2VisualFaithfulness === 'pass' &&
          nativeRuntimeParity === 'pass' &&
          nativeLivePackaging === 'pass'
        ? 'ready'
      : 'partial'

  return {
    readiness,
    structuralCoverage: structuralPass ? 'pass' : 'partial',
    r2VisualFaithfulness,
    generatedArtifactParity: artifactParity === 'pass' || artifactParity === 'not-applicable'
      ? 'pass'
      : artifactParity === 'pending'
        ? 'pending'
        : 'partial',
    nativeRuntimeParity,
    nativeLivePackaging,
    evidence: [
      structuralPass ? 'Structural renderer coverage: 190/190.' : 'Structural renderer coverage has gaps.',
      artifactParity === 'pass'
        ? 'Generated app artifact parity: manifest and blocks are target-stable.'
        : target === 'web'
          ? 'Web preview runtime evidence is available in Studio.'
          : 'Native renderer coverage is structural until artifact/runtime proof is supplied.',
      ...(nativeEvidence.length > 0
        ? nativeEvidence
        : native ? ['Native live app-loader packaging proof is pending.'] : []),
    ],
    partialReasons: [...new Set(partialReasons)],
  }
}

export function attachStudioGenerationEvidenceToBlock<T>(
  block: T,
  evidence: StudioGenerationEvidence,
): T {
  const record = readRecord(block)
  if (!record) return block

  const blockEvidence = withGeneratedAppReadiness(evidence, record)
  const baseMeta = {
    ...(readRecord(record.metadata) ?? {}),
    ...(readRecord(record.meta) ?? {}),
  }
  const meta = mergeStudioGenerationEvidenceMeta(baseMeta, blockEvidence)
  return {
    ...record,
    meta,
    metadata: meta,
  } as T
}

export function withSharedGlobalStateFromAppSpec<T>(
  app: T,
  appSpec: { globalState?: unknown } | null | undefined,
): T {
  const record = readRecord(app)
  if (!record) return app
  const specState = readRecord(appSpec?.globalState)
  if (!specState || Object.keys(specState).length === 0) return app
  const manifest = readRecord(record.manifest) ?? {}
  const existingManifest = readRecord(manifest.globalState)
  const existingTop = readRecord(record.globalState)
  // An empty `appData: {}` envelope is nonempty JSON but not countable
  // shared-state evidence. Only skip when the gate consumer can already
  // see at least one global symbol.
  const existingCountable =
    countScopedStateVariables(existingManifest, 'global') +
    countScopedStateVariables(existingTop, 'global')
  if (existingCountable > 0) {
    return app
  }
  return {
    ...record,
    manifest: {
      ...manifest,
      globalState: specState,
    },
  } as T
}

export function attachStudioGenerationEvidenceToApp<T>(
  app: T,
  evidence: StudioGenerationEvidence,
): T {
  const record = readRecord(app)
  if (!record) return app
  const normalizedRecord = shouldPreserveReferenceVisualShell(record)
    ? record
    : normalizeGeneratedAppStyleAuthority(record)
  // The app IS a generated document — everything dug out of it (including
  // its top-level keys) is app-document provenance and arrives capped.
  const sourceFullCoverage = buildStudioFullCoverageEvidence(normalizedRecord as StudioGenerationEvidenceSource, { documentOnly: true })
  const sourceExhaustiveProof = buildStudioExhaustiveProofEvidence(normalizedRecord as StudioGenerationEvidenceSource, { documentOnly: true })
  // A document-carried audit may upgrade missing/own-tier evidence for
  // visibility, but must never displace a studio-input summary.
  const adoptDocumentSummary = <S extends { status: StudioFullCoverageAuditStatus; provenance?: StudioEvidenceAuditProvenance }>(
    existing: S,
    incoming: S,
  ): S => {
    if (incoming.status === 'missing') return existing
    if (existing.provenance === 'studio-input' && existing.status !== 'missing') return existing
    return incoming
  }
  const evidenceWithSourceCoverage = {
    ...evidence,
    fullCoverage: adoptDocumentSummary(evidence.fullCoverage, sourceFullCoverage),
  }
  const evidenceWithSourceProof = {
    ...evidenceWithSourceCoverage,
    exhaustiveProof: adoptDocumentSummary(evidence.exhaustiveProof, sourceExhaustiveProof),
  }
  const appEvidence = withGeneratedAppReadiness(evidenceWithSourceProof, normalizedRecord)

  const blocks = readRecord(normalizedRecord.blocks)
  const nextBlocks = blocks
    ? Object.fromEntries(
        Object.entries(blocks).map(([name, block]) => [
          name,
          attachStudioGenerationEvidenceToBlock(block, appEvidence),
        ]),
      )
    : blocks
  const metadata = mergeStudioGenerationEvidenceMeta(normalizedRecord.metadata, appEvidence)
  const manifest = readRecord(normalizedRecord.manifest)
  const manifestEvidenceMeta = manifest
    ? mergeStudioGenerationEvidenceMeta(manifest, appEvidence)
    : null

  return {
    ...normalizedRecord,
    metadata,
    ...(manifest
      ? {
          manifest: {
            ...manifest,
            generationEvidence: appEvidence,
            renderEngineRegistrySummary: manifestEvidenceMeta?.renderEngineRegistrySummary ?? manifest.renderEngineRegistrySummary,
            actionflowContractSummary: manifestEvidenceMeta?.actionflowContractSummary ?? manifest.actionflowContractSummary,
            fullCoverageAudit: manifestEvidenceMeta?.fullCoverageAudit ?? manifest.fullCoverageAudit,
            exhaustiveProofAudit: manifestEvidenceMeta?.exhaustiveProofAudit ?? manifest.exhaustiveProofAudit,
            productionEvidenceChain: manifestEvidenceMeta?.productionEvidenceChain ?? manifest.productionEvidenceChain,
            applicationConformanceOracle: manifestEvidenceMeta?.applicationConformanceOracle ?? manifest.applicationConformanceOracle,
            uiOntologyGenerationConstraints: manifestEvidenceMeta?.uiOntologyGenerationConstraints ?? manifest.uiOntologyGenerationConstraints,
            uiOntologyConformance: manifestEvidenceMeta?.uiOntologyConformance ?? manifest.uiOntologyConformance,
          },
        }
      : {}),
    ...(nextBlocks ? { blocks: nextBlocks } : {}),
  } as T
}

const GENERATED_STYLE_TOKEN_REF = /^(?:ctok|uiox):[A-Za-z0-9:_-]+$/
const GENERATED_STYLE_RAW_VALUE =
  /#[0-9a-fA-F]{3,8}\b|\brgba?\(|\bhsla?\(|\boklch\(|\blab\(|\blch\(|\bvar\(--|\b(?:linear|radial|conic)-gradient\(/
const GENERATED_STYLE_TEXT_LAYOUT_TOKENS = new Set([
  'text-left', 'text-center', 'text-right', 'text-justify', 'text-start', 'text-end',
  'text-balance', 'text-pretty', 'text-wrap', 'text-nowrap', 'text-ellipsis', 'text-clip',
])
const GENERATED_STYLE_VISUAL_CLASS_PREFIXES = [
  'accent', 'bg', 'border', 'caret', 'decoration', 'divide', 'drop-shadow', 'fill',
  'font', 'from', 'gradient', 'leading', 'opacity', 'outline', 'placeholder', 'ring',
  'rounded', 'shadow', 'stroke', 'text', 'to', 'tracking', 'underline', 'via',
]

function shouldPreserveReferenceVisualShell(record: Record<string, unknown>): boolean {
  const manifest = readRecord(record.manifest)
  const metadata = readRecord(record.metadata)
  const meta = readRecord(record.meta)
  const generationEvidence = readRecord(metadata?.generationEvidence ?? manifest?.generationEvidence)
  const agenticRag = readRecord(
    metadata?.agenticRag ??
    meta?.agenticRag ??
    manifest?.agenticRag ??
    readRecord(generationEvidence?.generatedApp)?.agenticRag,
  )
  const appId = readString(manifest?.appId ?? record.appId)
  const mode = readString(agenticRag?.mode)
  const reference = readString(agenticRag?.reference)
  const source = readString(agenticRag?.source)
  const sourceRevision = readString(manifest?.sourceRevision ?? record.sourceRevision)
  return Boolean(
    appId === 'sipass.app' ||
      mode === 'exact' ||
      sourceRevision ||
      /\bhosted-reference\b/i.test(source) ||
      /\bsipass\.app\b/i.test(reference),
  )
}

function normalizeGeneratedAppStyleAuthority(record: Record<string, unknown>): Record<string, unknown> {
  const blocks = readRecord(record.blocks)
  if (!blocks) return normalizeGeneratedBlockStyleAuthority(record) as Record<string, unknown>
  return {
    ...record,
    blocks: Object.fromEntries(
      Object.entries(blocks).map(([blockId, block]) => [blockId, normalizeGeneratedBlockStyleAuthority(block)]),
    ),
  }
}

function normalizeGeneratedBlockStyleAuthority(block: unknown): unknown {
  const blockRecord = readRecord(block)
  if (!blockRecord) return block
  const ui = readRecord(blockRecord.ui)
  const uiRoot = readRecord(ui?.root)
  const root = readRecord(blockRecord.root)
  if (!uiRoot && !root) return block
  const next: Record<string, unknown> = { ...blockRecord }
  if (ui && uiRoot) next.ui = { ...ui, root: normalizeGeneratedStyleNode(uiRoot) }
  if (root) next.root = normalizeGeneratedStyleNode(root)
  return next
}

function normalizeGeneratedStyleNode(node: Record<string, unknown>): Record<string, unknown> {
  const next: Record<string, unknown> = { ...node }
  // STYLE_SEPARATION_GENERATOR_CONTRACT.inlineStylePolicy.allowed = false
  if ('style' in next) delete next.style
  const type = readString(next.type)
  if (type) {
    if (!hasGeneratedNodeDesignAuthority(next)) next.designRole = generatedDesignRole(type, readString(next.id))
    next.designClass = normalizeGeneratedDesignClass(next.designClass, type)
    const className = normalizeGeneratedClassName(next.className)
    if (className) next.className = className
    else delete next.className
  }
  const children = readArray(next.children)
  if (children) {
    next.children = children.map((child) => {
      const childRecord = readRecord(child)
      return childRecord ? normalizeGeneratedStyleNode(childRecord) : child
    })
  }
  return next
}

function generatedDesignRole(type: string, id: string): string {
  const typePart = sanitizeGeneratedDesignPart(type) || 'node'
  const idPart = sanitizeGeneratedDesignPart(id)
  return idPart ? `generated-${typePart}-${idPart}` : `generated-${typePart}`
}

function normalizeGeneratedDesignClass(value: unknown, type: string): string {
  const structural = filterGeneratedClassTokens(typeof value === 'string' ? value : '')
  const hasDesignHandle = structural.some((token) => token.startsWith('@ds:') || GENERATED_STYLE_TOKEN_REF.test(token))
  return [
    ...(hasDesignHandle ? [] : [`@ds:generated.${sanitizeGeneratedDesignPart(type) || 'node'}`]),
    ...structural,
  ].join(' ')
}

// `#{…}` spans inside a className are EXPRESSIONS, not class tokens. Splitting
// the whole string on whitespace tore them apart: a `dark:bg-chart-1/30'}` tail
// matched the `bg-` visual prefix and was dropped — taking the expression's
// closing brace with it and shipping an UNCLOSED expression (measured
// 2026-07-30, r3 rebuild: 1× structural `expr-unclosed` on
// branch-result-list). Expression spans are preserved whole; only the literal
// remainder between them is style-filtered.
// Split a className into literal tokens plus whole `#{…}` spans. An `#{` with
// no closing brace makes the rest of the string one span — that input is
// already malformed, and re-splitting it would only shed more of it.
function splitGeneratedClassParts(value: string): Array<{ token: string; expression: boolean }> {
  const parts: Array<{ token: string; expression: boolean }> = []
  let cursor = 0
  while (cursor < value.length) {
    const open = value.indexOf('#{', cursor)
    const literal = open === -1 ? value.slice(cursor) : value.slice(cursor, open)
    for (const token of literal.trim().split(/\s+/).filter(Boolean)) {
      parts.push({ token, expression: false })
    }
    if (open === -1) break
    const close = value.indexOf('}', open)
    parts.push({
      token: close === -1 ? value.slice(open) : value.slice(open, close + 1),
      expression: true,
    })
    if (close === -1) break
    cursor = close + 1
  }
  return parts
}

function filterGeneratedClassTokens(value: string): string[] {
  return splitGeneratedClassParts(value)
    .filter((part) => part.expression || !isGeneratedVisualOrRawClassToken(part.token))
    .map((part) => part.token)
}

function normalizeGeneratedClassName(value: unknown): string {
  if (typeof value !== 'string') return ''
  return filterGeneratedClassTokens(value).join(' ')
}

function isGeneratedVisualOrRawClassToken(token: string): boolean {
  if (token.startsWith('@ds:') || GENERATED_STYLE_TOKEN_REF.test(token)) return false
  if (GENERATED_STYLE_RAW_VALUE.test(token) || isGeneratedVisualClassToken(token)) return true
  // Same consumer as the publish gate: assessBlockStyleSeparation (countRawStyleLeaks).
  const report = assessBlockStyleSeparation(
    {
      ui: {
        root: {
          id: 'style-token-probe',
          type: 'container',
          designRole: 'probe',
          className: token,
        },
      },
    },
    { strict: true, requireDesignAuthority: true },
  )
  return report.findings.some(
    (finding) => finding.rule === 'raw-style-value' || finding.rule === 'visual-classname-token',
  )
}

function isGeneratedVisualClassToken(token: string): boolean {
  const base = stripGeneratedVariantPrefixes(token)
  if (!base || GENERATED_STYLE_TEXT_LAYOUT_TOKENS.has(base)) return false
  if (base === 'border' || base === 'shadow' || base === 'rounded' || base === 'underline') return true
  return GENERATED_STYLE_VISUAL_CLASS_PREFIXES.some((prefix) => base === prefix || base.startsWith(`${prefix}-`))
}

function stripGeneratedVariantPrefixes(token: string): string {
  return token.includes(':') ? token.slice(token.lastIndexOf(':') + 1) : token
}

function hasGeneratedNodeDesignAuthority(node: Record<string, unknown>): boolean {
  for (const field of ['designRole', 'designSystemRole', 'surfaceRole', 'styleRole', 'styleScope', 'styleRef']) {
    if (readString(node[field])) return true
  }
  if (hasNonEmptyObject(node.design) || hasNonEmptyObject(node.tokens) || hasNonEmptyObject(node.designTokens)) return true
  for (const field of ['className', 'designClass']) {
    const value = node[field]
    if (typeof value === 'string') {
      const tokens = value.trim().split(/\s+/).filter(Boolean)
      if (tokens.some((token) => token.startsWith('@ds:') || GENERATED_STYLE_TOKEN_REF.test(token))) return true
    }
  }
  const attrs = readRecord(node.dataAttributes)
  if (attrs) {
    return Object.entries(attrs).some(([key, value]) => {
      const normalized = key.replace(/^data-/, '').replace(/-/g, '').toLowerCase()
      return (
        normalized.includes('cogodesign') ||
        normalized.includes('cogosurface') ||
        normalized.includes('designsystem') ||
        normalized.includes('stylerole')
      ) && readString(value).length > 0
    })
  }
  return false
}

function hasNonEmptyObject(value: unknown): boolean {
  const record = readRecord(value)
  return Boolean(record && Object.keys(record).length > 0)
}

function sanitizeGeneratedDesignPart(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40)
}

export function buildUiConformancePanelFromStudioGenerationEvidence(
  evidence: StudioGenerationEvidence,
): UiConformancePanelModel {
  const render = evidence.renderEngine
  const actionflow = evidence.actionflow
  const ui = evidence.uiOntology
  const preflight = ui.preflight
  const generatedApp = evidence.generatedApp
  const targetMatrix = buildStudioRendererTargetMatrix(evidence)

  const capabilityItems: UiConformancePanelItem[] = [
    {
      id: 'studio-render-engine-parity',
      code: 'render-engine-parity',
      severity: 'info',
      message:
        `Renderer coverage: web ${render.engines.web.bound}/${render.denominator}, ` +
        `RN ${render.engines.rn.bound}/${render.denominator}, ` +
        `Flutter ${render.engines.flutter.bound}/${render.denominator} ` +
        `(${render.engines.flutter.direct} direct + ${render.engines.flutter.subpart} sub-part); drift ${render.drift}.`,
      studioConformanceType: 'renderer-evidence',
      generatorImpact: 'all-runtime-targets-covered',
      suggestedAction: 'No generator fallback is required for renderer coverage.',
      bindings: {
        denominator: String(render.denominator),
        fullyCovered: String(render.fullyCovered),
      },
    },
    {
      id: 'studio-renderer-target-matrix',
      code: 'renderer-target-matrix',
      severity: Object.values(targetMatrix.targets).some((target) => target.readiness === 'blocked')
        ? 'error'
        : Object.values(targetMatrix.targets).some((target) => target.readiness === 'partial')
          ? 'warning'
          : 'info',
      message:
        `Target matrix: Web ${targetMatrix.targets.web.readiness}, ` +
        `RN ${targetMatrix.targets.rn.readiness}, ` +
        `Flutter ${targetMatrix.targets.flutter.readiness}. ` +
        `R2 visual faithfulness remains ${targetMatrix.targets.web.r2VisualFaithfulness}; ` +
        `native live packaging RN=${targetMatrix.targets.rn.nativeLivePackaging}, ` +
        `Flutter=${targetMatrix.targets.flutter.nativeLivePackaging}.`,
      studioConformanceType: 'renderer-target-matrix',
      generatorImpact: 'target-readiness-routing',
      suggestedAction: 'Use web preview evidence for web readiness and keep native targets partial until artifact/runtime proof is attached.',
      bindings: {
        targetPlatform: targetMatrix.selectedTargetPlatform,
        runtimeTargets: targetMatrix.requestedRuntimeTargets.join('+'),
        web: targetMatrix.targets.web.readiness,
        rn: targetMatrix.targets.rn.readiness,
        flutter: targetMatrix.targets.flutter.readiness,
        rnNativeLivePackaging: targetMatrix.targets.rn.nativeLivePackaging,
        flutterNativeLivePackaging: targetMatrix.targets.flutter.nativeLivePackaging,
      },
    },
  ]

  const generatorItems: UiConformancePanelItem[] = [
    {
      id: 'studio-ui-ontology-compose-plan',
      code: 'ui-ontology-compose-plan',
      severity: preflight.canGenerate
        ? preflight.mode === 'llm-fallback' ? 'warning' : 'info'
        : 'error',
      message:
        `Preflight ${preflight.canGenerate ? 'allows' : 'blocks'} generation; ` +
        `mode=${preflight.mode}; ragCandidates=${preflight.ragCandidates.length}; ` +
        `bindPlans=${preflight.bindPlan.length}; fallbackCandidates=${preflight.fallbackCandidateFacts.length}.`,
      studioConformanceType: 'generator-constraint',
      generatorImpact: preflight.canGenerate
        ? preflight.mode === 'llm-fallback' ? 'llm-fallback-visible' : 'retrieve-bind-assemble'
        : 'generation-blocked-before-llm',
      suggestedAction: preflight.canGenerate
        ? preflight.fallbackCandidateFacts[0]?.message ?? 'Use the deterministic RAG candidate and bind plan as the first generation contract.'
        : preflight.blockedReasons.join(' ') || 'Fix the blocked ontology preflight reasons before generation.',
      bindings: {
        mode: preflight.mode,
        canGenerate: String(preflight.canGenerate),
        deterministicCandidates: String(preflight.deterministicCandidateCount),
      },
    },
    {
      id: 'studio-ui-ontology-target',
      code: 'ui-ontology-target',
      severity: 'info',
      message:
        `UI ontology generation is ${ui.mode}; targetPlatform=${ui.targetPlatform}; ` +
        `runtimeTargets=${ui.runtimeTargets.join('+')}; Flutter blank element count ${ui.rendererBlankTypes}.`,
      studioConformanceType: 'generator-constraint',
      generatorImpact: 'ontology-driven',
      suggestedAction: 'Keep the generation request on this ontology target contract.',
      bindings: {
        targetPlatform: ui.targetPlatform,
        runtimeTargets: ui.runtimeTargets.join('+'),
      },
    },
    {
      id: 'studio-actionflow-runtime-contract',
      code: 'actionflow-runtime-contract',
      severity: 'info',
      message:
        `ActionFlow ontology is runtime-honored; TBox parameter drift ${actionflow.tboxParamDrift}; ` +
        `long-tail param capabilities ${actionflow.longTailParamCapabilities}.`,
      studioConformanceType: 'generator-constraint',
      generatorImpact: 'runtime-honored-actionflow',
      suggestedAction: 'Generate and validate flows against the runtime-honored ActionFlow surface.',
      bindings: {
        executorSurface: actionflow.executorSurface,
        tboxParamDrift: String(actionflow.tboxParamDrift),
      },
    },
    {
      id: 'studio-full-coverage-audit',
      code: 'full-coverage-audit',
      severity: evidence.fullCoverage.status === 'pass'
        ? 'info'
        : evidence.fullCoverage.status === 'partial'
          ? 'warning'
          : 'error',
      message:
        `Full coverage audit is ${evidence.fullCoverage.status}; ` +
        `components=${evidence.fullCoverage.componentOntologyCount}; ` +
        `actions=${evidence.fullCoverage.actionOntologyCount}; ` +
        `renderer=${evidence.fullCoverage.rendererFullyCoveredCount}/190; ` +
        `capabilities=${evidence.fullCoverage.componentCapabilityCatalogCount}/${evidence.fullCoverage.componentCapabilityDenominator}; ` +
        `styles=${evidence.fullCoverage.bundledStyleCount}; ` +
        `FlatBuffer mapped=${evidence.fullCoverage.flatbufferMappedComponentCount}/190; ` +
        `blockingFindings=${evidence.fullCoverage.blockingFindingCount}.`,
      studioConformanceType: 'generator-constraint',
      generatorImpact: evidence.fullCoverage.status === 'pass'
        ? 'full-surface-covered'
        : 'full-surface-proof-required',
      suggestedAction: evidence.fullCoverage.findings[0]?.message ??
        'Attach a passing headless full coverage audit before accepting all-component/all-action/all-style proof.',
      bindings: {
        status: evidence.fullCoverage.status,
        uiElements: evidence.fullCoverage.sectionStatuses.uiElements,
        renderers: evidence.fullCoverage.sectionStatuses.renderers,
        componentCapabilities: evidence.fullCoverage.sectionStatuses.componentCapabilities,
        componentCapabilityCatalogCount: String(evidence.fullCoverage.componentCapabilityCatalogCount),
        componentCapabilityDenominator: String(evidence.fullCoverage.componentCapabilityDenominator),
        styleSeparation: evidence.fullCoverage.sectionStatuses.styleSeparation,
        actions: evidence.fullCoverage.sectionStatuses.actions,
        flatbuffer: evidence.fullCoverage.sectionStatuses.flatbuffer,
        blockingFindingCount: String(evidence.fullCoverage.blockingFindingCount),
        firstFinding: evidence.fullCoverage.findings[0]?.code ?? 'none',
      },
    },
    {
      id: 'studio-component-capability-catalog',
      code: 'component-capability-catalog',
      severity: evidence.fullCoverage.sectionStatuses.componentCapabilities === 'pass'
        ? 'info'
        : evidence.fullCoverage.sectionStatuses.componentCapabilities === 'partial'
          ? 'warning'
          : 'error',
      message:
        `Component capability catalog is ${evidence.fullCoverage.sectionStatuses.componentCapabilities}; ` +
        `components=${evidence.fullCoverage.componentCapabilityCatalogCount}/${evidence.fullCoverage.componentCapabilityDenominator}; ` +
        `web=${evidence.fullCoverage.componentCapabilityPlatformCoverage.webSupported}; ` +
        `RN=${evidence.fullCoverage.componentCapabilityPlatformCoverage.rnSupported}; ` +
        `Flutter=${evidence.fullCoverage.componentCapabilityPlatformCoverage.flutterSupported}; ` +
        `RenderPack=${evidence.fullCoverage.componentCapabilityPlatformCoverage.renderPackComponents}; ` +
        `highRisk=${evidence.fullCoverage.componentCapabilityHighRiskComponents.join(',') || 'none'}.`,
      studioConformanceType: 'generator-constraint',
      generatorImpact: evidence.fullCoverage.sectionStatuses.componentCapabilities === 'pass'
        ? 'component-capabilities-grounded'
        : 'component-capabilities-required',
      suggestedAction:
        evidence.fullCoverage.sectionStatuses.componentCapabilities === 'pass'
          ? 'Use the component capability catalog as the generation contract for component props, slots, events, runtime features, and target platform support.'
          : 'Attach a passing component capability catalog before accepting all-component generation proof.',
      bindings: {
        status: evidence.fullCoverage.sectionStatuses.componentCapabilities,
        source: 'packages/cogo-validator/ontology/component-capabilities.json',
        denominator: String(evidence.fullCoverage.componentCapabilityDenominator),
        componentCount: String(evidence.fullCoverage.componentCapabilityCatalogCount),
        webSupported: String(evidence.fullCoverage.componentCapabilityPlatformCoverage.webSupported),
        rnSupported: String(evidence.fullCoverage.componentCapabilityPlatformCoverage.rnSupported),
        flutterSupported: String(evidence.fullCoverage.componentCapabilityPlatformCoverage.flutterSupported),
        renderPackComponents: String(evidence.fullCoverage.componentCapabilityPlatformCoverage.renderPackComponents),
        highRiskComponents: evidence.fullCoverage.componentCapabilityHighRiskComponents.join(','),
      },
    },
    {
      id: 'studio-exhaustive-proof-audit',
      code: 'exhaustive-proof-audit',
      severity: evidence.exhaustiveProof.status === 'pass'
        ? 'info'
        : evidence.exhaustiveProof.status === 'partial'
          ? 'warning'
          : 'error',
      message:
        `Exhaustive proof audit is ${evidence.exhaustiveProof.status}; ` +
        `contractProbes=${evidence.exhaustiveProof.contractProbeCount}; ` +
        `renderPackProbes=${evidence.exhaustiveProof.renderPackProbeCount}; ` +
        `SSR=${evidence.exhaustiveProof.ssrRenderedComponentCount}/${evidence.exhaustiveProof.ontologyComponentCount}; ` +
        `soloSSR=${evidence.exhaustiveProof.soloSsrRenderedComponentCount}; ` +
        `compoundSSR=${evidence.exhaustiveProof.compoundSsrRenderedComponentCount}; ` +
        `providerSlotProof=${evidence.exhaustiveProof.providerSlotHydrationProofStatus}; ` +
        `providerSlotsHydrated=${evidence.exhaustiveProof.providerSlotHydratedComponentCount}; ` +
        `providerSlotsRequired=${evidence.exhaustiveProof.providerSlotHydrationRequiredComponentCount}; ` +
        `styleCollapsed=${evidence.exhaustiveProof.collapsedStyleSelectors.join(',') || 'none'}; ` +
        `actionsProven=${evidence.exhaustiveProof.runtimeProvenActionCount}/${evidence.exhaustiveProof.runtimeHonoredActionCount}; ` +
        `actionsPureExecuted=${evidence.exhaustiveProof.runtimeExecutedActionCount}; ` +
        `actionsAdapterProven=${evidence.exhaustiveProof.runtimeAdapterProvenActionCount}; ` +
        `actionsAdapterRequired=${evidence.exhaustiveProof.runtimeAdapterRequiredActionCount}; ` +
        `actionsMissing=${evidence.exhaustiveProof.runtimeNotExecutedActionCount}.`,
      studioConformanceType: 'generator-constraint',
      generatorImpact: evidence.exhaustiveProof.status === 'pass'
        ? 'actual-render-runtime-proof'
        : 'actual-render-runtime-proof-required',
      suggestedAction: evidence.exhaustiveProof.findings[0]?.message ??
        'Attach a passing exhaustive proof audit before claiming end-user application generation proof.',
      bindings: {
        status: evidence.exhaustiveProof.status,
        ssrRenderedComponentCount: String(evidence.exhaustiveProof.ssrRenderedComponentCount),
        ssrMissingComponentCount: String(evidence.exhaustiveProof.ssrMissingComponentCount),
        providerSlotHydrationProofStatus: evidence.exhaustiveProof.providerSlotHydrationProofStatus,
        providerSlotHydratedComponentCount: String(evidence.exhaustiveProof.providerSlotHydratedComponentCount),
        providerSlotHydrationRequiredComponentCount: String(evidence.exhaustiveProof.providerSlotHydrationRequiredComponentCount),
        runtimeExecutedActionCount: String(evidence.exhaustiveProof.runtimeExecutedActionCount),
        runtimeAdapterProvenActionCount: String(evidence.exhaustiveProof.runtimeAdapterProvenActionCount),
        runtimeProvenActionCount: String(evidence.exhaustiveProof.runtimeProvenActionCount),
        runtimeAdapterRequiredActionCount: String(evidence.exhaustiveProof.runtimeAdapterRequiredActionCount),
        runtimeNotExecutedActionCount: String(evidence.exhaustiveProof.runtimeNotExecutedActionCount),
        blockingFindingCount: String(evidence.exhaustiveProof.blockingFindingCount),
        partialFindingCount: String(evidence.exhaustiveProof.partialFindingCount),
        firstFinding: evidence.exhaustiveProof.findings[0]?.code ?? 'none',
      },
    },
    {
      id: 'studio-generated-app-readiness',
      code: 'generated-app-readiness',
      // 'pending' = nothing validated yet — must not read as a green item.
      severity: generatedApp.status === 'partial' || generatedApp.status === 'pending' ? 'warning' : 'info',
      message:
        `Generated app readiness is ${generatedApp.status}; appClass=${generatedApp.appClass}; ` +
        `flows=${generatedApp.flowCount}; eventBindings=${generatedApp.eventBindingCount}; ` +
        `datasources=${generatedApp.datasourceCount}; stateVariables=${generatedApp.stateVariableCount}; ` +
        `localState=${generatedApp.localStateVariableCount}; globalState=${generatedApp.globalStateVariableCount}; ` +
        `stateIsolation=${generatedApp.stateIsolation}; ` +
        `artifactParity=${generatedApp.artifactParity.status}; ` +
        `artifactBlocks=${generatedApp.artifactParity.blockCount}; artifactPages=${generatedApp.artifactParity.pageCount}; ` +
        `styleSeparation=${generatedApp.styleSeparation.status}; ` +
        `reportApplicationShell=${generatedApp.reportApplicationShell.status}.`,
      studioConformanceType: 'generated-app-evidence',
      generatorImpact: generatedApp.status === 'partial' || generatedApp.status === 'pending'
        ? 'not-ready'
        : 'readiness-evidence',
      suggestedAction: generatedApp.notes.join(' ') || 'Keep this evidence attached to the generated app.',
      bindings: {
        status: generatedApp.status,
        appClass: generatedApp.appClass,
        flowCount: String(generatedApp.flowCount),
        eventBindingCount: String(generatedApp.eventBindingCount),
        localStateVariableCount: String(generatedApp.localStateVariableCount),
        globalStateVariableCount: String(generatedApp.globalStateVariableCount),
        stateIsolation: generatedApp.stateIsolation,
        artifactParity: generatedApp.artifactParity.status,
        artifactBlockCount: String(generatedApp.artifactParity.blockCount),
        artifactPageCount: String(generatedApp.artifactParity.pageCount),
        styleSeparation: generatedApp.styleSeparation.status,
        styleSeparationFindingCount: String(generatedApp.styleSeparation.findingCount),
        reportApplicationShell: generatedApp.reportApplicationShell.status,
        reportApplicationShellFindingCount: String(generatedApp.reportApplicationShell.findingCount),
      },
    },
    {
      id: 'studio-application-conformance-oracle',
      code: 'application-conformance-oracle',
      severity: generatedApp.applicationConformance.status === 'blocked' ||
        (generatedApp.applicationConformance.status === 'missing' && generatedApp.applicationConformance.required)
        ? 'warning'
        : 'info',
      message:
        `Application conformance oracle is ${generatedApp.applicationConformance.status}; ` +
        `required=${generatedApp.applicationConformance.required}; ` +
        `blocks=${generatedApp.applicationConformance.blockCount}; ` +
        `dimensions=${generatedApp.applicationConformance.dimensions.length}; ` +
        `blockedReasons=${generatedApp.applicationConformance.blockedReasons.length}.`,
      studioConformanceType: 'generated-app-evidence',
      generatorImpact: generatedApp.applicationConformance.status === 'pass'
        ? 'readiness-evidence'
        : generatedApp.applicationConformance.required
          ? 'not-ready'
          : 'readiness-evidence',
      suggestedAction: generatedApp.applicationConformance.notes.join(' ') ||
        'Keep the ApplicationCompositionPlan conformance oracle attached to ontology-backed generated apps.',
      bindings: {
        status: generatedApp.applicationConformance.status,
        required: String(generatedApp.applicationConformance.required),
        blockCount: String(generatedApp.applicationConformance.blockCount),
        checkedBlockCount: String(generatedApp.applicationConformance.checkedBlockNames.length),
        dimensionCount: String(generatedApp.applicationConformance.dimensions.length),
        blockedReasonCount: String(generatedApp.applicationConformance.blockedReasons.length),
        firstBlockedReason: generatedApp.applicationConformance.blockedReasons[0] ?? 'none',
      },
    },
    {
      id: 'studio-generated-app-style-separation',
      code: 'generated-app-style-separation',
      severity: generatedApp.styleSeparation.status === 'partial'
        ? 'warning'
        : generatedApp.styleSeparation.status === 'pending'
          ? 'warning'
          : 'info',
      message:
        `Generated app style separation is ${generatedApp.styleSeparation.status}; ` +
        `checkedBlocks=${generatedApp.styleSeparation.checkedBlockCount}; ` +
        `blockedBlocks=${generatedApp.styleSeparation.blockedBlockCount}; ` +
        `visualClassNameTokens=${generatedApp.styleSeparation.visualClassNameTokenCount}; ` +
        `inlineStyleObjects=${generatedApp.styleSeparation.inlineStyleObjectCount}; ` +
        `unresolvedDesignAuthority=${generatedApp.styleSeparation.unresolvedDesignAuthorityCount}.`,
      studioConformanceType: 'generated-app-evidence',
      generatorImpact: generatedApp.styleSeparation.status === 'pass'
        ? 'readiness-evidence'
        : 'not-ready',
      suggestedAction: generatedApp.styleSeparation.notes.join(' ') ||
        'Keep UI structure and injected design-system styles separated.',
      bindings: {
        status: generatedApp.styleSeparation.status,
        checkedBlockCount: String(generatedApp.styleSeparation.checkedBlockCount),
        blockedBlockCount: String(generatedApp.styleSeparation.blockedBlockCount),
        findingCount: String(generatedApp.styleSeparation.findingCount),
        visualClassNameTokenCount: String(generatedApp.styleSeparation.visualClassNameTokenCount),
        rawStyleValueCount: String(generatedApp.styleSeparation.rawStyleValueCount),
        inlineStyleObjectCount: String(generatedApp.styleSeparation.inlineStyleObjectCount),
        inlineVisualStyleCount: String(generatedApp.styleSeparation.inlineVisualStyleCount),
        unresolvedDesignAuthorityCount: String(generatedApp.styleSeparation.unresolvedDesignAuthorityCount),
      },
    },
    {
      id: 'studio-generated-app-report-application-shell',
      code: 'generated-app-report-application-shell',
      severity: generatedApp.reportApplicationShell.status === 'partial'
        ? 'warning'
        : generatedApp.reportApplicationShell.status === 'pending'
          ? 'warning'
          : 'info',
      message:
        `Generated report application shell is ${generatedApp.reportApplicationShell.status}; ` +
        `applicable=${generatedApp.reportApplicationShell.applicable}; ` +
        `globalHeaders=${generatedApp.reportApplicationShell.summary.globalCommandHeaderCount}; ` +
        `chatInputs=${generatedApp.reportApplicationShell.summary.chatCommandInputCount}; ` +
        `headerControls=${generatedApp.reportApplicationShell.summary.reportCommonControlsInHeaderCount}; ` +
        `collapsibleSidebars=${generatedApp.reportApplicationShell.summary.collapsibleSidebarCount}; ` +
        `fullHeightSidebars=${generatedApp.reportApplicationShell.summary.fullHeightSidebarCount}; ` +
        `a4Canvases=${generatedApp.reportApplicationShell.summary.a4PortraitCanvasCount}; ` +
        `narratives=${generatedApp.reportApplicationShell.summary.reportNarrativeCount}; ` +
        `charts=${generatedApp.reportApplicationShell.summary.reportChartSurfaceCount}; ` +
        `tables=${generatedApp.reportApplicationShell.summary.reportDataTableCount}.`,
      studioConformanceType: 'generated-app-evidence',
      generatorImpact: generatedApp.reportApplicationShell.status === 'pass' ||
        generatedApp.reportApplicationShell.status === 'not-applicable'
        ? 'readiness-evidence'
        : 'not-ready',
      suggestedAction: generatedApp.reportApplicationShell.notes.join(' ') ||
        'Keep validation harness artifacts separate from the user-facing report application shell.',
      bindings: {
        status: generatedApp.reportApplicationShell.status,
        applicable: String(generatedApp.reportApplicationShell.applicable),
        findingCount: String(generatedApp.reportApplicationShell.findingCount),
        globalCommandHeaderCount: String(generatedApp.reportApplicationShell.summary.globalCommandHeaderCount),
        chatCommandInputCount: String(generatedApp.reportApplicationShell.summary.chatCommandInputCount),
        reportCommonControlsInHeaderCount: String(generatedApp.reportApplicationShell.summary.reportCommonControlsInHeaderCount),
        collapsibleSidebarCount: String(generatedApp.reportApplicationShell.summary.collapsibleSidebarCount),
        fullHeightSidebarCount: String(generatedApp.reportApplicationShell.summary.fullHeightSidebarCount),
        a4PortraitCanvasCount: String(generatedApp.reportApplicationShell.summary.a4PortraitCanvasCount),
        reportNarrativeCount: String(generatedApp.reportApplicationShell.summary.reportNarrativeCount),
        reportChartSurfaceCount: String(generatedApp.reportApplicationShell.summary.reportChartSurfaceCount),
        reportDataTableCount: String(generatedApp.reportApplicationShell.summary.reportDataTableCount),
        firstFinding: generatedApp.reportApplicationShell.firstFinding?.rule ?? 'none',
      },
    },
  ]
  if (generatedApp.imageEvidenceVerification.enabled || generatedApp.imageEvidenceVerification.surfaceCount > 0) {
    const imageVerification = generatedApp.imageEvidenceVerification
    generatorItems.push({
      id: 'studio-generated-ui-verification-policy',
      code: 'generated-ui-verification-policy',
      severity: imageVerification.status === 'blocked' || !imageVerification.canPublish
        ? 'warning'
        : 'info',
      message:
        `Generated UI verification policy is ${imageVerification.status}; ` +
        `surfaces=${imageVerification.surfaceCount}; ` +
        `promotionAllowed=${imageVerification.promotionAllowed}; ` +
        `canPublish=${imageVerification.canPublish}; ` +
        `requiredEvidence=${imageVerification.requiredEvidence.length}.`,
      studioConformanceType: 'generated-app-evidence',
      generatorImpact: imageVerification.canPublish
        ? 'readiness-evidence'
        : 'not-ready',
      suggestedAction: imageVerification.blockedReasons.join(' ') ||
        'Keep source provenance, policy gates, and candidate-review status attached to generated UI evidence.',
      bindings: {
        status: imageVerification.status,
        enabled: String(imageVerification.enabled),
        surfaceCount: String(imageVerification.surfaceCount),
        promotionAllowed: String(imageVerification.promotionAllowed),
        canPublish: String(imageVerification.canPublish),
        firstTier: imageVerification.surfaces[0]?.provenanceTier ?? 'none',
        firstVerificationLevel: imageVerification.surfaces[0]?.verificationLevel ?? 'none',
        requiredEvidence: imageVerification.requiredEvidence.join(','),
        firstBlockedReason: imageVerification.blockedReasons[0] ?? 'none',
      },
    })
  }
  if (generatedApp.imageEvidenceCandidateAdmission) {
    const admission = generatedApp.imageEvidenceCandidateAdmission
    generatorItems.push({
      id: 'studio-image-evidence-candidate-admission',
      code: 'image-evidence-candidate-admission',
      severity: admission.status === 'admitted' ? 'info' : 'warning',
      message:
        `Image evidence candidate admission is ${admission.status}; ` +
        `candidate=${admission.candidateId}; ` +
        `canSeedOntology=${admission.canSeedOntology}; ` +
        `currentGenerationPublish=${admission.canPublishFromCurrentGeneration}.`,
      studioConformanceType: 'generated-app-evidence',
      generatorImpact: admission.status === 'admitted'
        ? 'readiness-evidence'
        : 'not-ready',
      suggestedAction: admission.blockedReasons.join(' ') ||
        admission.notes?.join(' ') ||
        'Keep image-derived UI as a candidate until review admits it into ontology, then regenerate from the admitted ontology asset.',
      bindings: {
        status: admission.status,
        candidateId: admission.candidateId,
        canSeedOntology: String(admission.canSeedOntology),
        canPublishFromCurrentGeneration: String(admission.canPublishFromCurrentGeneration),
        requiredEvidence: admission.requiredEvidence.join(','),
        firstBlockedReason: admission.blockedReasons[0] ?? 'none',
        reviewerKind: admission.review?.reviewerKind ?? 'none',
      },
    })
  }
  const endUserProofItems: UiConformancePanelItem[] = []

  if (ui.adaptiveRuntime.status === 'planned') {
    generatorItems.push({
      id: 'studio-adaptive-runtime-plan',
      code: 'adaptive-runtime-plan',
      severity: ui.adaptiveRuntime.enabled ? 'info' : 'warning',
      message:
        `Adaptive runtime plan: enabled=${ui.adaptiveRuntime.enabled}; ` +
        `signals=${ui.adaptiveRuntime.situationSignals.join('+') || 'none'}; ` +
        `axes=${ui.adaptiveRuntime.adaptationAxes.join('+') || 'none'}; ` +
        `localState=${ui.adaptiveRuntime.localStatePolicy}; globalState=${ui.adaptiveRuntime.globalStatePolicy}.`,
      studioConformanceType: 'generator-constraint',
      generatorImpact: 'context-aware-runtime-contract',
      suggestedAction:
        'Preserve these adaptation signals and axes through generation, preview, ActionFlow proof, and ontology batch admission.',
      bindings: {
        enabled: String(ui.adaptiveRuntime.enabled),
        signals: ui.adaptiveRuntime.situationSignals.join(','),
        axes: ui.adaptiveRuntime.adaptationAxes.join(','),
        requireMeasuredDesignEvidence: String(ui.adaptiveRuntime.evidencePolicy.requireMeasuredDesignEvidence),
        quarantineUnmeasuredOntologyFacts: String(ui.adaptiveRuntime.evidencePolicy.quarantineUnmeasuredOntologyFacts),
        requireActionFlowTraceForDynamicApps: String(ui.adaptiveRuntime.evidencePolicy.requireActionFlowTraceForDynamicApps),
      },
    })
  }

  if (ui.goldenRecipes.status === 'available') {
    generatorItems.push({
      id: 'studio-golden-recipe-pack',
      code: 'golden-recipe-pack',
      severity: 'info',
      message:
        `Golden recipes: ${ui.goldenRecipes.scenarioIds.join('+')}; ` +
        `designGates=${ui.goldenRecipes.designSystemGates.length}; ` +
        `actionFlowContracts=${ui.goldenRecipes.actionFlowContracts.length}; ` +
        `browserProof=${ui.goldenRecipes.browserProof.length}.`,
      studioConformanceType: 'generator-constraint',
      generatorImpact: 'scenario-specific-quality-contract',
      suggestedAction:
        'Use these recipe gates as the first-pass generation contract before accepting measured design and ActionFlow evidence.',
      bindings: {
        recipeIds: ui.goldenRecipes.recipeIds.join(','),
        scenarioIds: ui.goldenRecipes.scenarioIds.join(','),
        designGates: String(ui.goldenRecipes.designSystemGates.length),
        actionFlowContracts: String(ui.goldenRecipes.actionFlowContracts.length),
        browserProof: String(ui.goldenRecipes.browserProof.length),
      },
    })
  }

  if (evidence.endUserAppProof) {
    const proof = evidence.endUserAppProof
    endUserProofItems.push({
      id: 'studio-end-user-app-generation-proof',
      code: 'end-user-app-generation-proof',
      severity: proof.referoBatch.canRun ? 'info' : 'warning',
      message:
        `End-user app proof: speed=${proof.speed.status} (${proof.speed.totalMs}ms total, ` +
        `${proof.speed.validationMs}ms validation); quality=${proof.quality.status} ` +
        `(score=${proof.quality.averageScore}, grade=${proof.quality.overallGrade}, ` +
        `blocks=${proof.quality.blockCount}); design=${proof.quality.designQualityStatus} ` +
        `(score=${proof.quality.designQualityScore}, source=${proof.quality.designQualitySourceKind}, ` +
        `artifacts=${proof.quality.designQualityArtifactCount}, ` +
        `styleObjects=${proof.quality.designQualityAuthorStyleObjectCount}, ` +
        `styleSwitch=${proof.quality.designStyleSwitch.status}); ` +
        `validationRag=${proof.validationRag.status} ` +
        `(covered=${proof.validationRag.coveredKinds.length}/${proof.validationRag.requiredKinds.length}, ` +
        `missing=${proof.validationRag.missingKinds.join('+') || 'none'}); ` +
        `scenarios=${proof.scenarioMatrix.status} ` +
        `(${proof.scenarioMatrix.passedScenarios.length}/${proof.scenarioMatrix.requiredScenarios.length} required); ` +
        `sandbox=${proof.designSandbox.status} ` +
        `(portal=${proof.designSandbox.portalId ?? 'missing'}, htmlStyle=${proof.designSandbox.htmlHasAppStyleClass}, ` +
        `bodyStyle=${proof.designSandbox.bodyHasAppStyleClass}, ` +
        `portalInsideSurface=${proof.designSandbox.portalInsideAppSurface}, ` +
        `headStyles=${proof.designSandbox.appStyleElementsInHeadCount}); ` +
        `adaptive=${proof.adaptiveRuntime.status} ` +
        `(signals=${proof.adaptiveRuntime.situationSignals.length}, axes=${proof.adaptiveRuntime.adaptationAxes.length}, ` +
        `transitions=${proof.adaptiveRuntime.passedTransitionCount}/${proof.adaptiveRuntime.transitionCount}, ` +
        `axisCoverage=${proof.adaptiveRuntime.axisCoverage.status} ` +
        `${proof.adaptiveRuntime.axisCoverage.passedAxes.length}/${proof.adaptiveRuntime.axisCoverage.requiredAxes.length}, ` +
        `axisMissing=${proof.adaptiveRuntime.axisCoverage.missingAxes.join('+') || 'none'}, ` +
        `stateBoundary=${proof.adaptiveRuntime.stateBoundary.status}); ` +
        `Refero batch=${proof.referoBatch.status}.`,
      studioConformanceType: 'end-user-app-proof',
      generatorImpact: proof.referoBatch.canRun ? 'refero-batch-open' : 'refero-batch-blocked',
      suggestedAction: proof.referoBatch.nextAction,
      bindings: {
        speed: proof.speed.status,
        totalMs: String(proof.speed.totalMs),
        quality: proof.quality.status,
        averageScore: String(proof.quality.averageScore),
        designQuality: proof.quality.designQualityStatus,
        designQualityScore: String(proof.quality.designQualityScore),
        designQualitySource: proof.quality.designQualitySourceKind,
        designQualityArtifacts: String(proof.quality.designQualityArtifactCount),
        designQualityTraces: String(proof.quality.designQualityInteractionTraceArtifactCount),
        designQualityStyleObjects: String(proof.quality.designQualityAuthorStyleObjectCount),
        designQualityUnconvertedStyleObjects: String(proof.quality.designQualityUnconvertedStyleObjectCount),
        designStyleSwitch: proof.quality.designStyleSwitch.status,
        validationRag: proof.validationRag.status,
        validationRagCoveredKinds: proof.validationRag.coveredKinds.join(',') || 'none',
        validationRagMissingKinds: proof.validationRag.missingKinds.join(',') || 'none',
        validationRagAcceptedEvidence: String(proof.validationRag.acceptedEvidenceIds.length),
        validationRagRejectedEvidence: String(proof.validationRag.rejectedEvidenceIds.length),
        designStyleSwitchPrimaryChanged: String(proof.quality.designStyleSwitch.primaryBackgroundChanged),
        designStyleSwitchBasePrimary: proof.quality.designStyleSwitch.basePrimaryBackground ?? 'missing',
        designStyleSwitchTargetPrimary: proof.quality.designStyleSwitch.targetPrimaryBackground ?? 'missing',
        designSandbox: proof.designSandbox.status,
        sandboxPortalId: proof.designSandbox.portalId ?? 'missing',
        sandboxPortalAppId: proof.designSandbox.portalAppId ?? 'missing',
        sandboxPortalCount: String(proof.designSandbox.portalCount),
        sandboxPortalInsideAppSurface: String(proof.designSandbox.portalInsideAppSurface),
        sandboxPortalInsideAppHost: String(proof.designSandbox.portalInsideAppHost),
        sandboxPortalParentTag: proof.designSandbox.portalParentTag ?? 'missing',
        sandboxAppRenderRootDesignScope: proof.designSandbox.appHostDesignScope ?? 'missing',
        sandboxAppRenderRootHasCompleteTokenAttr: String(proof.designSandbox.appRenderRootHasCompleteTokenAttr),
        sandboxAppHostDesignScope: proof.designSandbox.appHostDesignScope ?? 'missing',
        sandboxAppHostHasCompleteTokenAttr: String(proof.designSandbox.appHostHasCompleteTokenAttr),
        sandboxOutsideAppCompleteTokenScopeCount: String(proof.designSandbox.outsideAppCompleteTokenScopeCount),
        sandboxOutsideAppInlinePrimaryTokenCount: String(proof.designSandbox.outsideAppInlinePrimaryTokenCount),
        sandboxAppStyleElementCount: String(proof.designSandbox.appStyleElementCount),
        sandboxAppStyleElementsInAppHostCount: String(proof.designSandbox.appStyleElementsInAppHostCount),
        sandboxAppStyleElementsInHeadCount: String(proof.designSandbox.appStyleElementsInHeadCount),
        sandboxOutsideAppStyleElementCount: String(proof.designSandbox.outsideAppStyleElementCount),
        sandboxHtmlHasAppStyleClass: String(proof.designSandbox.htmlHasAppStyleClass),
        sandboxBodyHasAppStyleClass: String(proof.designSandbox.bodyHasAppStyleClass),
        sandboxHtmlHasCompleteTokenAttr: String(proof.designSandbox.htmlHasCompleteTokenAttr),
        sandboxBodyHasCompleteTokenAttr: String(proof.designSandbox.bodyHasCompleteTokenAttr),
        scenarioMatrix: proof.scenarioMatrix.status,
        requiredScenarios: proof.scenarioMatrix.requiredScenarios.join(','),
        passedScenarios: proof.scenarioMatrix.passedScenarios.join(','),
        adaptiveRuntime: proof.adaptiveRuntime.status,
        adaptiveSignals: proof.adaptiveRuntime.situationSignals.join(','),
        adaptiveAxes: proof.adaptiveRuntime.adaptationAxes.join(','),
        adaptiveTransitionCount: String(proof.adaptiveRuntime.transitionCount),
        adaptivePassedTransitionCount: String(proof.adaptiveRuntime.passedTransitionCount),
        adaptiveAxisCoverage: proof.adaptiveRuntime.axisCoverage.status,
        adaptiveAxisRequiredCount: String(proof.adaptiveRuntime.axisCoverage.requiredAxes.length),
        adaptiveAxisPassedCount: String(proof.adaptiveRuntime.axisCoverage.passedAxes.length),
        adaptiveAxisMissingCount: String(proof.adaptiveRuntime.axisCoverage.missingAxes.length),
        adaptiveAxisRequired: proof.adaptiveRuntime.axisCoverage.requiredAxes.join(','),
        adaptiveAxisPassed: proof.adaptiveRuntime.axisCoverage.passedAxes.join(','),
        adaptiveAxisMissing: proof.adaptiveRuntime.axisCoverage.missingAxes.join(',') || 'none',
        stateBoundary: proof.adaptiveRuntime.stateBoundary.status,
        stateBoundaryLocalPaths: String(proof.adaptiveRuntime.stateBoundary.localStateEvidencePaths.length),
        stateBoundaryGlobalPaths: String(proof.adaptiveRuntime.stateBoundary.globalStateEvidencePaths.length),
        stateBoundaryResultPaths: String(proof.adaptiveRuntime.stateBoundary.resultStateEvidencePaths.length),
        stateBoundaryComputedPaths: String(proof.adaptiveRuntime.stateBoundary.computedStateEvidencePaths.length),
        stateBoundaryMixedTransitions: String(proof.adaptiveRuntime.stateBoundary.mixedLocalGlobalTransitionCount),
        stateBoundaryLegacyPaths: String(proof.adaptiveRuntime.stateBoundary.legacyStateEvidencePaths.length),
        referoBatch: proof.referoBatch.status,
      },
    })
    endUserProofItems.push({
      id: 'studio-end-user-adaptive-axis-coverage-proof',
      code: 'adaptive-axis-coverage',
      severity: proof.adaptiveRuntime.axisCoverage.status === 'pass'
        ? 'info'
        : proof.adaptiveRuntime.axisCoverage.status === 'missing'
          ? 'warning'
          : 'error',
      message:
        `Adaptive axis coverage is ${proof.adaptiveRuntime.axisCoverage.status}; ` +
        `passed=${proof.adaptiveRuntime.axisCoverage.passedAxes.length}; ` +
        `required=${proof.adaptiveRuntime.axisCoverage.requiredAxes.length}; ` +
        `missing=${proof.adaptiveRuntime.axisCoverage.missingAxes.join(', ') || 'none'}.`,
      studioConformanceType: 'end-user-app-proof',
      generatorImpact: proof.adaptiveRuntime.axisCoverage.status === 'pass'
        ? 'adaptive-axis-coverage-proven'
        : 'adaptive-axis-coverage-required',
      suggestedAction: proof.adaptiveRuntime.axisCoverage.reasons[0] ??
        'Attach measured adaptive transitions for every required scenario axis before opening Refero batch or publish readiness.',
      bindings: {
        status: proof.adaptiveRuntime.axisCoverage.status,
        requiredAxes: proof.adaptiveRuntime.axisCoverage.requiredAxes.join(','),
        passedAxes: proof.adaptiveRuntime.axisCoverage.passedAxes.join(','),
        missingAxes: proof.adaptiveRuntime.axisCoverage.missingAxes.join(',') || 'none',
        requiredCount: String(proof.adaptiveRuntime.axisCoverage.requiredAxes.length),
        passedCount: String(proof.adaptiveRuntime.axisCoverage.passedAxes.length),
        missingCount: String(proof.adaptiveRuntime.axisCoverage.missingAxes.length),
      },
    })
    endUserProofItems.push({
      id: 'studio-end-user-state-boundary-proof',
      code: 'state-boundary-proof',
      severity: proof.adaptiveRuntime.stateBoundary.status === 'pass'
        ? 'info'
        : proof.adaptiveRuntime.stateBoundary.status === 'missing'
          ? 'warning'
          : 'error',
      message:
        `State boundary proof is ${proof.adaptiveRuntime.stateBoundary.status}; ` +
        `local=${proof.adaptiveRuntime.stateBoundary.localStateEvidencePaths.length}; ` +
        `appData=${proof.adaptiveRuntime.stateBoundary.globalStateEvidencePaths.length}; ` +
        `_results=${proof.adaptiveRuntime.stateBoundary.resultStateEvidencePaths.length}; ` +
        `computed=${proof.adaptiveRuntime.stateBoundary.computedStateEvidencePaths.length}; ` +
        `mixedLocalGlobalTransitions=${proof.adaptiveRuntime.stateBoundary.mixedLocalGlobalTransitionCount}; ` +
        `legacy=${proof.adaptiveRuntime.stateBoundary.legacyStateEvidencePaths.length}.`,
      studioConformanceType: 'end-user-app-proof',
      generatorImpact: proof.adaptiveRuntime.stateBoundary.status === 'pass'
        ? 'adaptive-state-boundary-separated'
        : 'adaptive-state-boundary-required',
      suggestedAction: proof.adaptiveRuntime.stateBoundary.reasons[0] ??
        'Keep UI block local state, shared appData, ActionFlow _results, and computed UI projection as separate evidence domains.',
      bindings: {
        status: proof.adaptiveRuntime.stateBoundary.status,
        localPaths: proof.adaptiveRuntime.stateBoundary.localStateEvidencePaths.join(','),
        globalPaths: proof.adaptiveRuntime.stateBoundary.globalStateEvidencePaths.join(','),
        resultPaths: proof.adaptiveRuntime.stateBoundary.resultStateEvidencePaths.join(','),
        computedPaths: proof.adaptiveRuntime.stateBoundary.computedStateEvidencePaths.join(','),
        transitionScopes: proof.adaptiveRuntime.stateBoundary.transitionScopes.join(','),
        localOnlyTransitionCount: String(proof.adaptiveRuntime.stateBoundary.localOnlyTransitionCount),
        globalOnlyTransitionCount: String(proof.adaptiveRuntime.stateBoundary.globalOnlyTransitionCount),
        mixedLocalGlobalTransitionCount: String(proof.adaptiveRuntime.stateBoundary.mixedLocalGlobalTransitionCount),
        resultTransitionCount: String(proof.adaptiveRuntime.stateBoundary.resultTransitionCount),
        computedTransitionCount: String(proof.adaptiveRuntime.stateBoundary.computedTransitionCount),
        designTokenTransitionCount: String(proof.adaptiveRuntime.stateBoundary.designTokenTransitionCount),
        legacyPaths: proof.adaptiveRuntime.stateBoundary.legacyStateEvidencePaths.join(','),
      },
    })
    endUserProofItems.push({
      id: 'studio-end-user-design-sandbox-isolation-proof',
      code: 'design-sandbox-isolation',
      severity: proof.designSandbox.status === 'pass'
        ? 'info'
        : 'error',
      message:
        `Design sandbox isolation is ${proof.designSandbox.status}; ` +
        `appRootSandboxed=${proof.designSandbox.appRootSandboxed}; ` +
        `portal=${proof.designSandbox.portalId ?? 'missing'}; ` +
        `portalInsideSurface=${proof.designSandbox.portalInsideAppSurface}; ` +
        `renderRootScope=${proof.designSandbox.appHostDesignScope ?? 'missing'}; ` +
        `outsideInlinePrimary=${proof.designSandbox.outsideAppInlinePrimaryTokenCount}; ` +
        `headStyles=${proof.designSandbox.appStyleElementsInHeadCount}; ` +
        `outsideStyles=${proof.designSandbox.outsideAppStyleElementCount}; ` +
        `htmlStyle=${proof.designSandbox.htmlHasAppStyleClass}; ` +
        `bodyStyle=${proof.designSandbox.bodyHasAppStyleClass}; ` +
        `htmlTokens=${proof.designSandbox.htmlHasCompleteTokenAttr}; ` +
        `bodyTokens=${proof.designSandbox.bodyHasCompleteTokenAttr}.`,
      studioConformanceType: 'end-user-app-proof',
      generatorImpact: proof.designSandbox.status === 'pass'
        ? 'studio-app-design-sandbox-separated'
        : 'studio-app-design-sandbox-required',
      suggestedAction: proof.designSandbox.reasons[0] ??
        'Keep generated-app style classes and complete tokens scoped to the app render root and app-owned portal container.',
      bindings: {
        status: proof.designSandbox.status,
        appRootSandboxed: String(proof.designSandbox.appRootSandboxed),
        portalInsideAppSurface: String(proof.designSandbox.portalInsideAppSurface),
        portalInsideAppHost: String(proof.designSandbox.portalInsideAppHost),
        portalId: proof.designSandbox.portalId ?? 'missing',
        portalAppId: proof.designSandbox.portalAppId ?? 'missing',
        portalParentTag: proof.designSandbox.portalParentTag ?? 'missing',
        appRenderRootDesignScope: proof.designSandbox.appHostDesignScope ?? 'missing',
        appRenderRootHasCompleteTokenAttr: String(proof.designSandbox.appRenderRootHasCompleteTokenAttr),
        appHostDesignScope: proof.designSandbox.appHostDesignScope ?? 'missing',
        appHostHasCompleteTokenAttr: String(proof.designSandbox.appHostHasCompleteTokenAttr),
        outsideAppCompleteTokenScopeCount: String(proof.designSandbox.outsideAppCompleteTokenScopeCount),
        outsideAppInlinePrimaryTokenCount: String(proof.designSandbox.outsideAppInlinePrimaryTokenCount),
        appStyleElementCount: String(proof.designSandbox.appStyleElementCount),
        appStyleElementsInAppHostCount: String(proof.designSandbox.appStyleElementsInAppHostCount),
        appStyleElementsInHeadCount: String(proof.designSandbox.appStyleElementsInHeadCount),
        outsideAppStyleElementCount: String(proof.designSandbox.outsideAppStyleElementCount),
        portalHasStyleClass: String(proof.designSandbox.portalHasStyleClass),
        portalHasCompleteTokenAttr: String(proof.designSandbox.portalHasCompleteTokenAttr),
        htmlHasAppStyleClass: String(proof.designSandbox.htmlHasAppStyleClass),
        bodyHasAppStyleClass: String(proof.designSandbox.bodyHasAppStyleClass),
        htmlHasCompleteTokenAttr: String(proof.designSandbox.htmlHasCompleteTokenAttr),
        bodyHasCompleteTokenAttr: String(proof.designSandbox.bodyHasCompleteTokenAttr),
      },
    })
    for (const scenario of proof.scenarioMatrix.scenarios.filter((item) => item.required || item.status !== 'not-requested')) {
      endUserProofItems.push({
        id: `studio-end-user-scenario-${scenario.id}`,
        code: `scenario-${scenario.id}`,
        severity: scenario.status === 'pass'
          ? 'info'
          : scenario.status === 'fail'
            ? 'error'
            : 'warning',
        message:
          `${scenario.label}: ${scenario.status}; ` +
          `interactions=${scenario.interactions.length}; ` +
          `state=${scenario.stateEvidence.length}; data=${scenario.dataEvidence.length}; ` +
          `artifacts=${scenario.artifactEvidence.length}; traces=${scenario.actionFlowTraceEvidence.length}; ` +
          `actionFlowProof=${scenario.actionFlowProof?.status ?? 'missing'}.`,
        studioConformanceType: 'end-user-app-proof',
        generatorImpact: scenario.status === 'pass'
          ? 'scenario-proof-passed'
          : 'scenario-proof-required',
        suggestedAction: scenario.reasons.join(' ') || 'Attach browser interaction, state, artifact, and ActionFlow trace evidence for this scenario.',
        bindings: {
          scenario: scenario.id,
          required: String(scenario.required),
          status: scenario.status,
          interactions: String(scenario.interactions.length),
          stateEvidence: String(scenario.stateEvidence.length),
          dataEvidence: String(scenario.dataEvidence.length),
          artifactEvidence: String(scenario.artifactEvidence.length),
          actionFlowTraceEvidence: String(scenario.actionFlowTraceEvidence.length),
          actionFlowProofStatus: scenario.actionFlowProof?.status ?? 'missing',
          actionFlowProofTraceCount: String(scenario.actionFlowProof?.traceCount ?? 0),
          actionFlowProofPassedFlowCount: String(scenario.actionFlowProof?.passedFlowCount ?? 0),
          actionFlowProofRequiredFlowCount: String(scenario.actionFlowProof?.requiredFlowCount ?? 0),
        },
      })
    }
  }

  return {
    status: !preflight.canGenerate
      || evidence.fullCoverage.status === 'blocked'
      || evidence.fullCoverage.status === 'missing'
      || evidence.exhaustiveProof.status === 'blocked'
      || evidence.exhaustiveProof.status === 'missing'
      ? 'blocked'
      : generatedApp.status === 'partial' ||
          // pre-stream evidence ('pending', no blocks generated yet) must not
          // render a 'pass' banner while the readiness gate says canPreview=false
          generatedApp.status === 'pending' ||
          preflight.mode === 'llm-fallback' ||
          evidence.fullCoverage.status === 'partial' ||
          evidence.exhaustiveProof.status === 'partial' ||
          evidence.endUserAppProof?.referoBatch.status === 'blocked'
        ? 'fallback'
        : 'pass',
    summary: {
      schemaErrors: 0,
      semanticViolations: 0,
      capabilityMismatches: 0,
      generatorConstraints: generatorItems.length,
      fallbackCandidates: preflight.fallbackCandidateFacts.length,
      designQualityWarnings: 0,
    },
    sections: [
      { id: 'schema-errors', title: 'Schema Errors', items: [] },
      { id: 'semantic-violations', title: 'Semantic Violations', items: [] },
      { id: 'capability-mismatches', title: 'Renderer Capability', items: capabilityItems },
      { id: 'generator-constraints', title: 'Generator Constraints', items: generatorItems },
      { id: 'end-user-app-proof', title: 'End-User App Proof', items: endUserProofItems },
      { id: 'design-quality-warnings', title: 'Design Quality Warnings', items: [] },
    ],
  }
}

function mergeEvidencePanel(
  existing: UiConformancePanelModel,
  evidencePanel: UiConformancePanelModel,
): UiConformancePanelModel {
  const sectionsById = new Map<UiConformancePanelSection['id'], UiConformancePanelSection>()
  let addedGeneratorItems = 0
  for (const section of existing.sections) {
    sectionsById.set(section.id, { ...section, items: [...section.items] })
  }

  for (const evidenceSection of evidencePanel.sections) {
    const current = sectionsById.get(evidenceSection.id)
    if (!current) {
      sectionsById.set(evidenceSection.id, evidenceSection)
      if (evidenceSection.id === 'generator-constraints') {
        addedGeneratorItems += evidenceSection.items.length
      }
      continue
    }
    const existingItemIds = new Set(current.items.map((item) => item.id))
    const evidenceItemsById = new Map(evidenceSection.items.map((item) => [item.id, item]))
    current.items = current.items.map((item) => evidenceItemsById.get(item.id) ?? item)
    const addedItems = evidenceSection.items.filter((item) => !existingItemIds.has(item.id))
    if (evidenceSection.id === 'generator-constraints') {
      addedGeneratorItems += addedItems.length
    }
    current.items = [...current.items, ...addedItems]
  }

  return {
    ...existing,
    status: deriveMergedPanelStatus([...sectionsById.values()], evidencePanel.status),
    sections: [...sectionsById.values()],
    summary: {
      ...existing.summary,
      generatorConstraints: existing.summary.generatorConstraints + addedGeneratorItems,
      designQualityWarnings: existing.summary.designQualityWarnings ?? 0,
    },
  }
}

function deriveMergedPanelStatus(
  sections: UiConformancePanelSection[],
  fallback: UiConformancePanelModel['status'],
): UiConformancePanelModel['status'] {
  const items = sections.flatMap((section) => section.items.map((item) => ({ sectionId: section.id, item })))
  if (items.some(({ sectionId, item }) => (
    item.severity === 'error' &&
    (sectionId === 'schema-errors' || sectionId === 'semantic-violations' || sectionId === 'generator-constraints')
  ))) {
    return 'blocked'
  }
  if (fallback === 'fallback' || items.some(({ item }) => item.severity === 'warning' || item.severity === 'error')) {
    return 'fallback'
  }
  return fallback
}

function firstPanel(...values: unknown[]): UiConformancePanelModel | null {
  for (const value of values) {
    if (isUiConformancePanelModel(value)) return value
  }
  return null
}

function isUiConformancePanelModel(value: unknown): value is UiConformancePanelModel {
  const record = readRecord(value)
  const summary = readRecord(record?.summary)
  return Boolean(
    record &&
      summary &&
      (record.status === 'pass' || record.status === 'blocked' || record.status === 'fallback') &&
      Array.isArray(record.sections) &&
      typeof summary.schemaErrors === 'number' &&
      typeof summary.semanticViolations === 'number' &&
      typeof summary.capabilityMismatches === 'number' &&
      typeof summary.generatorConstraints === 'number' &&
      typeof summary.fallbackCandidates === 'number',
  )
}

function isStudioFullCoverageEvidenceSummary(value: unknown): value is StudioFullCoverageEvidenceSummary {
  const record = readRecord(value)
  const sectionStatuses = readRecord(record?.sectionStatuses)
  const platformCoverage = readRecord(record?.componentCapabilityPlatformCoverage)
  return Boolean(
    record &&
      record.schemaVersion === 'studio-full-coverage-evidence.v1' &&
      (record.status === 'pass' || record.status === 'partial' || record.status === 'blocked' || record.status === 'missing') &&
      typeof record.componentOntologyCount === 'number' &&
      typeof record.componentCapabilityCatalogCount === 'number' &&
      typeof record.componentCapabilityDenominator === 'number' &&
      platformCoverage &&
      typeof platformCoverage.webSupported === 'number' &&
      typeof platformCoverage.rnSupported === 'number' &&
      typeof platformCoverage.flutterSupported === 'number' &&
      typeof platformCoverage.renderPackComponents === 'number' &&
      sectionStatuses &&
      typeof sectionStatuses.componentCapabilities === 'string',
  )
}

function isStudioExhaustiveProofEvidenceSummary(value: unknown): value is StudioExhaustiveProofEvidenceSummary {
  const record = readRecord(value)
  return Boolean(
    record &&
      record.schemaVersion === 'studio-exhaustive-proof-evidence.v1' &&
      (record.status === 'pass' || record.status === 'partial' || record.status === 'blocked' || record.status === 'missing') &&
      typeof record.ontologyComponentCount === 'number' &&
      typeof record.runtimeProvenActionCount === 'number' &&
      typeof record.providerSlotHydrationProofStatus === 'string',
  )
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
}

function readArray(value: unknown): readonly unknown[] | null {
  return Array.isArray(value) ? value : null
}

function readNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function readString(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.trim().length > 0 ? value : fallback
}

function normalizeStudioApplicationConformanceStatus(
  value: unknown,
): StudioApplicationConformanceOracleStatus {
  if (value === 'pass' || value === 'blocked') return value
  return 'missing'
}

function normalizeStudioApplicationConformanceDimensions(
  values: readonly unknown[] | null,
): StudioApplicationConformanceDimensionSummary[] {
  return (values ?? []).flatMap((value): StudioApplicationConformanceDimensionSummary[] => {
    const record = readRecord(value)
    if (!record) return []
    const dimension = readString(record.dimension, 'unknown')
    const status = record.status === 'pass' || record.status === 'blocked'
      ? record.status
      : 'missing'
    const divergences = readArray(record.divergences) ?? []
    const firstDivergence = readRecord(divergences[0])
    const firstDivergenceReason = readString(firstDivergence?.reason)

    return [{
      dimension,
      status,
      checkedCount: readNumber(record.checkedCount),
      divergenceCount: divergences.length,
      ...(firstDivergenceReason ? { firstDivergenceReason } : {}),
    }]
  })
}

function normalizeStudioVisibleFromJsonStatus(
  value: unknown,
): StudioVisibleFromJsonOracleStatus {
  if (value === 'pass' || value === 'blocked') return value
  return 'missing'
}

function normalizeVisibleFromJsonCheckedSlots(
  values: readonly unknown[] | null,
): StudioVisibleFromJsonCheckedSlotSummary[] {
  return (values ?? []).flatMap((value): StudioVisibleFromJsonCheckedSlotSummary[] => {
    const record = readRecord(value)
    if (!record) return []
    const status = record.status === 'pass' || record.status === 'blocked'
      ? record.status
      : 'blocked'
    const reasons = normalizeStringArray(readArray(record.reasons) ?? undefined)
    const slotId = readString(record.slotId)
    const nodeId = readString(record.nodeId)
    const label = readString(record.label)
    return [{
      ...(slotId ? { slotId } : {}),
      ...(nodeId ? { nodeId } : {}),
      ...(label ? { label } : {}),
      value: readString(record.value),
      required: record.required !== false,
      foundTextNodeCount: readNumber(record.foundTextNodeCount),
      visibleTextNodeCount: readNumber(record.visibleTextNodeCount),
      status,
      ...(reasons[0] ? { firstReason: reasons[0] } : {}),
    }]
  })
}

function normalizeFullCoverageStatus(value: unknown): StudioFullCoverageAuditStatus {
  if (value === 'pass' || value === 'partial' || value === 'blocked') return value
  return 'missing'
}

function normalizeAdaptiveTransitions(
  values: readonly Partial<StudioEndUserAdaptiveTransitionEvidence>[] | undefined,
): StudioEndUserAdaptiveTransitionEvidence[] {
  return (values ?? []).flatMap((value, index) => {
    const record = readRecord(value)
    if (!record) return []
    const signal = readString(record.signal, 'unknown-signal')
    const axis = readString(record.axis, 'unknown-axis')
    const before = readString(record.before)
    const after = readString(record.after)
    const uiProbe = readString(record.uiProbe, `adaptive-transition-${index}`)
    const stateScope = readString(record.stateScope, 'unknown-state')
    const actionFlow = readString(record.actionFlow, 'unknown-flow')
    const explicitReasons = normalizeStringArray(readArray(record.reasons) ?? undefined)
    const reasons = [
      ...explicitReasons,
      ...(!before ? ['Adaptive transition before value is missing.'] : []),
      ...(!after ? ['Adaptive transition after value is missing.'] : []),
      ...(before && after && before === after ? ['Adaptive transition did not change the UI projection.'] : []),
      ...(signal === 'unknown-signal' ? ['Adaptive transition signal is missing.'] : []),
      ...(axis === 'unknown-axis' ? ['Adaptive transition axis is missing.'] : []),
      ...(actionFlow === 'unknown-flow' ? ['Adaptive transition ActionFlow evidence is missing.'] : []),
    ]
    const explicitStatus = record.status === 'pass' || record.status === 'fail' ? record.status : undefined
    return [{
      signal,
      axis,
      before,
      after,
      uiProbe,
      stateScope,
      actionFlow,
      status: explicitStatus ?? (reasons.length === 0 ? 'pass' : 'fail'),
      reasons,
    }]
  })
}

function readAgenticValidationAssets(source: unknown): StudioAgenticRagValidationAssetEvidence[] {
  const record = readRecord(source)
  const items = Array.isArray(source)
    ? source
    : readArray(record?.assets) ??
      readArray(record?.evidence) ??
      readArray(record?.validationEvidence) ??
      []

  return items.flatMap((item): StudioAgenticRagValidationAssetEvidence[] => {
    const asset = readRecord(item)
    if (!asset) return []
    const kind = normalizeAgenticValidationKind(asset.kind)
    const assetId = readString(asset.assetId)
    const sourceRef = readString(asset.sourceRef)
    if (!kind || !assetId || !sourceRef) return []

    return [{
      kind,
      assetId,
      sourceRef,
      status: normalizeAgenticValidationAssetStatus(asset.status),
      authority: normalizeAgenticValidationAssetAuthority(asset.authority),
      ...(typeof asset.score === 'number' && Number.isFinite(asset.score) ? { score: asset.score } : {}),
      evidenceRefs: normalizeStringArray(readArray(asset.evidenceRefs) ?? undefined),
    }]
  })
}

function normalizeAgenticValidationKinds(
  values: readonly unknown[] | null,
): StudioAgenticRagValidationAssetKind[] {
  return REQUIRED_AGENTIC_VALIDATION_RAG_KINDS.filter((kind) => (
    (values ?? []).some((value) => value === kind)
  ))
}

function normalizeAgenticValidationKind(value: unknown): StudioAgenticRagValidationAssetKind | null {
  return REQUIRED_AGENTIC_VALIDATION_RAG_KINDS.includes(value as StudioAgenticRagValidationAssetKind)
    ? value as StudioAgenticRagValidationAssetKind
    : null
}

function normalizeAgenticValidationAssetStatus(value: unknown): StudioAgenticRagValidationAssetStatus {
  return value === 'admitted' || value === 'candidate' || value === 'quarantined' || value === 'rejected' || value === 'deprecated'
    ? value
    : 'candidate'
}

function normalizeAgenticValidationAssetAuthority(value: unknown): StudioAgenticRagValidationAssetAuthority {
  return value === 'canonical' || value === 'promoted' || value === 'candidate' || value === 'quarantined'
    ? value
    : 'candidate'
}

function normalizeActionFlowRuntimeProof(
  value: Partial<StudioEndUserActionFlowRuntimeProof> | null | undefined,
): StudioEndUserActionFlowRuntimeProof | null {
  const record = readRecord(value)
  if (!record) return null
  const status = record.status === 'pass' ? 'pass' : 'fail'
  const requirements: StudioEndUserActionFlowRuntimeProof['requirements'] = (readArray(record.requirements) ?? []).flatMap((item) => {
    const requirement = readRecord(item)
    if (!requirement) return []
    const requirementStatus: 'pass' | 'fail' = requirement.status === 'pass' ? 'pass' : 'fail'
    return [{
      flowName: readString(requirement.flowName, 'unknown-flow'),
      status: requirementStatus,
      reasons: normalizeStringArray(readArray(requirement.reasons) ?? undefined),
    }]
  })
  return {
    schemaVersion: 'studio-end-user-actionflow-runtime-proof.v1',
    status,
    traceCount: readNumber(record.traceCount),
    requiredFlowCount: readNumber(record.requiredFlowCount),
    passedFlowCount: readNumber(record.passedFlowCount),
    flowNames: normalizeStringArray(readArray(record.flowNames) ?? undefined),
    stateDiffPaths: normalizeStringArray(readArray(record.stateDiffPaths) ?? undefined),
    actionNames: normalizeStringArray(readArray(record.actionNames) ?? undefined),
    resultKeys: normalizeStringArray(readArray(record.resultKeys) ?? undefined),
    computedKeys: normalizeStringArray(readArray(record.computedKeys) ?? undefined),
    rendererProjectionTexts: normalizeStringArray(readArray(record.rendererProjectionTexts) ?? undefined),
    requirements,
    reasons: normalizeStringArray(readArray(record.reasons) ?? undefined),
  }
}

function normalizeStringArray(values: readonly unknown[] | undefined): string[] {
  return (values ?? []).filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
}

function normalizeNullableBoolean(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null
}

function normalizeBooleanish(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value
  if (value === 'true') return true
  if (value === 'false') return false
  return null
}

function inferRequiredEndUserScenarios(
  app: unknown,
  evidence: StudioGenerationEvidence,
): StudioEndUserScenarioId[] {
  const scenarios = new Set<StudioEndUserScenarioId>()
  const searchText = collectScenarioSearchText(app)
  const runtimeTargets = new Set(evidence.uiOntology.runtimeTargets)

  if (/(?:\b(?:cart|checkout|commerce|shopping|shop|storefront|retail store|catalog|order-flow|addtocart|add-to-cart)\b|\bproduct\b|appdata\.cart)/i.test(searchText)) {
    scenarios.add('shopping')
  }
  if (
    runtimeTargets.has('mobile') ||
    runtimeTargets.has('rn') ||
    runtimeTargets.has('flutter') ||
    /\b(?:mobile app|ios|android|bottom navigation|bottom-nav|safe-area|mobile-workflow|mobile-shell)\b/i.test(searchText)
  ) {
    scenarios.add('complex-mobile')
  }
  if (/(data-table|data_table|settablecell|set-table-cell|addtablerow|deletetablerow|cellchange|rowclick|backoffice|back office|editable table|records-table)/i.test(searchText)) {
    scenarios.add('backoffice-data-table')
  }

  return END_USER_SCENARIO_IDS.filter((id) => scenarios.has(id))
}

function collectScenarioSearchText(value: unknown, depth = 0): string {
  if (depth > 5 || value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) return value.map((item) => collectScenarioSearchText(item, depth + 1)).join(' ')
  const record = readRecord(value)
  if (!record) return ''
  return Object.entries(record)
    .filter(([key]) => !SCENARIO_SEARCH_IGNORED_KEYS.has(key))
    .flatMap(([key, nested]) => [key, collectScenarioSearchText(nested, depth + 1)])
    .join(' ')
}

const SCENARIO_SEARCH_IGNORED_KEYS = new Set([
  'actionflowContractSummary',
  'adaptiveRuntimePlan',
  'designQualityBenchmark',
  'designQualityEvidence',
  'exhaustiveProofAudit',
  'fullCoverageAudit',
  'generatedAppReadinessGate',
  'generationEvidence',
  'goldenRecipeEvidence',
  'metadata',
  'ontologyRetrievalTrace',
  'renderEngineRegistrySummary',
  'rendererTargetMatrix',
  'reportApplicationIntegration',
  'reportExportReadiness',
  'reportReadiness',
  'documentExportReadiness',
  'studioExhaustiveProofAudit',
  'studioFullCoverageAudit',
  'uiConformancePanel',
  'uiOntologyConformance',
  'uiOntologyGenerationConstraints',
])

function buildStudioUiOntologyPreflight(
  source: StudioGenerationEvidenceSource | null | undefined,
  projectPlan: StudioProjectPlanEvidenceSource | null | undefined,
  targetPlatform: StudioGenerationTargetPlatform,
): StudioUiOntologyPreflightSummary {
  const blockSpecs = readPreflightBlockSpecs(source)
  const fallbackCandidateFacts: StudioUiOntologyFallbackCandidateFact[] = []
  const blockedReasons: string[] = []
  const ragCandidates: StudioUiOntologyRagCandidate[] = []
  const bindPlan: StudioUiOntologyBindPlanItem[] = []

  if (blockSpecs.length === 0) {
    fallbackCandidateFacts.push({
      code: 'rag-corpus-miss',
      reviewStatus: 'pending-review',
      message: 'No block specs were available for deterministic retrieve + bind + assemble; Studio will keep LLM fallback visible.',
    })
  }

  for (const block of blockSpecs) {
    const blockName = block.name || 'unnamed-block'
    const blockType = normalizeBlockType(block.type)

    if (BLOCKED_BLOCK_TYPES.has(blockType)) {
      blockedReasons.push(`Blocked unsafe UI type "${blockType}" for ${blockName}.`)
      fallbackCandidateFacts.push({
        code: 'blocked-unsafe-ui-type',
        blockName,
        blockType,
        reviewStatus: 'quarantine',
        message: `The "${blockType}" block type is outside the closed Studio UI vocabulary for generation.`,
      })
      continue
    }

    const bindings = Array.isArray(block.binds)
      ? block.binds.filter((item): item is string => typeof item === 'string')
      : []

    bindPlan.push({
      blockName,
      blockType,
      bindingCount: bindings.length,
      eventTypes: extractEventTypes(bindings),
      stateScopes: extractStateScopes(bindings),
      notes: bindings.length > 0
        ? bindings.slice(0, 3)
        : ['No explicit bind strings supplied; use static content or validator-derived event requirements.'],
    })

    fallbackCandidateFacts.push({
      code: 'ontology-vocabulary-only',
      blockName,
      blockType,
      reviewStatus: 'pending-review',
      message:
        `Block "${blockName}" maps to the allowed UI type "${blockType}", but a type match is not ` +
        'source-backed retrieval authority. Studio will keep LLM fallback visible.',
    })

    if (GENERIC_BLOCK_TYPES.has(blockType)) {
      fallbackCandidateFacts.push({
        code: 'generic-pattern-fallback',
        blockName,
        blockType,
        reviewStatus: 'pending-review',
        message: `Block "${blockName}" uses generic type "${blockType}", so LLM fallback must honor the negative constraints and cannot silently promote a new ontology element.`,
      })
    }
  }

  const canGenerate = blockedReasons.length === 0
  const mode: StudioUiOntologyPreflightSummary['mode'] = canGenerate
    ? fallbackCandidateFacts.length > 0 ? 'llm-fallback' : 'ontology-driven'
    : 'blocked'
  const runtimeTargets = normalizeStudioRuntimeTargets(source, projectPlan)
  const adaptiveRuntime = buildStudioAdaptiveRuntimeEvidence(source, projectPlan)
  const goldenRecipes = buildStudioGoldenRecipeEvidence(source)
  const adaptiveFacts: StudioUiOntologySafeDesignFact[] = adaptiveRuntime.status === 'planned'
    ? [
        {
          kind: 'adaptive-runtime-signals',
          normalizedTarget: adaptiveRuntime.situationSignals.join('+') || 'none',
          confidence: 1,
        },
        {
          kind: 'adaptive-runtime-axes',
          normalizedTarget: adaptiveRuntime.adaptationAxes.join('+') || 'none',
          confidence: 1,
        },
      ]
    : []
  const recipeFacts: StudioUiOntologySafeDesignFact[] = goldenRecipes.status === 'available'
    ? [
        {
          kind: 'golden-recipe-scenarios',
          normalizedTarget: goldenRecipes.scenarioIds.join('+') || 'none',
          confidence: 1,
        },
        {
          kind: 'golden-recipe-design-gates',
          normalizedTarget: `${goldenRecipes.designSystemGates.length}-design-gates`,
          confidence: 1,
        },
      ]
    : []

  return {
    mode,
    canGenerate,
    ragCandidates,
    bindPlan,
    fallbackCandidateFacts,
    safeDesignFacts: [
      {
        kind: 'runtime-targets',
        normalizedTarget: runtimeTargets.join('+') || 'web',
        confidence: 1,
      },
      {
        kind: 'render-engine-coverage',
        normalizedTarget: targetPlatform === 'flutter' ? 'flutter-190-of-190' : 'react-190-of-190',
        confidence: 1,
      },
      ...adaptiveFacts,
      ...recipeFacts,
    ],
    negativeConstraints: [
      'Do not invent UI element types outside the 190-element ontology vocabulary.',
      'Do not emit raw HTML, script, body, head, or style nodes in generated Coogo JSON.',
      'Do not treat LLM fallback candidates as canonical ontology facts without review.',
      ...(targetPlatform === 'flutter'
        ? ['For Flutter targets, avoid any element type reported as rendererBlankTypes > 0.']
        : []),
    ],
    generationRetrievalRouting: buildStudioGenerationRetrievalRoutingSummary(ragCandidates),
    promptGuardrails: [
      ragCandidates.length > 0
        ? 'Prefer deterministic retrieve + bind + assemble when an admitted, source-backed RAG candidate exists.'
        : 'Treat UI type vocabulary matches as constraints only; do not promote them to deterministic retrieval authority.',
      'Keep all events and ActionFlow binds within the runtime-honored ontology contract.',
      'Keep style choices tied to design tokens instead of raw one-off color or shadow values.',
      ...(goldenRecipes.status === 'available'
        ? ['Honor the attached golden recipe design gates, ActionFlow contracts, and browser proof checklist.']
        : []),
    ],
    blockedReasons,
    deterministicCandidateCount: ragCandidates.length,
  }
}

function buildStudioGenerationRetrievalRoutingSummary(
  ragCandidates: StudioUiOntologyRagCandidate[],
): StudioGenerationRetrievalRoutingSummary {
  return {
    schemaVersion: 'agentic-generation-retrieval-routing.v1',
    policy: {
      admittedGenerationUse: 'admitted-only',
      blockedGenerationUse: 'mining-only',
      failClosed: true,
    },
    admittedRetrieval: ragCandidates.slice(0, 6).map((candidate) => ({
      retrievalId: `studio-generation-retrieval-${candidate.candidateId.replace(/[^a-zA-Z0-9-]/g, '-')}`,
      inventoryId: candidate.candidateId,
      itemName: candidate.blockName,
      itemKind: candidate.blockType,
      admissionTier: 'T4',
      lanes: [candidate.blockType, 'studio-app-generation'],
      generationUse: 'admitted-only',
      staticGenerationAllowed: true,
      interactiveGenerationAllowed: false,
      promptHint: 'Use as a static compose anchor; require T5 browser proof before interactive behavior is treated as positive retrieval.',
    })),
    referoStyles: [
      {
        styleId: 'refero.style.08ae8676-eeed-4eba-9835-856c0f25a1d4',
        title: 'Fey',
        theme: 'dark',
        roles: {
          background: '#0b0b0b',
          foreground: '#ffffff',
          primary: '#e6e6e6',
          'muted-foreground': '#868f97',
          border: '#e5e7eb',
          radius: '99px',
        },
        verifiedSampleBlocks: ['blog/blog-comment', 'food-delivery/restaurant-detail', 'testimonial/testimonial-grid'],
        promptHint: 'Map Refero roles through semantic design tokens; do not emit unreviewed raw visual classes.',
      },
      {
        styleId: 'refero.style.0dc5e09f-fa60-4e17-b8ee-83dacb8cbf7d',
        title: 'monday.com',
        theme: 'light',
        roles: {
          background: '#ffffff',
          foreground: '#181b34',
          primary: '#6161ff',
          'muted-foreground': '#676879',
          border: '#dcdfec',
          radius: '9999px',
        },
        verifiedSampleBlocks: ['blog/blog-comment', 'food-delivery/restaurant-detail', 'testimonial/testimonial-grid'],
        promptHint: 'Map Refero roles through semantic design tokens; do not emit unreviewed raw visual classes.',
      },
    ],
    gapGuidance: [
      {
        guidanceId: 'studio-gap-browser-e2e-proof-missing',
        inventoryId: 'studio-interactive-registry-items',
        itemName: 'interactive registry candidates',
        gapStage: 'actionflow-admission',
        gapCategory: 'browser-e2e-proof-missing',
        toolQueue: 'browser-e2e-proof',
        generationUse: 'mining-only',
        generationAllowed: false,
        negativeConstraints: [
          'Do not expose interactive registry behavior as positive retrieval until browser E2E proof exists.',
          'Do not claim browser-verified interaction behavior without a proof artifact.',
        ],
        nextGate: 'Run browser E2E proof for generated events, flow, and state machine before interactive retrieval.',
      },
      {
        guidanceId: 'studio-gap-renderer-or-converter-parity',
        inventoryId: 'studio-visual-delta-registry-items',
        itemName: 'visual-delta registry candidates',
        gapStage: 'render-admission',
        gapCategory: 'visual-delta-component-parity',
        toolQueue: 'renderer-or-converter-parity',
        generationUse: 'mining-only',
        generationAllowed: false,
        negativeConstraints: [
          'Do not use visual-delta registry rows as positive examples until render admission passes.',
          'Prefer admitted T4/T5 fallback candidates over blocked visual-delta rows.',
        ],
        nextGate: 'Patch renderer/converter parity and rerun render admission before positive retrieval.',
      },
    ],
  }
}

function readPreflightBlockSpecs(source: StudioGenerationEvidenceSource | null | undefined): Array<{
  name?: string
  type?: string
  description?: string
  binds?: unknown
}> {
  const record = readRecord(source)
  if (!record) return []

  if (Array.isArray(record.blocks)) {
    return record.blocks
      .filter((item) => readRecord(item))
      .map((item) => item as { name?: string; type?: string; description?: string; binds?: unknown })
  }

  const blocks = readRecord(record.blocks)
  if (blocks) {
    return Object.entries(blocks).map(([name, block]) => {
      const blockRecord = readRecord(block) ?? {}
      const meta = readRecord(blockRecord.meta) ?? readRecord(blockRecord.metadata) ?? {}
      const ui = readRecord(blockRecord.ui)
      const root = readRecord(ui?.root)
      return {
        name: String(meta.name ?? blockRecord.name ?? name),
        type: String(meta.blockType ?? meta.type ?? blockRecord.type ?? root?.type ?? 'section'),
        binds: blockRecord.binds,
      }
    })
  }

  const meta = readRecord(record.meta) ?? readRecord(record.metadata)
  const ui = readRecord(record.ui)
  const root = readRecord(ui?.root)
  if (meta || root) {
    return [{
      name: String(meta?.name ?? record.name ?? 'block'),
      type: String(meta?.blockType ?? meta?.type ?? record.type ?? root?.type ?? 'section'),
      description: typeof record.description === 'string' ? record.description : undefined,
      binds: record.binds,
    }]
  }

  if (typeof record.name === 'string' || typeof record.type === 'string') {
    return [{
      name: typeof record.name === 'string' ? record.name : 'block',
      type: typeof record.type === 'string' ? record.type : 'section',
      description: typeof record.description === 'string' ? record.description : undefined,
      binds: record.binds,
    }]
  }

  return []
}

function normalizeBlockType(value: unknown): string {
  const normalized = typeof value === 'string'
    ? value.trim().toLowerCase()
    : ''
  return normalized || 'section'
}

function extractEventTypes(bindings: string[]): string[] {
  const events = new Set<string>()
  for (const binding of bindings) {
    const lower = binding.toLowerCase()
    for (const eventName of ['tap', 'click', 'change', 'submit', 'input', 'select', 'hover']) {
      if (new RegExp(`\\b${eventName}\\b`).test(lower)) events.add(eventName === 'click' ? 'tap' : eventName)
    }
  }
  return [...events].sort()
}

function extractStateScopes(bindings: string[]): string[] {
  const scopes = new Set<string>()
  for (const binding of bindings) {
    for (const scope of ['local', 'appData', '_results', 'computed', 'props', 'event', 'route']) {
      if (binding.includes(`${scope}.`) || binding.includes(scope)) scopes.add(scope)
    }
  }
  return [...scopes].sort()
}

function withGeneratedAppReadiness(
  evidence: StudioGenerationEvidence,
  source: unknown,
): StudioGenerationEvidence {
  const generatedPreflight = buildStudioUiOntologyPreflight(
    source as StudioGenerationEvidenceSource,
    {
      appClass: evidence.generatedApp.appClass,
      runtimeTargets: evidence.uiOntology.runtimeTargets,
      adaptiveRuntime: evidence.uiOntology.adaptiveRuntime,
    },
    evidence.uiOntology.targetPlatform,
  )
  const generatedUiOntology: UiOntologyGenerationSummary = {
    ...evidence.uiOntology,
    status: generatedPreflight.canGenerate
      ? generatedPreflight.mode === 'llm-fallback' ? 'fallback' : 'pass'
      : 'blocked',
    mode: generatedPreflight.mode,
    preflight: generatedPreflight,
  }
  // The source here IS a generated app document — its proofs arrive with
  // app-document provenance (capped); the prior evidence rides through the
  // project-plan channel and keeps its own provenance.
  const sourceNativeTargetProof = buildStudioNativeTargetProofEvidence(
    source as StudioGenerationEvidenceSource,
    {
      appClass: evidence.generatedApp.appClass,
      runtimeTargets: evidence.uiOntology.runtimeTargets,
      nativeTargetProof: evidence.nativeTargetProof,
      adaptiveRuntime: evidence.uiOntology.adaptiveRuntime,
    },
    { documentOnly: true },
  )

  // A document-carried proof may upgrade missing/own-tier evidence but
  // never displaces a studio-input summary.
  const keepExistingNativeProof =
    sourceNativeTargetProof.status === 'missing' ||
    (evidence.nativeTargetProof.provenance === 'studio-input' &&
      evidence.nativeTargetProof.status !== 'missing' &&
      sourceNativeTargetProof.provenance !== 'studio-input')

  const generatedApp = buildGeneratedAppReadiness(
    source as StudioGenerationEvidenceSource,
    {
      appClass: evidence.generatedApp.appClass,
      runtimeTargets: evidence.uiOntology.runtimeTargets,
      adaptiveRuntime: evidence.uiOntology.adaptiveRuntime,
    },
    'generated',
  )
  const applicationConformance = generatedApp.applicationConformance.status === 'missing' &&
    evidence.applicationConformanceOracle.status !== 'missing'
    ? {
        ...evidence.applicationConformanceOracle,
        required: evidence.applicationConformanceOracle.required ||
          generatedApp.applicationConformance.required,
      }
    : generatedApp.applicationConformance
  const visibleFromJson = generatedApp.visibleFromJson.status === 'missing' &&
    evidence.generatedApp.visibleFromJson.status !== 'missing'
    ? {
        ...evidence.generatedApp.visibleFromJson,
        required: evidence.generatedApp.visibleFromJson.required ||
          generatedApp.visibleFromJson.required,
      }
    : generatedApp.visibleFromJson
  const imageEvidenceVerification = generatedApp.imageEvidenceVerification.enabled ||
    generatedApp.imageEvidenceVerification.surfaceCount > 0
    ? generatedApp.imageEvidenceVerification
    : evidence.generatedApp.imageEvidenceVerification
  const imageEvidenceCandidateAdmission = generatedApp.imageEvidenceCandidateAdmission ??
    evidence.generatedApp.imageEvidenceCandidateAdmission
  const imageEvidenceNotes = imageEvidenceVerification.enabled && !imageEvidenceVerification.canPublish
    ? ['Generated UI verification policy blocks publish until provenance gates and candidate review allow promotion.']
    : []
  const imageCandidateAdmissionNotes = imageEvidenceCandidateAdmission?.status &&
    imageEvidenceCandidateAdmission.status !== 'admitted'
    ? [`Image evidence candidate admission is ${imageEvidenceCandidateAdmission.status}; regenerate from an admitted ontology asset before publish.`]
    : []
  const generatedAppStatus = imageEvidenceVerification.status === 'blocked'
    ? 'partial'
    : (imageEvidenceNotes.length > 0 || imageCandidateAdmissionNotes.length > 0) &&
      (generatedApp.status === 'pass' || generatedApp.status === 'not-required')
      ? 'partial'
      : generatedApp.status

  return {
    ...evidence,
    uiOntology: generatedUiOntology,
    nativeTargetProof: keepExistingNativeProof
      ? evidence.nativeTargetProof
      : sourceNativeTargetProof,
    applicationConformanceOracle: applicationConformance,
    generatedApp: {
      ...generatedApp,
      status: generatedAppStatus,
      applicationConformance,
      visibleFromJson,
      imageEvidenceVerification,
      imageEvidenceCandidateAdmission,
      notes: [...new Set([...generatedApp.notes, ...imageEvidenceNotes, ...imageCandidateAdmissionNotes])],
    },
  }
}

function readValidationScores(
  validation: StudioAppValidationResultSource | null | undefined,
  blockQualityScores: Record<string, number> | number[] | undefined,
): number[] {
  const fromValidation = Object.values(validation?.scores ?? {})
    .flatMap((score) => typeof score.score === 'number' && Number.isFinite(score.score) ? [score.score] : [])
  if (fromValidation.length > 0) return fromValidation
  if (Array.isArray(blockQualityScores)) {
    return blockQualityScores.filter((score) => Number.isFinite(score))
  }
  return Object.values(blockQualityScores ?? {})
    .filter((score) => Number.isFinite(score))
}

function countGeneratedBlocks(app: unknown): number | null {
  const blocks = readRecord(readRecord(app)?.blocks)
  return blocks ? Object.keys(blocks).length : null
}

function readGeneratedUiVerificationRoots(source: unknown): Record<string, unknown>[] {
  const record = readRecord(source)
  if (!record) return []
  const metadata = readRecord(record.metadata)
  const meta = readRecord(record.meta)
  const manifest = readRecord(record.manifest)
  const generationEvidence = readRecord(
    record.generationEvidence ??
      metadata?.generationEvidence ??
      meta?.generationEvidence ??
      manifest?.generationEvidence,
  )
  const generatedApp = readRecord(generationEvidence?.generatedApp)
  return [record, metadata, meta, manifest, generationEvidence, generatedApp]
    .filter((item): item is Record<string, unknown> => Boolean(item))
}

function isImageSourceEvidence(value: unknown): value is ImageSourceEvidence {
  return validateImageSourceEvidenceContract(value).valid
}

function isGeneratedUiVerificationSurface(value: unknown): value is GeneratedUiVerificationSurface {
  return validateGeneratedUiVerificationSurfaceContract(value).valid
}

function isGeneratedUiVerificationSummary(value: unknown): value is GeneratedUiVerificationSummary {
  const record = readRecord(value)
  return Boolean(
    record &&
      record.schemaVersion === GENERATED_UI_VERIFICATION_SUMMARY_SCHEMA_VERSION &&
      typeof record.enabled === 'boolean' &&
      (record.status === 'pass' || record.status === 'blocked' || record.status === 'skipped') &&
      Array.isArray(record.surfaces),
  )
}

function isImageEvidenceCandidateAdmissionResult(value: unknown): value is ImageEvidenceCandidateAdmissionResult {
  const record = readRecord(value)
  return Boolean(
    record &&
      record.schemaVersion === IMAGE_EVIDENCE_CANDIDATE_ADMISSION_SCHEMA_VERSION &&
      typeof record.candidateId === 'string' &&
      record.sourceTier === 'image-derived-candidate' &&
      (
        record.status === 'review-required' ||
        record.status === 'admitted' ||
        record.status === 'rejected' ||
        record.status === 'quarantined' ||
        record.status === 'blocked'
      ) &&
      typeof record.canSeedOntology === 'boolean' &&
      record.canPublishFromCurrentGeneration === false &&
      Array.isArray(record.requiredEvidence) &&
      Array.isArray(record.blockedReasons),
  )
}

function readImageSourceEvidenceFromSource(source: unknown): ImageSourceEvidence | undefined {
  for (const root of readGeneratedUiVerificationRoots(source)) {
    if (isImageSourceEvidence(root.imageSourceEvidence)) return root.imageSourceEvidence
    const verification = readRecord(root.imageEvidenceVerification)
    if (isImageSourceEvidence(verification?.imageSourceEvidence)) return verification.imageSourceEvidence
  }
  return undefined
}

function readImageEvidenceCandidateAdmissionFromSource(
  source: unknown,
): ImageEvidenceCandidateAdmissionResult | undefined {
  for (const root of readGeneratedUiVerificationRoots(source)) {
    if (isImageEvidenceCandidateAdmissionResult(root.imageEvidenceCandidateAdmission)) {
      return root.imageEvidenceCandidateAdmission
    }
    const verification = readRecord(root.imageEvidenceVerification)
    if (isImageEvidenceCandidateAdmissionResult(verification?.candidateAdmission)) {
      return verification.candidateAdmission
    }
    const generatedApp = readRecord(root.generatedApp)
    if (isImageEvidenceCandidateAdmissionResult(generatedApp?.imageEvidenceCandidateAdmission)) {
      return generatedApp.imageEvidenceCandidateAdmission
    }
  }
  return undefined
}

function readExistingGeneratedUiVerificationSummary(
  source: unknown,
): GeneratedUiVerificationSummary | undefined {
  for (const root of readGeneratedUiVerificationRoots(source)) {
    if (isGeneratedUiVerificationSummary(root.imageEvidenceVerification)) {
      return root.imageEvidenceVerification
    }
    const generatedApp = readRecord(root.generatedApp)
    if (isGeneratedUiVerificationSummary(generatedApp?.imageEvidenceVerification)) {
      return generatedApp.imageEvidenceVerification
    }
  }
  return undefined
}

function readGeneratedUiVerificationSurfacesFromSource(
  source: unknown,
  imageSourceEvidence: ImageSourceEvidence | undefined,
): GeneratedUiVerificationSurface[] {
  const rawSurfaces: unknown[] = []
  for (const root of readGeneratedUiVerificationRoots(source)) {
    rawSurfaces.push(root.generatedUiVerificationSurface)
    rawSurfaces.push(...(readArray(root.generatedUiVerificationSurfaces) ?? []))
    const verification = readRecord(root.imageEvidenceVerification)
    rawSurfaces.push(verification?.generatedUiVerificationSurface)
    rawSurfaces.push(...(readArray(verification?.generatedUiVerificationSurfaces) ?? []))
  }
  const surfaces = rawSurfaces
    .filter(isGeneratedUiVerificationSurface)
    .map((surface) => (
      imageSourceEvidence && !surface.sourceEvidence
        ? { ...surface, sourceEvidence: imageSourceEvidence }
        : surface
    ))
  if (surfaces.length > 0) return surfaces
  if (imageSourceEvidence) {
    return [{
      id: `${imageSourceEvidence.sourceId}:image-derived-candidate`,
      provenanceTier: 'image-derived-candidate',
      changedFields: [],
      schemaValid: true,
      sourceEvidence: imageSourceEvidence,
    }]
  }
  return []
}

function isGeneratedUiVerificationEnabled(source: unknown): boolean {
  return readGeneratedUiVerificationRoots(source).some((root) => {
    if (root.imageEvidenceVerificationEnabled === true) return true
    if (root.generatedUiVerificationEnabled === true) return true
    const verification = readRecord(root.imageEvidenceVerification)
    return verification?.enabled === true
  })
}

function summarizeGeneratedUiVerification(source: unknown): GeneratedUiVerificationSummary {
  const existing = readExistingGeneratedUiVerificationSummary(source)
  if (existing) return existing
  const imageSourceEvidence = readImageSourceEvidenceFromSource(source)
  const surfaces = readGeneratedUiVerificationSurfacesFromSource(source, imageSourceEvidence)
  const enabled =
    isGeneratedUiVerificationEnabled(source) || surfaces.length > 0 || Boolean(imageSourceEvidence)
  return buildGeneratedUiVerificationSummary({ enabled, surfaces })
}

function buildGeneratedAppReadiness(
  source?: StudioGenerationEvidenceSource | null,
  projectPlan?: StudioProjectPlanEvidenceSource | null,
  phase: 'pending' | 'generated' = 'generated',
): StudioGeneratedAppReadinessSummary {
  const summary = summarizeGeneratedAppSurface(source)
  const appClass = normalizeAppClass(projectPlan?.appClass ?? source?.appClass)
  const actionFlowRequired = readActionFlowRequired(source) || appClass === 'dynamic' || appClass === 'hybrid'
  const artifactParity = summarizeGeneratedAppArtifactParity(
    source,
    normalizeStudioRuntimeTargets(source, projectPlan),
    phase,
  )
  const styleSeparation = summarizeGeneratedAppStyleSeparation(source, phase)
  const reportApplicationShell = summarizeGeneratedAppReportApplicationShell(source, phase)
  const applicationConformance = summarizeGeneratedAppApplicationConformance(source, phase)
  const visibleFromJson = summarizeGeneratedAppVisibleFromJson(source, phase)
  const imageEvidenceVerification = summarizeGeneratedUiVerification(source)
  const imageEvidenceCandidateAdmission = readImageEvidenceCandidateAdmissionFromSource(source)
  const designQuality = readDesignQualityEvidenceModel(source)
  const notes: string[] = []

  if (phase === 'pending') {
    notes.push('Generated app readiness is pending until blocks stream back.')
    return {
      status: 'pending',
      appClass,
      actionFlowRequired,
      artifactParity,
      styleSeparation,
      reportApplicationShell,
      applicationConformance,
      visibleFromJson,
      imageEvidenceVerification,
      imageEvidenceCandidateAdmission,
      ...summary,
      notes,
    }
  }

  if (artifactParity.status === 'partial') {
    notes.push(...artifactParity.notes)
  }
  if (styleSeparation.status === 'partial') {
    notes.push(...styleSeparation.notes)
  }
  if (reportApplicationShell.status === 'partial') {
    notes.push(...reportApplicationShell.notes)
  }
  if (applicationConformance.status === 'blocked') {
    notes.push(...applicationConformance.notes)
  }
  if (visibleFromJson.status === 'blocked') {
    notes.push(...visibleFromJson.notes)
  }
  if (imageEvidenceVerification.status === 'blocked') {
    notes.push(...imageEvidenceVerification.blockedReasons)
  }
  if (imageEvidenceVerification.enabled && !imageEvidenceVerification.canPublish) {
    notes.push('Generated UI verification policy blocks publish until provenance gates and candidate review allow promotion.')
  }
  if (imageEvidenceCandidateAdmission?.status && imageEvidenceCandidateAdmission.status !== 'admitted') {
    notes.push(`Image evidence candidate admission is ${imageEvidenceCandidateAdmission.status}; regenerate from an admitted ontology asset before publish.`)
  }
  if (visibleFromJson.status === 'missing' && visibleFromJson.required) {
    notes.push('Visible-from-JSON oracle is missing for ontology-backed visible slot generation.')
  }
  if (applicationConformance.status === 'missing' && applicationConformance.required) {
    notes.push('Application conformance oracle is missing for ontology-backed generated app.')
  }
  if (applicationConformance.required || applicationConformance.status !== 'missing') {
    if (!designQuality) {
      notes.push('Measured design quality evidence is missing; ontology retrieval/conformance cannot be claimed as design ontology application.')
    } else if (designQuality.status !== 'pass') {
      notes.push(`Measured design quality status is ${designQuality.status}; pass is required before design ontology application can be claimed.`)
    } else if (designQuality.evidence.sourceKind !== 'measured') {
      notes.push(`Design quality evidence source is ${designQuality.evidence.sourceKind}; measured evidence is required before design ontology application can be claimed.`)
    }
  }
  if (actionFlowRequired && summary.flowCount === 0) {
    notes.push('ActionFlow is required for this app plan, but no flow definitions were found.')
  }
  if (summary.eventBindingCount > 0 && summary.flowCount === 0) {
    notes.push('Event bindings exist without matching flow definitions.')
  }
  if ((appClass === 'dynamic' || appClass === 'hybrid') && summary.datasourceCount === 0 && summary.stateVariableCount === 0) {
    notes.push('Dynamic or hybrid apps need datasource or state evidence before release.')
  }
  if (actionFlowRequired && summary.stateVariableCount > 0 && summary.stateIsolation === 'partial') {
    notes.push('ActionFlow-ready apps need both block-local state and shared appData/global state evidence to avoid cross-block state conflicts.')
  }

  if (notes.length > 0) {
    return {
      status: 'partial',
      appClass,
      actionFlowRequired,
      artifactParity,
      styleSeparation,
      reportApplicationShell,
      applicationConformance,
      visibleFromJson,
      imageEvidenceVerification,
      imageEvidenceCandidateAdmission,
      ...summary,
      notes,
    }
  }

  if (!actionFlowRequired && summary.flowCount === 0) {
    notes.push('No ActionFlow requirement was detected for this app.')
    return {
      status: 'not-required',
      appClass,
      actionFlowRequired,
      artifactParity,
      styleSeparation,
      reportApplicationShell,
      applicationConformance,
      visibleFromJson,
      imageEvidenceVerification,
      imageEvidenceCandidateAdmission,
      ...summary,
      notes,
    }
  }

  notes.push('Generated app contains the required runtime evidence for this app plan.')
  return {
    status: 'pass',
    appClass,
    actionFlowRequired,
    artifactParity,
    styleSeparation,
    reportApplicationShell,
    applicationConformance,
    visibleFromJson,
    imageEvidenceVerification,
    imageEvidenceCandidateAdmission,
    ...summary,
    notes,
  }
}

function summarizeGeneratedAppVisibleFromJson(
  source: unknown,
  phase: 'pending' | 'generated',
): StudioVisibleFromJsonOracleSummary {
  const explicit = readVisibleFromJsonOracleFromSource(source)
  const required = phase === 'generated' && sourceRequiresApplicationConformance(source)

  if (explicit) {
    return {
      ...explicit,
      required: explicit.required || required,
    }
  }

  return {
    schemaVersion: 'studio-visible-from-json-oracle.v1',
    status: 'missing',
    required,
    checkedSurface: 'missing',
    declaredSlotCount: 0,
    visibleSlotCount: 0,
    hiddenSlotCount: 0,
    missingSlotCount: 0,
    rootRasterOnly: false,
    rasterAuthorityDetected: false,
    blockedReasons: [],
    checkedSlots: [],
    notes: [
      phase === 'pending'
        ? 'Visible-from-JSON oracle is pending until generated blocks stream back.'
        : required
          ? 'Visible-from-JSON oracle is required for ontology-backed generated apps.'
          : 'Visible-from-JSON oracle is not attached for this generated app.',
    ],
  }
}

function summarizeGeneratedAppApplicationConformance(
  source: unknown,
  phase: 'pending' | 'generated',
): StudioApplicationConformanceOracleSummary {
  const explicit = readApplicationConformanceOracleFromSource(source)
  const required = phase === 'generated' && sourceRequiresApplicationConformance(source)

  if (explicit) {
    return {
      ...explicit,
      required: explicit.required || required,
    }
  }

  return {
    schemaVersion: 'studio-application-conformance-oracle.v1',
    status: 'missing',
    required,
    blockCount: 0,
    checkedBlockNames: [],
    dimensions: [],
    blockedReasons: [],
    notes: [
      phase === 'pending'
        ? 'Application conformance oracle is pending until generated blocks stream back.'
        : required
          ? 'Application conformance oracle is required for ontology-backed generated apps.'
          : 'Application conformance oracle is not required for this non-ontology-backed generated app.',
    ],
  }
}

function readApplicationConformanceOracleFromSource(
  source: unknown,
): StudioApplicationConformanceOracleSummary | null {
  const record = readRecord(source)
  if (!record) return null
  const metadata = readRecord(record.metadata)
  const meta = readRecord(record.meta)
  const manifest = readRecord(record.manifest)
  const generationEvidence = readRecord(
    record.generationEvidence ??
      metadata?.generationEvidence ??
      meta?.generationEvidence ??
      manifest?.generationEvidence,
  )

  const candidates = [
    record.applicationConformanceOracle,
    metadata?.applicationConformanceOracle,
    meta?.applicationConformanceOracle,
    manifest?.applicationConformanceOracle,
    generationEvidence?.applicationConformanceOracle,
    readRecord(generationEvidence?.generatedApp)?.applicationConformance,
  ]

  for (const candidate of candidates) {
    const oracle = readStudioApplicationConformanceOracle(candidate)
    if (oracle) return oracle
  }
  return null
}

function readVisibleFromJsonOracleFromSource(
  source: unknown,
): StudioVisibleFromJsonOracleSummary | null {
  const record = readRecord(source)
  if (!record) return null
  const metadata = readRecord(record.metadata)
  const meta = readRecord(record.meta)
  const manifest = readRecord(record.manifest)
  const generationEvidence = readRecord(
    record.generationEvidence ??
      metadata?.generationEvidence ??
      meta?.generationEvidence ??
      manifest?.generationEvidence,
  )
  const generatedApp = readRecord(generationEvidence?.generatedApp)

  const candidates = [
    record.visibleFromJsonOracle,
    metadata?.visibleFromJsonOracle,
    meta?.visibleFromJsonOracle,
    manifest?.visibleFromJsonOracle,
    generationEvidence?.visibleFromJsonOracle,
    generatedApp?.visibleFromJson,
  ]

  for (const candidate of candidates) {
    const oracle = readStudioVisibleFromJsonOracle(candidate)
    if (oracle) return oracle
  }
  return null
}

function sourceRequiresApplicationConformance(source: unknown): boolean {
  const record = readRecord(source)
  if (!record) return false
  const metadata = readRecord(record.metadata)
  const meta = readRecord(record.meta)
  const manifest = readRecord(record.manifest)
  const generationEvidence = readRecord(
    record.generationEvidence ??
      metadata?.generationEvidence ??
      meta?.generationEvidence ??
      manifest?.generationEvidence,
  )

  const compositionPlan = [
    record.applicationCompositionPlan,
    metadata?.applicationCompositionPlan,
    meta?.applicationCompositionPlan,
    manifest?.applicationCompositionPlan,
    generationEvidence?.applicationCompositionPlan,
  ].some(Boolean)
  if (compositionPlan) return true

  const productionEvidenceChain = readStudioProductionGenerationEvidenceChain(
    record.productionEvidenceChain ??
      metadata?.productionEvidenceChain ??
      meta?.productionEvidenceChain ??
      manifest?.productionEvidenceChain ??
      generationEvidence?.productionEvidenceChain,
  )
  if (
    productionEvidenceChain &&
    productionEvidenceChain.deterministic === true &&
    productionEvidenceChain.sourceEvidenceRefs.length > 0
  ) {
    return true
  }

  return hasCompilerOwnedGeneratedBlocks(record.blocks)
}

function hasCompilerOwnedGeneratedBlocks(blocks: unknown): boolean {
  const record = readRecord(blocks)
  if (!record) return false
  return Object.values(record).some((block) => {
    const blockRecord = readRecord(block)
    if (!blockRecord) return false
    const meta = readRecord(blockRecord.meta)
    const metadata = readRecord(blockRecord.metadata)
    const agenticRag = readRecord(meta?.agenticRag ?? metadata?.agenticRag)
    const nativeProjection = readRecord(meta?.nativeCogoJsonV4Ds ?? metadata?.nativeCogoJsonV4Ds)
    return agenticRag?.compilerOwned === true || nativeProjection?.compilerOwned === true
  })
}

function summarizeGeneratedAppReportApplicationShell(
  source: unknown,
  phase: 'pending' | 'generated',
): StudioGeneratedAppReportApplicationShellSummary {
  const emptySummary: ReportApplicationShellReport['summary'] = {
    nodeCount: 0,
    globalCommandHeaderCount: 0,
    chatCommandInputCount: 0,
    reportCommonControlsInHeaderCount: 0,
    collapsibleSidebarCount: 0,
    fullHeightSidebarCount: 0,
    a4PortraitCanvasCount: 0,
    reportNarrativeCount: 0,
    reportChartSurfaceCount: 0,
    reportDataTableCount: 0,
  }

  if (!isReportApplicationSource(source)) {
    return {
      schemaVersion: 'studio-generated-app-report-application-shell.v1',
      status: 'not-applicable',
      applicable: false,
      findingCount: 0,
      summary: emptySummary,
      notes: ['Report application shell proof is not required for this app kind.'],
    }
  }

  if (phase === 'pending') {
    return {
      schemaVersion: 'studio-generated-app-report-application-shell.v1',
      status: 'pending',
      applicable: true,
      findingCount: 0,
      summary: emptySummary,
      notes: ['Report application shell proof is pending until generated app structure is attached.'],
    }
  }

  const report = assessReportApplicationShell(source)
  const firstFinding = report.findings[0]

  return {
    schemaVersion: 'studio-generated-app-report-application-shell.v1',
    status: report.status === 'pass' ? 'pass' : 'partial',
    applicable: true,
    findingCount: report.findings.length,
    summary: report.summary,
    ...(firstFinding
      ? {
          firstFinding: {
            rule: firstFinding.rule,
            severity: firstFinding.severity,
            path: firstFinding.path,
          },
        }
      : {}),
    notes: report.status === 'pass'
      ? ['Report application shell has user-facing A4 canvas, global chat header, sidebar, narrative, chart, and table structure.']
      : report.findings.slice(0, 5).map((finding) =>
          `Report application shell failed: ${finding.rule} at ${finding.path}.`
        ),
  }
}

function isReportApplicationSource(source: unknown): boolean {
  const record = readRecord(source)
  if (!record) return false
  const candidates = [
    record.applicationKind,
    record.appKind,
    record.kind,
    record.reportApplicationSchemaVersion,
    readRecord(record.metadata)?.applicationKind,
    readRecord(record.metadata)?.appKind,
    readRecord(record.meta)?.applicationKind,
    readRecord(record.manifest)?.applicationKind,
  ]
  return candidates.some((value) => {
    const text = typeof value === 'string' ? value.toLowerCase() : ''
    return text === 'cogo.report-application' || text.includes('report-application')
  })
}

function summarizeGeneratedAppStyleSeparation(
  source: unknown,
  phase: 'pending' | 'generated',
): StudioGeneratedAppStyleSeparationSummary {
  if (phase === 'pending') {
    return {
      schemaVersion: 'studio-generated-app-style-separation.v1',
      status: 'pending',
      checkedBlockCount: 0,
      blockedBlockCount: 0,
      findingCount: 0,
      visualClassNameTokenCount: 0,
      rawStyleValueCount: 0,
      inlineStyleObjectCount: 0,
      inlineVisualStyleCount: 0,
      unresolvedDesignAuthorityCount: 0,
      reports: [],
      notes: ['Style-separation proof is pending until generated blocks are attached.'],
    }
  }

  const blocks = collectGeneratedAppStyleSeparationBlocks(source)
  if (blocks.length === 0) {
    return {
      schemaVersion: 'studio-generated-app-style-separation.v1',
      status: 'pending',
      checkedBlockCount: 0,
      blockedBlockCount: 0,
      findingCount: 0,
      visualClassNameTokenCount: 0,
      rawStyleValueCount: 0,
      inlineStyleObjectCount: 0,
      inlineVisualStyleCount: 0,
      unresolvedDesignAuthorityCount: 0,
      reports: [],
      notes: ['Style-separation proof found no generated Coogo UI blocks to inspect.'],
    }
  }

  const reports = blocks.map(({ blockId, block }) => {
    const report = assessBlockStyleSeparation(block, {
      strict: true,
      requireDesignAuthority: true,
    })
    const firstFinding = report.findings[0]
    return {
      blockId,
      status: report.status,
      findingCount: report.findings.length,
      summary: report.summary,
      ...(firstFinding
        ? {
            firstFinding: {
              rule: firstFinding.rule,
              severity: firstFinding.severity,
              path: firstFinding.path,
              ...(firstFinding.token ? { token: firstFinding.token } : {}),
              ...(firstFinding.key ? { key: firstFinding.key } : {}),
            },
          }
        : {}),
    }
  })
  const blockedReports = reports.filter((report) => report.status !== 'pass')
  const totals = reports.reduce((acc, report) => {
    acc.findingCount += report.findingCount
    acc.visualClassNameTokenCount += report.summary.visualClassNameTokenCount
    acc.rawStyleValueCount += report.summary.rawStyleValueCount
    acc.inlineStyleObjectCount += report.summary.inlineStyleObjectCount
    acc.inlineVisualStyleCount += report.summary.inlineVisualStyleCount
    acc.unresolvedDesignAuthorityCount += report.summary.unresolvedDesignAuthorityCount
    return acc
  }, {
    findingCount: 0,
    visualClassNameTokenCount: 0,
    rawStyleValueCount: 0,
    inlineStyleObjectCount: 0,
    inlineVisualStyleCount: 0,
    unresolvedDesignAuthorityCount: 0,
  })

  const notes = blockedReports.length > 0
    ? blockedReports.slice(0, 5).map((report) => {
        const first = report.firstFinding
        return first
          ? `Style separation failed for ${report.blockId}: ${first.rule} at ${first.path}.`
          : `Style separation failed for ${report.blockId}.`
      })
    : ['Generated app UI structure is separated from injected design-system style authority.']

  return {
    schemaVersion: 'studio-generated-app-style-separation.v1',
    status: blockedReports.length > 0 ? 'partial' : 'pass',
    checkedBlockCount: reports.length,
    blockedBlockCount: blockedReports.length,
    reports,
    notes,
    ...totals,
  }
}

function collectGeneratedAppStyleSeparationBlocks(source: unknown): Array<{ blockId: string; block: unknown }> {
  const record = readRecord(source)
  if (!record) return []

  const blocks = readRecord(record.blocks)
  if (blocks) {
    return Object.entries(blocks)
      .filter(([, block]) => Boolean(readRecord(block)))
      .map(([blockId, block]) => ({ blockId, block }))
  }

  if (readRecord(record.ui)?.root || readRecord(record.root) || typeof record.type === 'string') {
    return [{ blockId: String(record.name ?? record.id ?? 'generated-app'), block: record }]
  }

  return []
}

function normalizeAppClass(value: unknown): StudioGeneratedAppReadinessSummary['appClass'] {
  return value === 'static' || value === 'dynamic' || value === 'hybrid'
    ? value
    : 'unknown'
}

function summarizeGeneratedAppSurface(source: unknown): Pick<
  StudioGeneratedAppReadinessSummary,
  | 'flowCount'
  | 'eventBindingCount'
  | 'datasourceCount'
  | 'stateVariableCount'
  | 'localStateVariableCount'
  | 'globalStateVariableCount'
  | 'stateIsolation'
> {
  const record = readRecord(source)
  if (!record) {
    return {
      flowCount: 0,
      eventBindingCount: 0,
      datasourceCount: 0,
      stateVariableCount: 0,
      localStateVariableCount: 0,
      globalStateVariableCount: 0,
      stateIsolation: 'not-required',
    }
  }

  let flowCount = countFlowDefinitions(record.flows) + countFlowDefinitions(record.flow)
  let eventBindingCount = countEventBindings(record.events)
  let datasourceCount =
    countDatasourceDefinitions(record.datasources) +
    countDatasourceDefinitions(record.datasource) +
    countDatasourceDefinitions(record.data)
  let stateVariableCount =
    countStateVariables(record.globalState) +
    countStateVariables(record.state) +
    countStateVariables(record.variables)
  let localStateVariableCount =
    countScopedStateVariables(record.state, 'local') +
    countScopedStateVariables(record.variables, 'local')
  let globalStateVariableCount =
    countScopedStateVariables(record.globalState, 'global') +
    countScopedStateVariables(record.state, 'global') +
    countScopedStateVariables(record.variables, 'global')

  const manifest = readRecord(record.manifest)
  if (manifest) {
    datasourceCount += countDatasourceDefinitions(manifest.datasources)
    stateVariableCount += countStateVariables(manifest.globalState)
    globalStateVariableCount += countScopedStateVariables(manifest.globalState, 'global')
  }

  const blocks = readRecord(record.blocks)
  if (blocks) {
    for (const block of Object.values(blocks)) {
      const blockRecord = readRecord(block)
      if (!blockRecord) continue
      flowCount += countFlowDefinitions(blockRecord.flow) + countFlowDefinitions(blockRecord.flows)
      eventBindingCount += countEventBindings(blockRecord.events)
      datasourceCount +=
        countDatasourceDefinitions(blockRecord.datasource) +
        countDatasourceDefinitions(blockRecord.datasources) +
        countDatasourceDefinitions(blockRecord.data)
      stateVariableCount +=
        countStateVariables(blockRecord.state) +
        countStateVariables(blockRecord.variables)
      localStateVariableCount +=
        countScopedStateVariables(blockRecord.state, 'local', 'local') +
        countScopedStateVariables(blockRecord.variables, 'local', 'local')
      globalStateVariableCount +=
        countScopedStateVariables(blockRecord.state, 'global', 'none') +
        countScopedStateVariables(blockRecord.variables, 'global', 'none')
    }
  }
  const appDataBindingCount = countAppDataBindingEvidence(record)
  if (appDataBindingCount > 0 && globalStateVariableCount === 0) {
    globalStateVariableCount += appDataBindingCount
    stateVariableCount += appDataBindingCount
  }

  const stateIsolation: StudioGeneratedAppReadinessSummary['stateIsolation'] =
    stateVariableCount === 0
      ? 'not-required'
      : localStateVariableCount > 0 && globalStateVariableCount > 0
        ? 'pass'
        : 'partial'

  return {
    flowCount,
    eventBindingCount,
    datasourceCount,
    stateVariableCount,
    localStateVariableCount,
    globalStateVariableCount,
    stateIsolation,
  }
}

function summarizeGeneratedAppArtifactParity(
  source: unknown,
  runtimeTargets: StudioRuntimeTargetEvidence[],
  phase: 'pending' | 'generated',
): StudioGeneratedAppArtifactParitySummary {
  if (phase === 'pending') {
    return {
      status: 'pending',
      manifestPresent: false,
      blockCount: 0,
      pageCount: 0,
      missingPageBlockRefs: [],
      targetPlatforms: runtimeTargets,
      notes: ['Generated app artifact parity is pending until the manifest and blocks stream back.'],
    }
  }

  const record = readRecord(source)
  if (!record) {
    return {
      status: 'pending',
      manifestPresent: false,
      blockCount: 0,
      pageCount: 0,
      missingPageBlockRefs: [],
      targetPlatforms: runtimeTargets,
      notes: ['No generated app artifact was available.'],
    }
  }

  const ui = readRecord(record.ui)
  if (readRecord(ui?.root) && !readRecord(record.blocks) && !readRecord(record.manifest)) {
    return {
      status: 'not-applicable',
      manifestPresent: false,
      blockCount: 1,
      pageCount: 0,
      missingPageBlockRefs: [],
      targetPlatforms: runtimeTargets,
      notes: ['Single-block generation does not produce an app-level manifest artifact.'],
    }
  }

  const manifest = readRecord(record.manifest)
  const blocks = readRecord(record.blocks)
  const blockNames = new Set(Object.keys(blocks ?? {}))
  const blockAliases = buildBlockNameAliasSet(blockNames)
  const pages = Array.isArray(record.pages)
    ? record.pages
    : Array.isArray(manifest?.pages)
      ? manifest.pages
      : []
  const pageBlockRefs = [
    ...extractPageBlockRefs(pages),
    ...extractPageBlockRefs(Array.isArray(manifest?.globalBlocks) ? manifest.globalBlocks : []),
  ]
  const missingPageBlockRefs = [
    ...new Set(pageBlockRefs.filter((ref) => !blockAliases.has(ref))),
  ]
  const notes: string[] = []

  if (!manifest) notes.push('Generated app manifest is missing.')
  if (!blocks || blockNames.size === 0) notes.push('Generated app blocks are missing.')
  if (pages.length === 0) notes.push('Generated app manifest has no pages to prove block placement.')
  if (missingPageBlockRefs.length > 0) {
    notes.push(`Manifest page block refs missing generated blocks: ${missingPageBlockRefs.join(', ')}.`)
  }

  if (notes.length > 0) {
    return {
      status: 'partial',
      manifestPresent: Boolean(manifest),
      blockCount: blockNames.size,
      pageCount: pages.length,
      missingPageBlockRefs,
      targetPlatforms: runtimeTargets,
      notes,
    }
  }

  return {
    status: 'pass',
    manifestPresent: true,
    blockCount: blockNames.size,
    pageCount: pages.length,
    missingPageBlockRefs: [],
    targetPlatforms: runtimeTargets,
    notes: ['Generated app artifact contains a manifest, pages, and all referenced blocks.'],
  }
}

function extractPageBlockRefs(values: unknown[]): string[] {
  const refs: string[] = []
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      refs.push(value)
      continue
    }

    const record = readRecord(value)
    if (!record) continue
    const directRef = record.blockName ?? record.blockId ?? record.block
    if (typeof directRef === 'string' && directRef.trim()) refs.push(directRef)

    const blockRef = readRecord(record.blockRef)
    const nestedRef = blockRef?.blockId ?? blockRef?.blockName ?? blockRef?.id
    if (typeof nestedRef === 'string' && nestedRef.trim()) refs.push(nestedRef)

    const nestedBlocks = Array.isArray(record.blocks)
      ? record.blocks
      : Array.isArray(record.blockRefs)
        ? record.blockRefs
        : Array.isArray(record.blockIds)
          ? record.blockIds
          : []
    refs.push(...extractPageBlockRefs(nestedBlocks))
  }
  return refs
}

function buildBlockNameAliasSet(blockNames: Set<string>): Set<string> {
  const aliases = new Set<string>()
  for (const name of blockNames) {
    aliases.add(name)
    aliases.add(camelToKebabName(name))
    aliases.add(kebabToCamelName(name))
  }
  return aliases
}

function camelToKebabName(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()
}

function kebabToCamelName(value: string): string {
  return value.replace(/-([a-z0-9])/g, (_, char: string) => char.toUpperCase())
}

function countFlowDefinitions(value: unknown): number {
  const record = readRecord(value)
  if (!record) return 0
  const flows = readRecord(record.flows)
  return Object.keys(flows ?? record).length
}

function countEventBindings(value: unknown): number {
  const record = readRecord(value)
  if (!record) return 0
  const bindings = readRecord(record.bindings)
  return Object.keys(bindings ?? record).length
}

function countDatasourceDefinitions(value: unknown): number {
  if (Array.isArray(value)) return value.length
  const record = readRecord(value)
  if (!record) return 0
  const sources = readRecord(record.sources)
  return Object.keys(sources ?? record).length
}

function countStateVariables(value: unknown): number {
  const record = readRecord(value)
  if (!record) return 0
  const variables = readRecord(record.variables) ?? record
  return Object.values(variables).reduce<number>((count, scope) => {
    const scopedRecord = readRecord(scope)
    return count + (scopedRecord ? countVariableEntries(scopedRecord) : 1)
  }, 0)
}

function countAppDataBindingEvidence(record: Record<string, unknown>): number {
  const seen = new Set<string>()
  const scan = (value: unknown): void => {
    if (value == null) return
    const text = typeof value === 'string' ? value : JSON.stringify(value)
    for (const match of text.matchAll(/appData\.([A-Za-z0-9_]+)/g)) {
      if (match[1]) seen.add(match[1])
    }
  }
  scan(record.blocks ?? record)
  scan(record.manifest)
  scan(record.globalState)
  return seen.size
}

function countScopedStateVariables(
  value: unknown,
  scope: 'local' | 'global',
  siblingSymbols: 'local' | 'global' | 'none' = scope === 'global' ? 'global' : 'none',
): number {
  const record = readRecord(value)
  if (!record) return 0
  const variables = readRecord(record.variables) ?? record
  const localKeys = ['local', 'localState', 'state']
  const globalKeys = ['appData', 'global', 'globalState']
  const scopeKeys = scope === 'local' ? localKeys : globalKeys
  const reserved = new Set([...localKeys, ...globalKeys, 'variables'])
  let count = scopeKeys.reduce((total, key) => {
    const scoped = readRecord(variables[key])
    if (!scoped) return total
    return total + countVariableEntries(scoped)
  }, 0)
  // map-appspec writes sharedAppData as globalState.{symbol} (type+default).
  // Generated blocks use the same sibling shape for block-local symbols.
  // Do not count block-local siblings as global, or isolation inverts.
  if (siblingSymbols === scope) {
    for (const [key, entry] of Object.entries(variables)) {
      if (reserved.has(key)) continue
      const scoped = readRecord(entry)
      if (scoped && ('type' in scoped || 'default' in scoped)) {
        count += 1
      }
    }
  }
  return count
}

function countVariableEntries(record: Record<string, unknown>): number {
  if ('type' in record || 'default' in record) return 1
  return Object.keys(record).length
}

function readActionFlowRequired(source: unknown): boolean {
  const record = readRecord(source)
  const contract = readRecord(record?.interactionContract)
  return contract?.actionFlowRequired === true
}

export function createStudioCoreUiBlockConsumerReceiptV1(input: {
  expectedIdentity: UiBlockClosureIdentityV1
  documents: {
    block: Record<string, unknown>
    ui: Record<string, unknown>
    events: Record<string, unknown>
    flow: Record<string, unknown>
    variables: Record<string, unknown>
    datasource: Record<string, unknown>
  }
  designRelease: { releaseHash: UiBlockClosureIdentityV1['authorityHashes']['design']; artifact: unknown }
  renderedArtifact: unknown
  actionFlowTrace: unknown
  startedStateHash: string
  finalStateHash: string
  datasourceReadback: unknown
  evidenceRefs?: string[]
}): UiBlockConsumerReceiptV1 {
  const hashPattern = /^sha256:[a-f0-9]{64}$/u
  if (!hashPattern.test(input.startedStateHash) || !hashPattern.test(input.finalStateHash)) {
    throw new Error('STUDIO_UI_BLOCK_RUNTIME_STATE_HASH_INVALID')
  }
  const observedIdentity = deriveSingleBlockUiBlockClosureIdentityV1({
    sourceRevisions: input.expectedIdentity.sourceRevisions,
    authorityHashes: input.expectedIdentity.authorityHashes,
    documents: input.documents,
  })
  return createMeasuredUiBlockConsumerReceiptV1({
    expectedIdentity: input.expectedIdentity,
    observedIdentity,
    consumer: 'studio-core',
    expectedDesignReleaseHash: input.expectedIdentity.authorityHashes.design,
    observedDesignReleaseHash: input.designRelease.releaseHash,
    evidenceSource: 'measured-studio-core',
    observations: {
      packageLoaded: true,
      renderedOrLowered: input.renderedArtifact !== null && input.renderedArtifact !== undefined,
      designReleaseResolved: hashCanonicalContent(input.designRelease.artifact) === input.designRelease.releaseHash,
      actionFlowExecuted: isPassingUiBlockActionFlowTraceV1(input.actionFlowTrace),
      stateTransitionObserved: input.startedStateHash !== input.finalStateHash,
      datasourceResolved:
        hashCanonicalContent(input.datasourceReadback) === observedIdentity.documentHashes.datasource,
    },
    artifactHashes: {
      application: observedIdentity.applicationHash,
      renderedOrLowered: hashCanonicalContent(input.renderedArtifact),
      actionFlowTrace: hashCanonicalContent(input.actionFlowTrace),
      datasource: hashCanonicalContent(input.datasourceReadback),
    },
    evidenceRefs: input.evidenceRefs,
  })
}
